/* eslint-disable quotes */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { GlobalContext } from '../../providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import ModuleHeaderLayout from '../../components/Common/Layouts/ModuleHeaderLayout';
import TariffRagSearch from 'components/TariffChecker/RagAgent/TariffRagSearch';
import { LoadingFeedback } from 'components/Common/LoadingModal';
import format from 'date-fns/format';
import TarifFResultsComp from 'components/TariffChecker/TariffResults';
import { Dialog, DialogContent, DialogTitle, Modal } from '@mui/material';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import { screeningCountries } from 'utils/screeningCountries';
import { findScreeningCountryOption } from 'utils/countriesWithCodes';
import {
  getUnsupportedHtsChapter,
  unsupportedHtsChapterMessage,
} from 'utils/htsChapters';
import {
  BanGateAnswer,
  ImportBan,
  findActiveImportBan,
  importBanGateAnswer,
  importBanResultRow,
} from 'utils/importBans';
import { isRemitted, remissionOptions } from 'utils/remissions';
import { isForcedNavigationInFlight } from 'services/sessionRecovery';
import { useSnackbar } from 'notistack';
import CasesAPI from 'api/CasesAPI';
import DatahubAPI from 'api/DatahubAPI';
import UsersAPI from 'api/UsersAPI';
import { ResultsPlaceholder } from 'components/TariffChecker/ResultsPlaceholder';
import ftaMappingFile from '../../components/TariffChecker/FTAMappings.json';
import { runAIExplanationOnly } from 'api/PyAPIConnect';
import {
  COLUMN2_ISO2,
  digitsOnly,
  formatHtsPretty,
  isFreeBase,
  normalizeCountry,
} from 'utils/tariffCodes';
import {
  buildEngineInputs,
  engineFigures,
  hydrateMaterialPct,
  runEngine,
  SUPERSEDED,
} from 'utils/tariffEngine';
import moment from 'moment';
import { Prompt, useHistory, useLocation, useParams } from 'react-router-dom'; // ensure useHistory is imported
import {
  useGetTariffById,
  useSaveTariffResult,
} from 'api/bitedb/tariffs/tariffs.hooks';
import TariffToolInsightsPanel from 'components/TariffChecker/TariffToolInsightsPanel';
import { screeningTicketTariffPDF } from 'components/Common/EntityTicketPdf';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';

const FTA: any = ftaMappingFile.fta_mapping;

/* -------------------- Types -------------------- */
type ResultRow = {
  type: string;
  code: string;
  rate: string;
  _codeDigits?: string;
  _requiresLineSplit?: boolean;
  _lineSplitLabel?: string;
  _excludedByDigits?: string[];
  _variantSingleLine?: boolean;
  _relatesDigits?: string[];
};

type HtsHistoryItem = {
  id: string;
  date: string;
  summary: string;
};

type RiskFlag = {
  id: string;
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'info';
};

type Candidate = {
  label: string;
  codeDigits: string;
  codePretty: string;
  ratePct: number;
  rateDisplay: string;
  excludedByDigits: string[];
  requiresLineSplit: boolean;
  lineSplitKey?: string;
  lineSplitLabel?: string;
  bd_offer_choice: boolean;
  relatesDigits: string[];
  variantSingleLine: boolean;
  applicability?: any[];
  hasCountryCond?: boolean;
  _needsLoadingCountryForThis?: boolean;
  countryKeys?: string[];
  _failedSplitGate?: boolean;
  _failedCountryCheck?: boolean;
  _keepForDiscovery: boolean;
  applicabilityOk?: boolean;
  hasValidityCriteria?: boolean;
  bd_232_question?: string;
  bd_232_answer?: string;
};

type Line = {
  id: string;
  label: string;
  value: number;
  rows: ResultRow[];
  order?: number;
};

// `mpfOnly` marks an FTA that was surfaced purely from the origin country's
// eligibility — the specific HTS code does NOT carry it in its own special-rate
// data. Claiming such an FTA removes the Merchandise Processing Fee for goods
// that qualify by country of origin, but must NOT drive the base duty to Free.
// Data-backed FTAs (present in the code's special rate) leave `mpfOnly` unset
// and keep their existing base-Free behavior.
type FtaOption = { code: string; name: string; mpfOnly?: boolean };

// FTAs whose qualifying goods are exempt from the Merchandise Processing Fee.
// Used both to zero the MPF when one is claimed and to decide which
// country-eligibility (MPF-only) FTAs are worth surfacing.
const MPF_EXEMPT_FTA_CODES = new Set([
  'AU',
  'BH',
  'CL',
  'P',
  'P+',
  'CO',
  'IL',
  'KR',
  'OM',
  'PA',
  'PE',
  'SC',
  'S',
  'S+',
  'E',
  'R',
  'Y',
]);

type TariffStatus = { color: 'Green' | 'Yellow' | 'Red'; message: string };

type TariffCountry = 'US' | 'MX' | 'CA';

const TARIFF_COUNTRY_OPTIONS: Array<{
  value: TariffCountry;
  label: string;
  whiteLabelAccess?: boolean;
  // Set false to withdraw a country from the tool UI while KEEPING its engine,
  // lookup, parsing and rendering code intact. Flip back to true (or delete the
  // flag) to re-expose the tab — nothing else needs to change.
  uiEnabled?: boolean;
}> = [
  { value: 'US', label: 'United States', whiteLabelAccess: true },
  // Mexico: withdrawn from the UI. All MX support below is deliberately left in
  // place — the TIGIE/cuota/safeguard types, the AMX additive-duty semantics,
  // the LIGIE unit abbreviations and the MX branch of the foreign lookup are all
  // still here and still compile.
  { value: 'MX', label: 'Mexico', whiteLabelAccess: false, uiEnabled: false },
  { value: 'CA', label: 'Canada', whiteLabelAccess: false },
];

// The countries a user can actually reach, derived from the catalog above so the
// tab list and the ?country= parser can never disagree. A deep link to a
// withdrawn country falls back to US rather than selecting a tab that isn't
// rendered.
const SELECTABLE_TARIFF_COUNTRIES = new Set<TariffCountry>(
  TARIFF_COUNTRY_OPTIONS.filter((o) => o.uiEnabled !== false).map(
    (o) => o.value,
  ),
);

// CA/MX (foreign) tariffs are a Pro+ feature. Standard / none / tariff-only
// users can view only US tariffs; the foreign tabs render but are disabled.
const FOREIGN_TARIFF_TIERS = ['pro', 'business', 'enterprise'];

const parseTariffCountryParam = (
  raw: string | null,
  useInternationalTariffCountries = true,
): TariffCountry => {
  const upper = (raw || '').trim().toUpperCase();
  if (!useInternationalTariffCountries) {
    return 'US';
  }
  return SELECTABLE_TARIFF_COUNTRIES.has(upper as TariffCountry)
    ? (upper as TariffCountry)
    : 'US';
};

// Saved tariff tickets persist their jurisdiction separately from the result
// snapshot. Keep the strict parser here so an invalid historic value cannot
// select a country that is no longer available in the UI.
const parseSavedTariffCountry = (raw: unknown): TariffCountry | null => {
  const upper = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  return SELECTABLE_TARIFF_COUNTRIES.has(upper as TariffCountry)
    ? (upper as TariffCountry)
    : null;
};

const FOREIGN_SOURCE_BY_COUNTRY: Record<'MX' | 'CA', 'Mexico' | 'Canada'> = {
  MX: 'Mexico',
  CA: 'Canada',
};

// Mirrors datahub/src/utils/TariffRateParsers/tariffRateTypes.ts. Keep in
// sync if backend shape changes.
type TariffRateUnit =
  | 'kg'
  | 'each'
  | 'pair'
  | 'set'
  | 'dozen'
  | 'tonne'
  | 'litre'
  | 'gram'
  | 'metre'
  | 'square_metre'
  | 'cubic_metre'
  | 'unknown';

type TariffSpecificRate = {
  kind: 'specific';
  amountPerUnit: number;
  unit: TariffRateUnit;
  display: string;
  qualifier?: string;
};

type TariffRateComponent =
  | { kind: 'free'; display: string }
  | { kind: 'percent'; percent: number; display: string }
  | TariffSpecificRate;

type ParsedTariffRate = {
  raw: string;
  parsed: boolean;
  primary: TariffRateComponent;
  floor?: TariffSpecificRate; // max(primary, floor)        [CA "but not less than"]
  ceiling?: TariffSpecificRate; // min(primary, ceiling)    [CA "but not more than"]
  additional?: TariffSpecificRate; // primary + additional  [MX AMX "X% + Y$/kg"]
  notes: string[];
};

// Quantity entered against a schedule UoQ may not match the unit named in
// the rate string (e.g., schedule uses KGM but the rate says "/each"). For
// now we only auto-confirm matches within the same dimension we can be
// sure about. Anything ambiguous is flagged so the user knows the calc
// may be off. Handles both CBSA codes (Canada) and Spanish abbreviations
// (Mexico, e.g. "Kg", "pza", "m²").
const unitMatchesUoQ = (
  rateUnit: TariffRateUnit,
  uoq: string | null | undefined,
): boolean => {
  if (!uoq) return rateUnit === 'unknown';
  const key = uoq.trim().toUpperCase();
  switch (rateUnit) {
    case 'kg':
      return key === 'KGM' || key === 'KNS' || key === 'KSD' || key === 'KG';
    case 'tonne':
      return key === 'TNE' || key === 'TSD';
    case 'gram':
      return key === 'GRM' || key === 'G';
    case 'litre':
      return key === 'LTR' || key === 'LPA' || key === 'L';
    case 'dozen':
      return key === 'DZN' || key === 'DOZ';
    case 'pair':
      return key === 'PAR';
    case 'set':
      return key === 'SET';
    case 'metre':
      return key === 'MTR' || key === 'M';
    case 'square_metre':
      return key === 'MTK' || key === 'M²' || key === 'M2';
    case 'cubic_metre':
      return key === 'MTQ' || key === 'M³' || key === 'M3';
    case 'each':
      return key === 'NMB' || key === 'NAP' || key === 'PZA' || key === 'CBZA';
    case 'unknown':
      return false;
    default:
      return false;
  }
};

type CanadaSimaMeasure = {
  id: string | null;
  listName: string | null;
  productName: string | null;
  type: string | null; // "Subsidy" | "Dumping" | "Subsidy and Dumping" | ...
  mifCode: string | null;
  entityCountry: string | null;
  entityCountryIso2: string | null;
  sourceInformationUrl: string | null;
  isActive: boolean;
};

type MexicoCuotaSafeguard = {
  id: string | null;
  htsCode: string | null;
  listName: string | null;
  productName: string | null;
  productScope: string | null;
  productScopeEn: string | null;
  entityCountry: string | null;
  entityCountryIso2: string | null;
  rawCountry: string | null;
  measureFamily: string | null;
  measureFamilyEn: string | null;
  legalActionType: string | null;
  legalActionRaw: string | null;
  measureStage: string | null;
  rateBasis: string | null;
  rateValueExtracted: number | null;
  rateCurrencyOrSymbol: string | null;
  quotaRateRaw: string | null;
  companyScopeType: string | null;
  exporterOrProducerScope: string | null;
  remissionAvailable: boolean | null;
  autoApplyAllowed: boolean | null;
  reviewRequired: boolean | null;
  rateMathAllowed: boolean | null;
  qaLogicStatus: string | null;
  reviewReason: string | null;
  sourceInformationUrl: string | null;
  sourceDocumentTitle: string | null;
  measureId: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  dofPublicationDate: string | null;
};

type ForeignFtaOption = {
  id: string | null;
  agreementId: string | null;
  agreementName: string | null;
  agreementType: string | null;
  listName: string | null;
  originScopeIso2: string[];
  originScopeRaw: string | null;
  countryScopeBasis: string | null;
  ruleType: string | null;
  scopeLevel: string | null;
  scopeValue: string | null;
  preferentialImportRateLogic: string | null;
  requiresOriginQualification: boolean;
  claimRequired: boolean;
  certificateOrProofRequired: boolean;
  tariffScheduleRequired: boolean;
  codeLevelRatesIncluded: boolean;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  effectiveDateStatus: string | null;
  sourceIds: string | null;
  officialSourceUrls: string | null;
  operationalUse: string | null;
  cautions: string | null;
};

type MexicoTigieModification = {
  id: string | null;
  htsCode: string | null;
  appliesToCode: string | null;
  listName: string | null;
  description: string | null;
  generalDutyRate: string | null;
  parsedRate: ParsedTariffRate | null;
  unitOfQuantity: string | null;
  decreeTitle: string | null;
  sectionTitle: string | null;
  annex: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  overallEffectiveTo: string | null;
  rateEffectiveTo: string | null;
  dofPublicationDate: string | null;
  rateEffectType: string | null;
  rateMathAllowed: boolean | null;
  reviewRequired: boolean | null;
  cupoRequired: boolean | null;
  certificateRequired: boolean | null;
  contextNotes: string | null;
  sourceFile: string | null;
};

// One Canada surtax / safeguard record, as normalized by the datahub.
// Shape follows the CA Surtax schema of 2026-08-13: `List Name` is now the
// constant "CA Surtax" and the surtax / safeguard split lives on
// `measureType` + `tariffRegime`, while the old conflict_group / charge_family
// / conflict_role / quota-threshold fields were cut from the dataset.
type ForeignAdditionalDuty = {
  id: string | null;
  biteRecordId: string | null;
  measureId: string | null;
  htsCode: string | null;
  listName: string | null; // now always "CA Surtax"
  caseStatus: string | null; // Active | Expired | Superseded
  description: string | null;
  generalDutyRate: string | null;
  rateNarrative: string | null;
  rateType: string | null; // ad_valorem | none
  parsedRate: ParsedTariffRate | null;
  programName: string | null;
  measureName: string | null;

  // surtax | safeguard. Safeguards are provisional measures pending a CITT
  // injury inquiry and are accounted in CAD field 87, not field 85.
  measureType: string | null;
  tariffRegime: string | null; // CA SURTAX | CA SAFEGUARD
  cbsaCode: string | null;

  regulationNumber: string | null;
  legalInstrumentName: string | null;
  legalAuthority: string | null;
  amendedBy: string | null;
  legalSourceUrl: string | null;

  // Quota side. `offerChoice` marks the rows where the user must state which
  // side of the quota they are on.
  versionType: string | null; // Standard | Within Access | Over Access
  variantName: string | null;
  offerChoice: boolean | null;

  // Precedence. `precedenceConflict` is set when two orders that are not
  // meant to overlap both survived resolution — a data defect, not a
  // stacking case, so the amounts must not be summed.
  imposesSurtax: boolean | null;
  surtaxPrecedence: string | null; // exclusive | subordinate | normal
  precedenceExcludedIf: string[];
  precedenceLegalBasis: string | null;
  precedenceConflict: boolean | null;
  // Set when a quota-gated measure was priced without the user having
  // declared their quota position. The over-access row is the legal default
  // for goods without a valid permit, so it is shown — but it is an
  // assumption, and must not be presented as a settled charge.
  quotaSideAssumed: boolean | null;

  // Origin scope. `originConditional` is set when whether the measure catches
  // these goods depends on a question that has not been answered, or on an
  // exclusion the dataset cannot express.
  originTest: string | null;
  meltPourRequired: boolean | null;
  smeltCastRequired: boolean | null;
  originConditional: boolean | null;
  originConditionalReason: string | null;
  exclusionSummary: string | null;
  deMinimisThreshold: string | null;
  inTransitDate: string | null;

  quotaApplicable: boolean | null;
  quotaVolume: string | null;
  quotaBasis: string | null;
  permitRequirement: string | null;

  remissionAvailable: boolean | null;
  remissionOrderName: string | null;
  remissionOrderNumber: string | null;
  remissionOrderUrl: string | null;
  remissionConditionSummary: string | null;

  effectiveFrom: string | null;
  effectiveTo: string | null;
  additionalRemarks: string | null;
};

// Canada tariff-rate quotas (List Name "CA TRQs"), grouped by ICL item in
// the datahub. A TRQ rate is ordinary customs duty (rate_basis "MFN"), so an
// over-access rate REPLACES the base rate rather than layering on it the way
// a surtax does — which is why these do not travel in `additionalDuties`.
type CanadaQuotaPool = {
  agreement: string; // WTO | CPTPP | CUSMA | CETA | Other | Quota volume
  description: string;
};

// One side of a quota. Absent where the dataset carries no record for it.
// One line-level rate recovered from a varying group's narrative.
type CanadaTrqRateVariant = {
  label: string;
  rateText: string;
  codes: string[];
  parsedRate: ParsedTariffRate | null;
};

type CanadaTrqSide = {
  id: string | null;
  biteRecordId: string | null;
  versionType: string | null; // Within Access | Over Access
  rateType: string | null; // ad_valorem | specific
  rateBasis: string | null; // MFN
  unitOfQuantity: string | null;
  rateNarrative: string | null;
  // True where the rate differs across lines in the group: the stored number
  // is the representative (highest) one and the narrative carries the detail.
  rateVaries: boolean;
  parsedRate: ParsedTariffRate | null;
  // Non-empty only where the rate varies AND every alternative was
  // recoverable. The user picks which one describes their goods.
  rateVariants: CanadaTrqRateVariant[];
  permitRequirement: string | null;
  offerChoice: boolean | null;
};

type CanadaQuota = {
  iclItem: string | null;
  productName: string | null;
  appliesTo: string[];
  pools: CanadaQuotaPool[];
  sharedWith: string | null;
  quotaVolume: string | null;
  quotaYearFrom: string | null;
  quotaYearTo: string | null;
  administrationMethod: string | null;
  supplyManaged: boolean | null;
  preferentialNote: string | null;
  underfillNote: string | null;
  regulationUrl: string | null;
  gacNoticeUrl: string | null;
  agency: string | null;
  additionalRemarks: string | null;
  within: CanadaTrqSide | null;
  over: CanadaTrqSide | null;
  // Sides the dataset has no record for. A rate is never inferred from the
  // other side — an over-access rate is typically two orders of magnitude
  // above the within-access one.
  missingSides: Array<'within' | 'over'>;
};

// The user's declaration of where their shipment sits, keyed by ICL item.
type TrqPosition = 'within' | 'over';

type ForeignTariffMatch = {
  id: string | null;
  sourceCountry: 'Mexico' | 'Canada';
  htsCode: string | null;
  productName: string | null;
  unitOfQuantity: string | null;
  generalDutyRate: string | null;
  parsedRate: ParsedTariffRate | null;
  additionalDuties: ForeignAdditionalDuty[];
  tigieModifications: MexicoTigieModification[];
  ftaOptions: ForeignFtaOption[];
  cuotasSafeguards: MexicoCuotaSafeguard[];
  simaMeasures: CanadaSimaMeasure[];
  trqQuotas: CanadaQuota[];
  pgaRequirements: CanadaPgaRequirement[];
  specialDutyRate: string | null;
  generalDutyRateImport: string | null;
  generalDutyRateExport: string | null;
  chapter: string | null;
  section: string | null;
  listName: string | null;
  tradeType: string | null;
  agency: string | null;
  regulationUrl: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  chapterNotes: string | null;
  rawSource: Record<string, any>;
};

// The scope questions Canada surtax records can turn on. Each maps to a
// request parameter the datahub reads when matching, not to anything the
// client can decide for itself.
type CanadaScopeAnswers = {
  // ss. 2(4) / 2.1(4): goods imported without a valid EIPA item-82 permit are
  // DEEMED over access regardless of volume, so this answer can settle the
  // quota side on its own.
  hasEipaPermit?: boolean;
  quotaSide?: 'within' | 'over';
  meltedAndPouredInChina?: boolean;
  smeltedAndCastInChina?: boolean;
};

// The Canadian PGA programmes carried in the commodity index. Each agency is
// its own `List Name`; `BITE Lens` is the constant "PGA Codes", so the agency
// name is the usable handle when prefilling a screening search.
//
// The full set is listed rather than derived from the flags on screen: the
// tariff tool applies each record's own `HS Operator` before flagging, which is
// deliberately narrower than a screening search. Passing only what flagged
// would hide records the Screening module would legitimately show. An agency
// with nothing to match simply returns nothing.
const CANADA_PGA_LIST_NAMES = [
  'PGA Health Canada',
  'PGA Natural Resources Canada',
  'PGA Public Health Agency of Canada',
  'PGA Canadian Food Inspection Agency',
  'PGA Global Affairs Canada',
  'PGA Environment and Climate Change Canada',
  'PGA Fisheries and Oceans Canada',
  'PGA Canadian Nuclear Safety Commission',
  'PGA Canada Border Services Agency Food Plant and Animal Soil Risks',
  'PGA Transport Canada',
];

const CANADA_SIMA_LIST_NAME = 'Special Import Measures Act (SIMA)';

// A Canada PGA (Partner Government Agency) requirement matched to an HS code.
// Lives in bite_app_commodities_combined under BITE Lens "PGA Codes" with
// Trade Type "CA Import"; the agency is carried by List Name ("PGA Health
// Canada"), never by the lens, which is the constant "PGA Codes".
type CanadaPgaRequirement = {
  id: string;
  agency: string | null;
  program: string | null;
  hsCode: string | null;
  hsOperator: string | null; // Equal To | Starts With
  intendedUseCode: string | null;
  productCategory: string | null;
  regulationUrl: string | null;
};

// Does a PGA record actually cover the entered line?
//
// These records carry their own match semantics in `HS Operator`, which the
// commodity search does not apply — it matches at heading, subheading and
// tariff-item level, so an "Equal To" record for 8479.89.20.21 would otherwise
// flag a search for 8479.89.20.99. A record with no HS Code at all cannot be
// tied to a line and never matches.
//
// Where the entered code is LESS specific than the record's, a prefix match
// counts: the user has not classified to ten digits yet and the requirement
// may well be theirs. A PGA flag is informational, and missing a required
// filing is a worse outcome than showing one that turns out not to apply.
const pgaCoversCode = (
  recordHsCode: string | null,
  hsOperator: string | null,
  enteredDigits: string,
): boolean => {
  const rec = String(recordHsCode || '').replace(/\D/g, '');
  if (!rec || !enteredDigits) return false;

  const operator = String(hsOperator || '').trim().toLowerCase();
  if (operator === 'equal to') {
    return rec === enteredDigits || rec.startsWith(enteredDigits);
  }
  // "Starts With", and anything unrecognized, matches in either direction.
  return enteredDigits.startsWith(rec) || rec.startsWith(enteredDigits);
};

// Canadian tariff lines are published dotted (0402.91.10.00). The datasets
// store them undotted, so restore the published shape when showing a user
// which lines a quota covers.
const formatHtsDotted = (code: string): string => {
  const d = String(code || '').replace(/\D/g, '');
  if (d.length !== 10) return code;
  return `${d.slice(0, 4)}.${d.slice(4, 6)}.${d.slice(6, 8)}.${d.slice(8, 10)}`;
};

// A relief or non-application condition attached to one Canada measure.
//
// The tool never silently REMOVES a charge on the strength of one of these:
// each turns on something it cannot fully verify — the shipment total rather
// than this line, a transit date it only has a proxy for, or a remission the
// importer must actually claim. Under-charging is the worse failure, so the
// duty stands and the condition is surfaced for the user to check.
type CanadaReliefCondition = {
  label: string;
  detail: string;
  url?: string | null;
};

type CanadaReliefItem = {
  id: string;
  measure: string;
  regulation: string | null;
  conditions: CanadaReliefCondition[];
};

// Pull the numeric floor out of a de_minimis_threshold string such as
// "CAD 5,000 cumulative value for duty (s. 5)".
const parseDeMinimisAmount = (raw: string | null): number | null => {
  if (!raw) return null;
  const m = String(raw).match(/(?:CAD|\$)\s*([\d,]+(?:\.\d+)?)/i);
  if (!m) return null;
  const n = Number(m[1].replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

type CanadaScopeQuestion = {
  id: keyof CanadaScopeAnswers;
  prompt: string;
  helperText: string;
  options: Array<{ id: string; label: string }>;
  selected?: string;
};

type ForeignTariffState = {
  isLoading: boolean;
  match: ForeignTariffMatch | null;
  hitCount: number;
};

const EMPTY_FOREIGN_TARIFF_STATE: ForeignTariffState = {
  isLoading: false,
  match: null,
  hitCount: 0,
};

const BULK_STATUS_COLORS: Record<
  TariffStatus['color'],
  { border: string; bg: string; text: string; icon: string; action: string }
> = {
  Green: {
    border: '#1F7A4D',
    bg: 'rgba(71, 205, 137, 0.08)',
    text: '#7EE7B6',
    icon: '#47CD89',
    action: '#47CD89',
  },
  Yellow: {
    border: '#B57507',
    bg: 'rgba(253, 176, 34, 0.08)',
    text: '#FFD699',
    icon: '#FDB022',
    action: '#FDB022',
  },
  Red: {
    border: '#A63232',
    bg: 'rgba(240, 68, 56, 0.08)',
    text: '#FECACA',
    icon: '#F04438',
    action: '#F04438',
  },
};

const formatUSD = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

type DutyComponent = {
  label: string;
  amount: number;
  formula: string;
  applied: boolean;
};

type DutyCalc = {
  appliedAmount: number;
  components: DutyComponent[];
  warnings: string[];
};

// Duty math given a parsed rate. Resolves the three combinator patterns we
// support today:
//   floor       → max(primary, floor)      [Canada "but not less than"]
//   ceiling     → min(primary, ceiling)    [Canada "but not more than"]
//   additional  → primary + additional     [Mexico AMX "X% + Y$/Kg"]
// Unit mismatches between the rate string and the schedule's UoQ raise
// warnings rather than silently producing a wrong number, since quantity-
// conversion (e.g. kg ↔ each) needs data we don't have at this layer.
const calculateTariffDuty = (
  parsed: ParsedTariffRate,
  productValue: number,
  // A parsed rate can have multiple specific components with DIFFERENT units
  // (e.g. a per-piece primary plus a per-kg additional). Resolve the quantity
  // per component unit instead of applying one quantity to every component.
  quantityForUnit: (unit: TariffRateUnit) => number,
  scheduleUoQ: string | null,
): DutyCalc => {
  const warnings: string[] = [];
  const value = Number.isFinite(productValue) ? productValue : 0;

  const components: DutyComponent[] = [];

  const computeComponent = (
    comp: TariffRateComponent,
    role: 'primary' | 'floor' | 'ceiling' | 'additional',
  ): { amount: number; component: DutyComponent } => {
    if (comp.kind === 'free') {
      return {
        amount: 0,
        component: {
          label: 'Free',
          amount: 0,
          formula: 'Duty-free',
          applied: role === 'primary',
        },
      };
    }
    if (comp.kind === 'percent') {
      const amt = value * (comp.percent / 100);
      return {
        amount: amt,
        component: {
          label: `Ad valorem (${comp.percent}%)`,
          amount: amt,
          formula: `${comp.percent}% × ${formatUSD(value)} = ${formatUSD(amt)}`,
          applied: false,
        },
      };
    }
    // specific
    if (!unitMatchesUoQ(comp.unit, scheduleUoQ)) {
      warnings.push(
        `Rate "${comp.display}" uses ${
          comp.unit
        }, but the schedule's Unit of Quantity is ${
          scheduleUoQ || 'unspecified'
        }. Quantity entered must be in ${comp.unit} to be accurate.`,
      );
    }
    if (comp.qualifier) {
      warnings.push(
        `Rate "${comp.display}" is charged per ${comp.unit} ${comp.qualifier} — quantity entered must reflect ${comp.qualifier} content, not total product weight.`,
      );
    }
    const rawQty = quantityForUnit(comp.unit);
    const qty = Number.isFinite(rawQty) ? rawQty : 0;
    const amt = comp.amountPerUnit * qty;
    const perDisplay =
      comp.amountPerUnit < 1
        ? `${(comp.amountPerUnit * 100).toFixed(2)}¢`
        : formatUSD(comp.amountPerUnit);
    const roleLabel =
      role === 'primary'
        ? 'Specific'
        : role === 'floor'
        ? 'Floor'
        : role === 'ceiling'
        ? 'Ceiling'
        : 'Additional specific';
    return {
      amount: amt,
      component: {
        label: `${roleLabel} (${comp.display})`,
        amount: amt,
        formula: `${perDisplay}/${comp.unit} × ${qty} ${
          comp.unit
        } = ${formatUSD(amt)}`,
        applied: false,
      },
    };
  };

  const primary = computeComponent(parsed.primary, 'primary');
  components.push(primary.component);

  // Additional (Mexico AMX): both primary and additional are charged.
  if (parsed.additional) {
    const additional = computeComponent(parsed.additional, 'additional');
    components.push(additional.component);
    const total = primary.amount + additional.amount;
    primary.component.applied = true;
    additional.component.applied = true;
    return {
      appliedAmount: total,
      components: components,
      warnings: warnings,
    };
  }

  // Floor / ceiling: max-of or min-of with primary.
  let appliedAmount = primary.amount;
  let winningLabelPrefix:
    | 'Specific'
    | 'Ad valorem'
    | 'Free'
    | 'Floor'
    | 'Ceiling' = (() => {
    switch (parsed.primary.kind) {
      case 'free':
        return 'Free';
      case 'percent':
        return 'Ad valorem';
      default:
        return 'Specific';
    }
  })();

  if (parsed.floor) {
    const floor = computeComponent(parsed.floor, 'floor');
    components.push(floor.component);
    if (floor.amount > primary.amount) {
      appliedAmount = floor.amount;
      winningLabelPrefix = 'Floor';
    }
  }

  if (parsed.ceiling) {
    const ceiling = computeComponent(parsed.ceiling, 'ceiling');
    components.push(ceiling.component);
    if (ceiling.amount < appliedAmount) {
      appliedAmount = ceiling.amount;
      winningLabelPrefix = 'Ceiling';
    }
  }

  const winningIdx = components.findIndex((c) =>
    c.label.startsWith(winningLabelPrefix),
  );
  if (winningIdx >= 0) {
    components[winningIdx].applied = true;
  } else {
    components[0].applied = true;
  }

  return {
    appliedAmount: appliedAmount,
    components: components,
    warnings: warnings,
  };
};

// Map our country-neutral TariffRateUnit onto the canonical unit strings
// the US TariffPage pipeline already understands (kg, g, l, pcs, prs, doz,
// m, m2, m3, t). Used to build requiredAmountUnits + rate strings for the
// US summary-row shape.
const tariffUnitToCanonical = (u: TariffRateUnit): string => {
  switch (u) {
    case 'kg':
      return 'kg';
    case 'gram':
      return 'g';
    case 'tonne':
      return 't';
    case 'litre':
      return 'l';
    case 'each':
      return 'pcs';
    case 'pair':
      return 'prs';
    case 'set':
      return 'pcs';
    case 'dozen':
      return 'doz';
    case 'metre':
      return 'm';
    case 'square_metre':
      return 'm2';
    case 'cubic_metre':
      return 'm3';
    default:
      return 'kg';
  }
};

// Build the US-shaped tariffResults.data object from a foreign match so
// TarifFResultsComp can render it as if it were a US lookup. Combinator
// patterns map to:
//   primary alone        → one Base Duty row
//   primary + additional → Base Duty + Additional Duty rows  (MX AMX)
//   primary + floor      → one Base Duty row carrying the raw rate
//                          string; cost is overridden because the US
//                          pipeline doesn't natively handle the floor.
type ForeignTariffData = {
  baseDutyRate: string;
  baseDutyRateRaw: string;
  productDesc: string;
  results: Array<{ type: string; code: string; rate: string }>;
  lines: any[];
  optionalExclusions: any[];
  ftaOptions: any[];
  hasIeepaTariffs: boolean;
  requiredAmountUnits: string[];
  additionalDutyRate: string;
  // Set when a tariff-rate quota applies but no duty can be stated yet:
  // either the user has not declared their quota position, or they have and
  // the dataset carries no rate for that side. The cost panel shows the
  // message in place of figures rather than a misleading $0.00.
  trqPendingMessage?: string;
};

// Returns true when at least one FTA published on the match has been
// claimed via the user's enabledFtas toggle map. Used to drive the
// "Free" base override in both the display and cost calc.
const isAnyForeignFtaClaimed = (
  match: { ftaOptions?: ForeignFtaOption[] } | null,
  enabledFtas: Record<string, boolean>,
): boolean => {
  if (!match?.ftaOptions || match.ftaOptions.length === 0) return false;
  return match.ftaOptions.some((f) => {
    const key = f.agreementId || f.id;
    return Boolean(key && enabledFtas[key]);
  });
};

// Project the backend's normalized FTA shape into the { code, name } shape
// TariffResults / TariffFtaTable expect. The "code" is what feeds
// enabledFtas[code] when the user toggles a row, so it must round-trip
// with isAnyForeignFtaClaimed above.
const projectForeignFtas = (
  ftas: ForeignFtaOption[] | undefined,
): Array<{ code: string; name: string }> => {
  if (!ftas) return [];
  return ftas
    .map((f) => {
      const code = f.agreementId || f.id;
      const name = f.agreementName || f.agreementId || code;
      if (!code || !name) return null;
      return { code: String(code), name: String(name) };
    })
    .filter((x): x is { code: string; name: string } => Boolean(x));
};

// Resolve a quota to the rate that actually applies, given the user's
// declaration.
//
// Per the rule for this dataset: OVER access replaces the base rate with the
// TRQ rate; WITHIN access falls back to the ordinary base rate. Until the
// user declares, neither is true and no duty can be stated — the two sides
// differ by orders of magnitude (0.86¢ per chick against a prohibitive
// over-access rate), so defaulting either way produces an authoritative
// looking number that is badly wrong.
type TrqResolution =
  | { state: 'none' }
  | { state: 'pending'; quota: CanadaQuota }
  | { state: 'within'; quota: CanadaQuota; rate: ParsedTariffRate | null }
  | { state: 'over'; quota: CanadaQuota; rate: ParsedTariffRate }
  | { state: 'unavailable'; quota: CanadaQuota; side: TrqPosition };

// Pick the rate for one side, preferring a line-level variant the user chose
// over the group's stored figure — which describes only one of the group's
// lines and so cannot price a shipment on any of the others.
const trqSideRate = (
  side: CanadaTrqSide | null,
  chosenIndex: number | undefined,
): ParsedTariffRate | null => {
  if (!side) return null;

  // A varying side is resolved ONLY by a chosen line. The group's stored rate
  // describes one line of several and pricing another from it is the Item 140
  // undercharge, so it is never a fallback here. Gated on `rateVaries` rather
  // than on the parser having marked the rate unparsed — the guarantee then
  // lives at the point that depends on it instead of two modules away.
  if (side.rateVaries) {
    const variants = side.rateVariants || [];
    if (variants.length === 0 || chosenIndex === undefined) return null;
    const chosen = variants[chosenIndex];
    return chosen?.parsedRate?.parsed ? chosen.parsedRate : null;
  }

  return side.parsedRate?.parsed ? side.parsedRate : null;
};

const resolveTrq = (
  quotas: CanadaQuota[] | undefined,
  positions: Record<string, TrqPosition>,
  variantChoice: Record<string, number> = {},
): TrqResolution => {
  const list = Array.isArray(quotas) ? quotas : [];
  if (list.length === 0) return { state: 'none' };

  // One HS code sits in one quota in practice; if the data ever gives more,
  // the first is used and the panel lists them all.
  const quota = list[0];
  const declared = positions[quota.iclItem || ''];
  if (!declared) return { state: 'pending', quota: quota };

  if (declared === 'within') {
    // The within-access record states the rate for its own side, and it is
    // what the panel shows the user before they choose ($0.0086 per chick).
    // Selecting it has to charge that rate, not the ordinary base rate for
    // the entered line — which may be the OVER-access tariff item, since one
    // record covers both lines of the group. Falls back to the base rate only
    // where the dataset has no within record.
    const rate = trqSideRate(
      quota.within,
      variantChoice[`${quota.iclItem || ''}|within`],
    );
    // A varying within side that has not been resolved to a line cannot be
    // priced — and must NOT quietly fall through to the ordinary base rate,
    // which for a group covering both quota sides may be the over-access
    // item's rate.
    if (quota.within?.rateVaries && !rate) {
      return { state: 'unavailable', quota: quota, side: 'within' };
    }
    return { state: 'within', quota: quota, rate: rate };
  }

  const overRate = trqSideRate(
    quota.over,
    variantChoice[`${quota.iclItem || ''}|over`],
  );
  if (!overRate) {
    return { state: 'unavailable', quota: quota, side: 'over' };
  }
  return { state: 'over', quota: quota, rate: overRate };
};

// Build the HTS Code History notifications from a set of schedule records.
//
// Extracted so the Canada / Mexico lookup can produce the same list the US one
// does — it was inline in the US branch of handleSubmit, which is why the
// foreign tabs always read "No recent changes detected".
type HtsScheduleRecord = {
  id: string;
  rate: string | null;
  effFrom: Date | null;
  effTo: Date | null;
};

const buildHtsHistory = (
  records: HtsScheduleRecord[],
  baseDate: Date,
): HtsHistoryItem[] => {
  const ninetyDaysAgo = new Date(baseDate);
  ninetyDaysAgo.setDate(baseDate.getDate() - 90);
  const ninetyDaysFromNow = new Date(baseDate);
  ninetyDaysFromNow.setDate(baseDate.getDate() + 90);

  // Newest first, so "previous record" is the one after it.
  const sorted = [...records].sort((a, b) => {
    const tA = a.effFrom ? a.effFrom.getTime() : 0;
    const tB = b.effFrom ? b.effFrom.getTime() : 0;
    return tB - tA;
  });

  const out: HtsHistoryItem[] = [];
  sorted.forEach((hit, index) => {
    const { rate, effFrom, effTo } = hit;
    // Skip missing dates and epoch placeholders (1969/1970) — valid schedule
    // dates in this context are well after 2000.
    if (effFrom && effFrom.getFullYear() > 2000) {
      if (effFrom >= ninetyDaysAgo && effFrom <= baseDate) {
        const previousRecord = sorted[index + 1];
        const summaryText = !previousRecord
          ? `HTS code added to tariff schedule with a rate of ${rate}.`
          : previousRecord.rate !== rate
          ? `HTS code updated — duty rate changed from ${previousRecord.rate} to ${rate}.`
          : `HTS code updated — duty rate remains ${rate}.`;
        out.push({
          id: `update-${index}`,
          date: format(effFrom, 'MMM d, yyyy'),
          summary: summaryText,
        });
      }
    }

    if (effTo && effTo <= ninetyDaysFromNow && effTo >= baseDate) {
      out.push({
        id: `expire-${index}`,
        date: format(effTo, 'MMM d, yyyy'),
        summary:
          'This HTS provision is scheduled to expire. Ensure your classification remains valid for future entries.',
      });
    }
  });

  return out;
};

const buildForeignTariffData = (
  match: any,
  enabledFtas: Record<string, boolean> = {},
  trqPositions: Record<string, TrqPosition> = {},
  trqVariantChoice: Record<string, number> = {},
  enabledRemissions: Record<string, boolean> = {},
): ForeignTariffData => {
  const htsCode: string = match?.htsCode || '';

  // A tariff-rate quota replaces the base rate when the goods are over
  // access, so it is resolved before anything reads `parsed`.
  const trq = resolveTrq(match?.trqQuotas, trqPositions, trqVariantChoice);
  const parsed: ParsedTariffRate | null =
    trq.state === 'over'
      ? trq.rate
      : trq.state === 'within' && trq.rate
      ? trq.rate
      : match?.parsedRate || null;

  const rateDisplayFor = (c: TariffRateComponent): string => {
    if (c.kind === 'free') return 'Free';
    if (c.kind === 'percent') return `${c.percent}%`;
    return `$${c.amountPerUnit}/${tariffUnitToCanonical(c.unit)}`;
  };

  // Mexico TIGIE Annex Modifications: an active modification temporarily
  // replaces the base rate. We surface a "Modified" placeholder in the
  // Base Duty row so the user sees the original was overridden, and add
  // one row per modification with its new rate. foreignCostOverrides
  // routes the cost calc through the modification's parsedRate so the
  // dollars line up with the displayed rate.
  const tigieModifications: MexicoTigieModification[] =
    match?.tigieModifications && Array.isArray(match.tigieModifications)
      ? match.tigieModifications
      : [];
  const hasActiveTigie = tigieModifications.length > 0;
  const tigieModificationRows: Array<{
    type: string;
    code: string;
    rate: string;
  }> = tigieModifications.map((mod) => {
    const modRate =
      mod.parsedRate?.parsed && mod.parsedRate.primary.kind === 'percent'
        ? `${mod.parsedRate.primary.percent}%`
        : mod.parsedRate?.parsed && mod.parsedRate.primary.kind === 'specific'
        ? `$${mod.parsedRate.primary.amountPerUnit}/${tariffUnitToCanonical(
            mod.parsedRate.primary.unit,
          )}`
        : mod.parsedRate?.parsed && mod.parsedRate.primary.kind === 'free'
        ? 'Free'
        : mod.generalDutyRate || 'Modified';
    return {
      type: mod.listName || 'MX TIGIELIGIE Annex Modifications',
      code: mod.htsCode || mod.appliesToCode || htsCode,
      rate: modRate,
    };
  });

// Does this record carry anything that could relieve or disapply the charge?
// Read off the record alone, so the Duty Summary row can flag it without
// needing the product value or loading date.
const hasReliefConditions = (ad: ForeignAdditionalDuty): boolean =>
  Boolean(
    ad.remissionAvailable === true || ad.deMinimisThreshold || ad.inTransitDate,
  );

// Condense a record's `cbsa_code` into something that fits under a Duty
// Summary row. The field is documented as "the CBSA surtax or safeguard code
// an importer enters on the CAD", but where an order's code changed mid-life
// it carries the whole history instead — SOR/2025-148's is a ~250-character
// paragraph. The CAD field number is the part that always matters (85 for a
// surtax, 87 for a safeguard, and filing a safeguard under 85 is wrong), so
// that is extracted, and the code itself is shown only when it is short
// enough to read at a glance.
const CBSA_CODE_INLINE_MAX = 48;
const summarizeCbsaCode = (
  cbsaCode: string | null,
  measureType: string | null,
): string => {
  const parts: string[] = [];
  const raw = (cbsaCode || '').trim();

  // Drop the trailing "— CAD field 85 Surtax" clause first (it is restated
  // below), then judge the length — otherwise a perfectly readable code like
  // "25154A (steel) / 25154B (aluminium)" is rejected for the suffix alone.
  const codeOnly = raw.replace(/\s*[—–-]?\s*CAD field \d+\s*\w*\.?\s*$/i, '').trim();
  if (codeOnly && codeOnly.length <= CBSA_CODE_INLINE_MAX) {
    parts.push(`CBSA code ${codeOnly}`);
  }

  const field = raw.match(/CAD field (\d+)/i)?.[1];
  const isSafeguard = measureType === 'safeguard';
  if (field) {
    parts.push(`CAD field ${field} (${isSafeguard ? 'Safeguard' : 'Surtax'})`);
  } else if (isSafeguard) {
    parts.push('Provisional safeguard — accounted in CAD field 87');
  }

  return parts.join(' · ');
};

  // Canada surtax / safeguard records render as their own rows in the
  // Duty Summary regardless of how the base rate parsed. We compute them
  // once here so all of the parser branches below can append the same
  // tail. foreignCostOverrides re-runs the math against the raw list so
  // the cost panel reflects them additively.
  const additionalDuties: ForeignAdditionalDuty[] = Array.isArray(
    match?.additionalDuties,
  )
    ? match!.additionalDuties
    : [];
  const additionalDutyRows: Array<{
    type: string;
    code: string;
    rate: string;
    note?: string;
    noteClass?: string;
    _codeDigits?: string;
  }> = additionalDuties.map((ad) => {
    const baseRate =
      ad.parsedRate?.parsed && ad.parsedRate.primary.kind === 'percent'
        ? `${ad.parsedRate.primary.percent}%`
        : ad.parsedRate?.parsed && ad.parsedRate.primary.kind === 'specific'
        ? `$${ad.parsedRate.primary.amountPerUnit}/${tariffUnitToCanonical(
            ad.parsedRate.primary.unit,
          )}`
        : ad.generalDutyRate || 'Free';
    // The row stays when the user claims its remission order, so the Duty
    // Summary (and the PDF) still show which surtax was remitted and why.
    const remitted = isRemitted(ad, enabledRemissions);
    // A charge that depends on an unanswered question is still costed
    // (understating duty is the worse failure) but must not read as settled.
    const adRate = remitted
      ? 'Remitted'
      : ad.originConditional
      ? `${baseRate} if applicable`
      : ad.quotaSideAssumed
      ? `${baseRate} if over access`
      : baseRate;
    // Program label. The controller takes this from `surtax_program_name`,
    // since "List Name" is now the constant "CA Surtax" and would render
    // every measure identically. Fall back through measure name → list name.
    const programLabel =
      ad.programName || ad.measureName || ad.listName || 'Additional Duty';

    // Caveats shown under the measure name. Ordered most to least urgent.
    const notes: string[] = [];
    if (remitted) {
      const order = [ad.remissionOrderName, ad.remissionOrderNumber]
        .filter(Boolean)
        .join(', ');
      notes.push(`Remission claimed${order ? ` under ${order}` : ''}`);
    }
    if (ad.precedenceConflict) {
      notes.push(
        'Two orders that are not meant to overlap both resolved to these goods — a data issue. Only the highest is charged; verify which one applies.',
      );
    }
    if (ad.originConditional && ad.originConditionalReason) {
      notes.push(ad.originConditionalReason);
    }
    if (ad.quotaSideAssumed) {
      notes.push(
        'Quota position not declared. Shown at the over-access rate because goods imported without a valid EIPA item-82 permit are deemed over access — declare your position to settle it.',
      );
    }
    // Safeguards are accounted in CAD field 87, surtaxes in field 85 — a real
    // operational split for whoever files the entry. `cbsa_code` holds that
    // field number, and usually the code too, but for orders whose code
    // changed mid-life it is a paragraph of history (SOR/2025-148 runs to
    // ~250 characters), so only a short code is shown verbatim.
    // Relief is evaluated in the Relief & Exemptions panel, which has the
    // product value and loading date this function does not; the row only
    // points at it.
    if (!remitted && hasReliefConditions(ad)) {
      notes.push('Relief may be available');
    }
    const cbsaNote = summarizeCbsaCode(ad.cbsaCode, ad.measureType);
    if (cbsaNote) notes.push(cbsaNote);

    // Surtax / safeguard rows show the program label in the type column (in
    // line with "Base Duty") and leave the code column blank — repeating the
    // line's base HTS code adds nothing on these rows. `_codeDigits` keeps
    // the underlying HTS digits available for any code-keyed logic that
    // reads the row.
    return {
      type: programLabel,
      code: '',
      rate: adRate,
      note: notes.length > 0 ? notes.join(' · ') : undefined,
      noteClass: ad.precedenceConflict ? 'tariff-row-note-warn' : undefined,
      _codeDigits: (htsCode || '').replace(/\D/g, ''),
    };
  });

  // Quantity-unit collection runs across the base parsed rate AND every
  // overlay that can introduce a specific component — active TIGIE
  // modifications + Canada additional duties — so the search form
  // surfaces an input for each unit the calculator will end up reading.
  // Without this the cost panel can silently default amountInputs[unit]
  // to 0 (or worse, a stale value) when an overlay rate is specific but
  // the base rate is ad-valorem-only.
  const collectQuantityUnits = (): string[] => {
    const units = new Set<string>();
    const consider = (c?: TariffRateComponent) => {
      if (c && c.kind === 'specific') {
        units.add(tariffUnitToCanonical(c.unit));
      }
    };
    if (parsed) {
      consider(parsed.primary);
      consider(parsed.additional);
      consider(parsed.floor);
      consider(parsed.ceiling);
    }
    for (const mod of tigieModifications) {
      const mp = mod.parsedRate;
      if (mp?.parsed) {
        consider(mp.primary);
        consider(mp.additional);
        consider(mp.floor);
        consider(mp.ceiling);
      }
    }
    for (const ad of additionalDuties) {
      const ap = ad.parsedRate;
      if (ap?.parsed) {
        consider(ap.primary);
        consider(ap.additional);
        consider(ap.floor);
        consider(ap.ceiling);
      }
    }
    return Array.from(units);
  };

  // FTAs (Mexico Preferential Dataset, etc.) are surfaced as toggles via
  // the existing US ftaOptions / enabledFtas / toggleFta plumbing. When
  // the user claims any one of them the base rate becomes Free. Note: FTA
  // claim takes priority over TIGIE "Modified" since a preferential rate
  // overrides both ordinary base and temporary annex modifications.
  const projectedFtaOptions = projectForeignFtas(match?.ftaOptions);
  const hasClaimedFta = isAnyForeignFtaClaimed(
    match as { ftaOptions?: ForeignFtaOption[] } | null,
    enabledFtas,
  );

  // A quota applies but no rate can be stated. This must come before every
  // parser branch below, because those would otherwise print the ordinary
  // base rate as if it were the answer.
  if (trq.state === 'pending' || trq.state === 'unavailable') {
    const pendingMessage =
      trq.state === 'pending'
        ? 'Declare whether this shipment is within or over the quota to see the duty.'
        : `The dataset carries no ${trq.side}-access rate for ${
            trq.quota.iclItem || 'this quota'
          }, so the duty cannot be calculated.`;
    const baseRowRate =
      trq.state === 'pending' ? 'Declare quota position' : 'Rate unavailable';

    return {
      baseDutyRate: baseRowRate,
      baseDutyRateRaw: baseRowRate,
      productDesc: match?.productName || '',
      results: [
        { type: 'Base Duty', code: htsCode, rate: baseRowRate },
        ...tigieModificationRows,
        ...additionalDutyRows,
      ],
      lines: [],
      optionalExclusions: [],
      ftaOptions: projectedFtaOptions,
      hasIeepaTariffs: false,
      requiredAmountUnits: collectQuantityUnits(),
      additionalDutyRate: '0%',
      trqPendingMessage: pendingMessage,
    };
  }

  // Display-only fallback for unparsed rates: just show the raw schedule
  // string in a single row so the user still sees what the index returned.
  if (!parsed || !parsed.parsed) {
    const raw: string =
      match?.generalDutyRate || match?.generalDutyRateImport || '—';
    const baseRowRate = hasClaimedFta
      ? 'Free'
      : hasActiveTigie
      ? 'Modified'
      : raw;
    return {
      baseDutyRate: baseRowRate,
      baseDutyRateRaw: raw,
      productDesc: match?.productName || '',
      results: [
        { type: 'Base Duty', code: htsCode, rate: baseRowRate },
        ...tigieModificationRows,
        ...additionalDutyRows,
      ],
      lines: [],
      optionalExclusions: [],
      ftaOptions: projectedFtaOptions,
      hasIeepaTariffs: false,
      // Overlays (TIGIE mods + CA additional duties) can still need
      // quantity even when the base rate didn't parse, so collect from
      // them rather than returning an empty list.
      requiredAmountUnits: collectQuantityUnits(),
      additionalDutyRate: '0%',
    };
  }

  // Canada "but not less than" / "but not more than": preserve the raw
  // rate string so the user sees the actual schedule entry. Cost is
  // overridden externally via calculateTariffDuty.
  if (parsed.floor || parsed.ceiling) {
    const raw = parsed.raw || match?.generalDutyRate || '—';
    const baseRowRate = hasClaimedFta
      ? 'Free'
      : hasActiveTigie
      ? 'Modified'
      : raw;
    return {
      baseDutyRate: baseRowRate,
      baseDutyRateRaw: raw,
      productDesc: match?.productName || '',
      results: [
        { type: 'Base Duty', code: htsCode, rate: baseRowRate },
        ...tigieModificationRows,
        ...additionalDutyRows,
      ],
      lines: [],
      optionalExclusions: [],
      ftaOptions: projectedFtaOptions,
      hasIeepaTariffs: false,
      requiredAmountUnits: collectQuantityUnits(),
      additionalDutyRate: '0%',
    };
  }

  // Standard primary row.
  const originalBaseDutyRate = rateDisplayFor(parsed.primary);
  const baseDutyRate = hasClaimedFta
    ? 'Free'
    : hasActiveTigie
    ? 'Modified'
    : originalBaseDutyRate;
  const rows: Array<{ type: string; code: string; rate: string }> = [
    { type: 'Base Duty', code: htsCode, rate: baseDutyRate },
    ...tigieModificationRows,
  ];

  // Mexico AMX adds an Additional Duty row so the US aggregator picks it
  // up as a non-base contribution.
  let additionalDutyRate = '0%';
  if (parsed.additional) {
    const addRate = `$${
      parsed.additional.amountPerUnit
    }/${tariffUnitToCanonical(parsed.additional.unit)}`;
    rows.push({ type: 'Additional Duty', code: htsCode, rate: addRate });
    additionalDutyRate = addRate;
  }

  rows.push(...additionalDutyRows);

  return {
    baseDutyRate: baseDutyRate,
    baseDutyRateRaw: originalBaseDutyRate,
    productDesc: match?.productName || '',
    results: rows,
    lines: [],
    optionalExclusions: [],
    ftaOptions: projectedFtaOptions,
    hasIeepaTariffs: false,
    requiredAmountUnits: collectQuantityUnits(),
    additionalDutyRate: additionalDutyRate,
  };
};

/* -------------------- Component -------------------- */
const TariffPage: React.FC<any> = (props: any) => {
  // --- INJECTED START: Route & Mode Logic ---
  const { id: routeTariffId } = useParams<{ id?: string }>();
  const history = useHistory();
  const location = useLocation();
  const forceStatic = Boolean(props?.forceStatic);
  const tariffId = forceStatic ? undefined : routeTariffId;
  // Canada & Mexico tariffs are released to production — available in all
  // environments. (White-label access is still gated separately below.)
  const useInternationalTariffCountries = true;

  const [selectedTariffCountry, setSelectedTariffCountry] =
    useState<TariffCountry>(() =>
      parseSavedTariffCountry(props?.tariffJurisdiction) ||
      parseTariffCountryParam(
        new URLSearchParams(location.search).get('country'),
        useInternationalTariffCountries,
      ),
    );
  // If we have an ID, start in view-only mode. Otherwise (standalone), edit mode is always true.
  const [isEditMode, setIsEditMode] = useState(forceStatic ? false : !tariffId);
  const isStaticView = forceStatic || (Boolean(tariffId) && !isEditMode);
  // stripe
  const [userhasTariffs, setUserHasTariffs] = useState(true);
  const [htsHistory, setHtsHistory] = useState<HtsHistoryItem[]>([]);
  const [riskFlags, setRiskFlags] = useState<RiskFlag[]>([]);
  const [hideIeepa, setHideIeepa] = useState(false);

  // Fetch saved data if ID exists
  const { data: dbTariffData, refetch: refetchDbData } = useGetTariffById(
    tariffId,
    userhasTariffs && !forceStatic,
  );
  // const updateTariffResultMutation = useUpdateTariffResult(); // TODO: Add hook
  // --- INJECTED END ---
  const { isWhiteLabel } = useContext(GlobalContext);
  const { tenant_access } = useContext(GlobalContext);
  const { primary, origin } = props.location?.state || {};
  // Read once: syncing the country tab to the URL replaces the location and drops its state.
  const [carriedOrigin] = useState(() => {
    const option = findScreeningCountryOption(origin);
    return {
      originCountry: option?.name || '',
      originIso2: option?.iso2 || '',
    };
  });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAILoading] = useState(false);
  const [tariffResults, setTariffResults] = useState<any>({
    isLoading: false,
    data: {},
  });

  const targetRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const setShowPDFDownload = async () => {
    setIsGeneratingPDF(true);

    try {
      // Same vector pdfkit report used by Entity Screening's "PDF report"
      // export, built from the saved-ticket snapshot shape (not a DOM
      // screenshot), so the two reports share one design/layout.
      const snapshot = buildTariffSnapshot();
      await screeningTicketTariffPDF(
        {
          ticketName: tariffInputs.htsCode
            ? `Tariff — ${tariffInputs.htsCode}`
            : 'Tariff Results',
          docType: 'Tariff',
          tariffResults: snapshot,
          preparedBy:
            [cur_user?.basics?.firstName, cur_user?.basics?.lastName]
              .filter(Boolean)
              .join(' ') || undefined,
        },
        `TariffResults ${moment().format('YYYY-MM-DD-hh-mm-ss')}.pdf`,
      );
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const [effectDate, setEffectDate] = useState(new Date());
  const [loadDate, setLoadDate] = useState(new Date());
  const [productValue, setProductValue] = useState(1000);
  const [frequency, setFrequency] = useState<string>('Once');
  const [dateStrategy, setDateStrategy] = useState<'current' | 'original'>(
    'current',
  );

  // Import bans (utils/importBans) covering the checked line, and the filer's
  // answers to their gate questions. A gate missing from the map still holds
  // its stored answer, which keeps the ban on.
  const [importBans, setImportBans] = useState<ImportBan[]>([]);
  const [banGateAnswers, setBanGateAnswers] = useState<
    Record<string, BanGateAnswer>
  >({});
  // The HTS line and origin those answers belong to. They carry across
  // re-runs of the same check (a date change, re-entering Live Mode on a saved
  // one) but never onto another product, where a leftover "imported before the
  // ban" answer would quietly lift its ban.
  const importBanCheckRef = React.useRef('');
  // The bans exclude goods from the United States, so only a US check can be
  // barred. While one is active it is the whole result: no entry, no duty.
  const activeImportBan =
    selectedTariffCountry === 'US'
      ? findActiveImportBan(importBans, banGateAnswers)
      : null;
  const [tariffCandidates, setTariffCandidates] = useState<Candidate[]>([]);
  const [enabledExclusions, setEnabledExclusions] = useState<
    Record<string, boolean>
  >({});
  const [lineSplitShow, setLineSplitShow] = useState(false);
  const [loadingCountryShow, setLoadingCountryShow] = useState(false);
  const [ammountShow, setAmmountShow] = useState(false);
  const [enabledFtas, setEnabledFtas] = useState<Record<string, boolean>>({});
  // SPI codes claimed on the SAVED transaction. The Tariff Analyzer bulk upload
  // carries an SPI Code column and the engine derives its FTA set from
  // `inputs.spiCode`; this page has no SPI field of its own and drives FTAs from
  // `enabledFtas` toggles instead. Those two paths have to agree, or entering
  // Live Mode recalculates WITHOUT the preference and saving overwrites a
  // correct duty-free row with a dutiable one.
  //
  // Split on the same delimiters the engine's splitSpiTokens uses (comma, space,
  // semicolon, slash, pipe) — deliberately NOT '+', which is part of codes like
  // 'S+' and 'P+'.
  const claimedSpiCodes = React.useMemo(() => {
    const raw = (dbTariffData as any)?.spi_code;
    return Array.from(
      new Set(
        String(raw || '')
          .split(/[\s,;/|]+/)
          .map((t) => t.trim().toUpperCase())
          .filter(Boolean),
      ),
    );
  }, [dbTariffData]);
  // Claimed SPIs this HTS/origin cannot support. Reported, never applied.
  const [unofferedSpiCodes, setUnofferedSpiCodes] = useState<string[]>([]);
  const [loadingCountry, setLoadingCountry] = useState<string>('');
  const [countryFieldKey, setCountryFieldKey] = useState<string | undefined>(
    undefined,
  );
  const [explanationText, setExplanationText] = useState('');
  const [countryFieldCodes, setCountryFieldCodes] = useState<string[]>([]);
  const [amountInputs, setAmountInputs] = useState<Record<string, number>>({
    kg: 10,
  });
  const [materialPctByKey, setMaterialPctByKey] = useState<
    Record<string, number>
  >({});
  const [adcvdResults, setAdcvdResults] = useState<any>('');
  const [foreignTariffResult, setForeignTariffResult] =
    useState<ForeignTariffState>(EMPTY_FOREIGN_TARIFF_STATE);
  // Answers to the Canada scope questions. Unlike the FTA toggles these are
  // NOT applied to the response — they change which surtax records match, so
  // each answer re-runs the lookup. Cleared whenever a new search starts.
  const [canadaScope, setCanadaScope] = useState<CanadaScopeAnswers>({});
  const [reliefModalOpen, setReliefModalOpen] = useState(false);
  // Declared quota position per ICL item. Unlike the Canada scope answers
  // this needs no refetch: both sides' rates are already in the payload, so
  // the declaration only decides which one is applied.
  const [trqPositions, setTrqPositions] = useState<Record<string, TrqPosition>>(
    {},
  );
  const [trqDetailOpen, setTrqDetailOpen] = useState(false);
  // Which line-level rate the user picked for a varying group, keyed by
  // "<ICL item>|<side>". Needed before that side can be declared, because the
  // group's stored rate describes only one of its lines.
  const [trqVariantChoice, setTrqVariantChoice] = useState<
    Record<string, number>
  >({});
  // Remission orders the user has claimed for this Canada shipment, keyed as
  // in utils/remissions. Unlike scope answers, a claim does not change which
  // measures match, only whether their surtax is owed. Claims are saved with
  // the check.
  const [enabledRemissions, setEnabledRemissions] = useState<
    Record<string, boolean>
  >({});
  // The line and origin those claims belong to. Like the import-ban answers,
  // they carry across re-runs of the same check (entering Live Mode on a saved
  // one) but never onto another product.
  const remissionCheckRef = React.useRef('');
  // Sequence token for the foreign-tariff lookup. Every scope answer starts a
  // fresh request, and answering two questions quickly can land the responses
  // out of order — an older one finishing last would overwrite the result with
  // records matched against superseded answers, while the panel showed the
  // newer answers. Only the newest request is allowed to write state.
  const foreignRequestSeq = React.useRef(0);
  // The same for the US check. A run started while another is in flight (Run
  // Check pressed during the auto-run, or the inputs changing) used to let the
  // older one write its results, toggles and loading flags after the newer one
  // had reset them.
  const usRequestSeq = React.useRef(0);
  // Leaving the page drops any run in flight, so its snackbars don't land on
  // whatever page comes next.
  useEffect(
    () => () => {
      usRequestSeq.current += 1;
      foreignRequestSeq.current += 1;
    },
    [],
  );

  const { cur_user } = useContext(GlobalContext);
  const logAppAction = useLogAppAction();

  const { enqueueSnackbar } = useSnackbar();
  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const [saving, setSaving] = useState(false);
  const [clearingBulkStatus, setClearingBulkStatus] = useState(false);
  const [bulkStatusOverride, setBulkStatusOverride] =
    useState<TariffStatus | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [disableLeaveWarning, setDisableLeaveWarning] = useState(false);
  const [newTicketName, setNewTicketName] = useState<string>('');
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  const [htsDocument, setHtsDocument] = useState<any>({});

  const handleCloseModal = () => setModalOpen(false);
  const allowNavigationRef = React.useRef(false);
  // Track whether we previously had a split, so we can clean up when it turns off
  const hydratingRef = React.useRef(false);

  const [tariffInputs, setTariffInputs] = useState<any>({
    htsCode: primary ? primary : '',
    ...carriedOrigin,
    materialPct: 40,
    amountValue: 10,
    amountUnit: 'kg',
    loadingCountry: '',
    transportMethod: 'Ocean',
  });

  const leaveWarningStorageKey = React.useMemo(() => {
    const userId = cur_user?.basics?.id || 'anon';
    return `tariff-leave-warning-optout-${userId}`;
  }, [cur_user?.basics?.id]);

  const parseTariffLeaveOptOut = React.useCallback((value: any) => {
    const attrArray = Array.isArray(value) ? value : [value];
    return attrArray.some((entry: any) => {
      if (typeof entry === 'string') {
        return entry.toLowerCase() === 'true' || entry === '1';
      }
      return entry === true || entry === 1;
    });
  }, []);

  useEffect(() => {
    const localOptOut = localStorage.getItem(leaveWarningStorageKey) === 'true';
    const userAttrOptOut = parseTariffLeaveOptOut(
      cur_user?.basics?.attributes?.tariff_leave_warning_opt_out,
    );

    setDisableLeaveWarning(localOptOut || userAttrOptOut);
  }, [
    cur_user?.basics?.attributes?.tariff_leave_warning_opt_out,
    leaveWarningStorageKey,
    parseTariffLeaveOptOut,
  ]);

  useEffect(() => {
    let isMounted = true;
    const userId = cur_user?.basics?.id;

    if (!userId) {
      return undefined;
    }

    UsersAPI.getUser(userId)
      .then((resp: any) => {
        if (!isMounted || !resp?.success) {
          return;
        }

        const freshOptOut = parseTariffLeaveOptOut(
          resp?.result?.basics?.attributes?.tariff_leave_warning_opt_out,
        );

        if (freshOptOut) {
          localStorage.setItem(leaveWarningStorageKey, 'true');
          setDisableLeaveWarning(true);
        }
      })
      .catch(() => {
        // no-op; fallback to currently loaded user + local cache
      });

    return () => {
      isMounted = false;
    };
  }, [cur_user?.basics?.id, leaveWarningStorageKey, parseTariffLeaveOptOut]);

  const hasUnsavedTariffWork = React.useMemo(() => {
    return (
      Boolean(tariffInputs.htsCode) ||
      Boolean(tariffInputs.originCountry) ||
      Boolean(tariffInputs.originIso2) ||
      Object.keys(tariffResults?.data || {}).length > 0
    );
  }, [tariffInputs, tariffResults?.data]);

  const shouldWarnOnLeave =
    !forceStatic &&
    !tariffId &&
    !isStaticView &&
    !disableLeaveWarning &&
    hasUnsavedTariffWork;

  useEffect(() => {
    if (!shouldWarnOnLeave || isWhiteLabel) {
      return undefined;
    }

    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      // A dead-session logout redirect (KeycloakService.doLogout with
      // preserveRecoveryDrafts) already snapshotted this page's state and
      // isn't something the user chose -- warning here just doubles up on
      // the "Leave site?" prompt they may have already dismissed moments
      // earlier for their own, real navigation attempt. See
      // sessionRecovery.ts's isForcedNavigationInFlight().
      if (isForcedNavigationInFlight()) {
        return;
      }
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [shouldWarnOnLeave]);

  const navigateToPendingLocation = React.useCallback(() => {
    if (!pendingLocation) {
      return;
    }

    history.push({
      pathname: pendingLocation.pathname,
      search: pendingLocation.search,
      hash: pendingLocation.hash,
      state: pendingLocation.state,
    });
  }, [history, pendingLocation]);

  const handleBlockedNavigation = (nextLocation: any) => {
    if (allowNavigationRef.current) {
      return true;
    }

    if (!shouldWarnOnLeave) {
      return true;
    }

    setPendingLocation(nextLocation);
    setIsLeaveModalOpen(true);
    return false;
  };

  const handleLeaveWithoutSaving = () => {
    setIsLeaveModalOpen(false);
    allowNavigationRef.current = true;
    navigateToPendingLocation();
    window.setTimeout(() => {
      allowNavigationRef.current = false;
    }, 0);
  };

  const handleSaveTicketFromLeaveModal = () => {
    if (!userhasTariffs) {
      return;
    }
    setIsLeaveModalOpen(false);
    setModalOpen(true);
  };

  const handleLeaveAndOptOut = () => {
    const userId = cur_user?.basics?.id;

    if (!userId) {
      localStorage.setItem(leaveWarningStorageKey, 'true');
      setDisableLeaveWarning(true);
      handleLeaveWithoutSaving();
      return;
    }

    UsersAPI.updateTariffLeaveWarningPreference(userId, true)
      .then((resp) => {
        if (resp?.success) {
          localStorage.setItem(leaveWarningStorageKey, 'true');
          setDisableLeaveWarning(true);
        } else {
          enqueueSnackbar('Unable to save your notification preference.', {
            variant: 'error',
          });
        }
      })
      .catch(() => {
        enqueueSnackbar('Unable to save your notification preference.', {
          variant: 'error',
        });
      })
      .finally(() => {
        handleLeaveWithoutSaving();
      });
  };

  const clearPage = () => {
    setTariffResults({
      isLoading: false,
      data: {},
    });
    setTariffInputs({
      htsCode: primary ? primary : '',
      ...carriedOrigin,
      materialPct: 40,
      amountValue: 10,
      amountUnit: 'kg',
      loadingCountry: '',
      transportMethod: 'Ocean',
    });
    setEffectDate(new Date());
    setLoadDate(new Date());
    setTariffCandidates([]);
    setEnabledExclusions({});
    setLineSplitShow(false);
    setAmmountShow(false);
    setEnabledFtas({});
    setCountryFieldKey(undefined);
    setExplanationText('');
    setCountryFieldCodes([]);
    setAmountInputs({
      kg: 10,
    });
    setLoadingCountry('');
    setLoadingCountryShow(false);
    setAmmountShow(false);
    setLineSplitShow(false);
    setAdcvdResults('');
    setHtsDocument({});
    setHideIeepa(false);
    setForeignTariffResult(EMPTY_FOREIGN_TARIFF_STATE);
    setImportBans([]);
    setBanGateAnswers({});
    importBanCheckRef.current = '';
    setEnabledRemissions({});
    remissionCheckRef.current = '';
  };

  // Keep the URL ?country= param in sync with state so reloads and shared
  // links land on the right dataset. Skip when embedded in a saved ticket
  // view, where the route already owns the URL.
  useEffect(() => {
    if (tariffId || forceStatic) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const countryParam = params.get('country');
    const current = parseTariffCountryParam(
      countryParam,
      useInternationalTariffCountries,
    );
    // A ?country= value we refuse to honour has to be REMOVED from the URL, not
    // just ignored. parseTariffCountryParam already resolved it to US, so without
    // this the early-return below would leave e.g. `country=mx` in the address bar
    // while the page shows United States tariffs — a copied or shared link would
    // then misrepresent the dataset it actually opens.
    //
    // Derived from the parse result rather than a hardcoded country list, so it
    // covers every case at once: a withdrawn country (uiEnabled: false), a foreign
    // country while useInternationalTariffCountries is off, and outright garbage.
    const rawCountryParam = (countryParam || '').trim().toUpperCase();
    const hasDisallowedForeignCountry =
      Boolean(rawCountryParam) && rawCountryParam !== current;
    if (current === selectedTariffCountry && !hasDisallowedForeignCountry) {
      return;
    }
    if (selectedTariffCountry === 'US') {
      params.delete('country');
    } else {
      params.set('country', selectedTariffCountry.toLowerCase());
    }
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
      hash: location.hash,
    });
  }, [
    selectedTariffCountry,
    tariffId,
    forceStatic,
    history,
    location.pathname,
    location.search,
    location.hash,
    useInternationalTariffCountries,
  ]);

  // CA/MX tariffs require a Pro license or above AND a non-white-label tenant.
  // White-label tenants are scoped to US-only (MX/CA have whiteLabelAccess:false),
  // so they must be forced back to US even on a deep-linked ?country=ca|mx.
  const hasForeignTariffAccess =
    !isWhiteLabel &&
    (cur_user?.bite_super_admin === true ||
      FOREIGN_TARIFF_TIERS.includes(cur_user?.license));

  // A saved ticket must reopen using the jurisdiction chosen when it was
  // saved. The URL is intentionally not used for static Saved Work views, and
  // historic ticket routes commonly have no `?country=` parameter at all.
  const savedTicketCountry = parseSavedTariffCountry(
    props?.tariffJurisdiction,
  );
  useEffect(() => {
    if (!forceStatic || !savedTicketCountry) {
      return;
    }
    const country =
      savedTicketCountry !== 'US' && !hasForeignTariffAccess
        ? 'US'
        : savedTicketCountry;
    setSelectedTariffCountry((prev) => (prev === country ? prev : country));
  }, [
    forceStatic,
    hasForeignTariffAccess,
    savedTicketCountry,
  ]);

  // Reflect external URL changes (back/forward) back into state.
  //
  // This effect must depend ONLY on the URL (and gating flags), never on
  // selectedTariffCountry. If it re-ran on state changes it would fire right
  // after a tab click while location.search is still the stale previous value,
  // parse the old country, and revert the selection — fighting the state -> URL
  // effect above in an infinite US <-> selected loop. The functional update
  // keeps the comparison against the latest state without needing it as a dep.
  useEffect(() => {
    if (tariffId || forceStatic) {
      return;
    }
    const parsed = parseTariffCountryParam(
      new URLSearchParams(location.search).get('country'),
      useInternationalTariffCountries,
    );
    // Deep-linked ?country=ca|mx is ignored for users without a Pro license.
    const fromUrl = parsed !== 'US' && !hasForeignTariffAccess ? 'US' : parsed;
    setSelectedTariffCountry((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [
    forceStatic,
    location.search,
    tariffId,
    useInternationalTariffCountries,
    hasForeignTariffAccess,
  ]);

  const handleSelectTariffCountry = (next: TariffCountry) => {
    if (!useInternationalTariffCountries && next !== 'US') {
      return;
    }
    // Foreign (CA/MX) tariffs are gated to Pro+.
    if (next !== 'US' && !hasForeignTariffAccess) {
      return;
    }
    if (next === selectedTariffCountry) {
      return;
    }
    clearPage();
    setSelectedTariffCountry(next);
  };

  // handy console helpers
  (globalThis as any).tcDebugOn = (code = '') => {
    localStorage.setItem('tc.debug', '1');
    localStorage.setItem('tc.debug.code', digitsOnly(code));
  };
  (globalThis as any).tcDebugOff = () => {
    localStorage.removeItem('tc.debug');
    localStorage.removeItem('tc.debug.code');
  };

  const requiredTiers = [
    'tariffs',
    'starter',
    'standard',
    'pro',
    'business',
    'enterprise',
    'tariff-only',
  ];

  const normalizedLicense = (() => {
    switch (cur_user?.license) {
      case 'none':
      case 'standard':
      case 'pro':
      case 'business':
      case 'enterprise':
        return cur_user.license as LicenseType;
      case 'starter':
      case 'tariffs':
      case 'tariff-only':
        return 'standard';
      default:
        return 'none';
    }
  })();

  /* -------------------- License -------------------- */
  const checkLicense = () => {
    setUserHasTariffs(
      isWhiteLabel ||
        cur_user?.bite_super_admin === true ||
        requiredTiers.includes(cur_user?.license),
    );
  };

  useEffect(() => {
    checkLicense();
  }, [cur_user?.license]);

  const saveTariffResultMutation = useSaveTariffResult();

  const getSavedSnapshot = React.useCallback(() => {
    if (forceStatic) {
      return props?.savedTariffResult || {};
    }

    const dataAny = dbTariffData as any;
    if (!dataAny) {
      return null;
    }

    const latestResultContainer =
      dataAny.latest_tariff_result ||
      dataAny.latestTariffResult ||
      dataAny.tariffResult ||
      dataAny;

    if (!latestResultContainer) {
      return null;
    }

    if (latestResultContainer.inputs || latestResultContainer.results) {
      return latestResultContainer;
    }

    return (
      latestResultContainer.tariff_result ||
      latestResultContainer.tariffResult ||
      null
    );
  }, [dbTariffData, forceStatic, props?.savedTariffResult]);

  const savedBulkStatus = React.useMemo(() => {
    const snapshot = (getSavedSnapshot() as any) || {};
    const status = snapshot?.status;
    if (!status) {
      return null;
    }

    const color =
      status?.color === 'Green' ||
      status?.color === 'Yellow' ||
      status?.color === 'Red'
        ? status.color
        : null;

    if (!color) {
      return null;
    }

    const message = String(status?.message || '');

    // If the saved status was Yellow "FTA Eligible" but the user has since
    // accepted an FTA toggle, reflect that in the banner instead
    // of forcing the user to manually clear the warning.
    if (color === 'Yellow' && message === 'FTA Eligible') {
      const savedEnabledFtas = snapshot?.results?.enabledFtas;
      const hasAcceptedFta =
        savedEnabledFtas &&
        typeof savedEnabledFtas === 'object' &&
        Object.values(savedEnabledFtas).some(Boolean);
      if (hasAcceptedFta) {
        return {
          color: 'Green',
          message: 'FTA Accepted',
        } as TariffStatus;
      }
    }

    // Suppress smelt/material default warnings for HTS codes where neither
    // the smelt-country nor line-split UI was ever shown (e.g. animal fats).
    if (
      color === 'Yellow' &&
      (message === 'Smelt Provided, Material Default' ||
        message === 'Material Percentage Default')
    ) {
      const countryUiShown = Boolean(snapshot?.countryUi?.show);
      const hasLineSplit =
        Array.isArray(snapshot?.results?.lineSplitFields) &&
        snapshot.results.lineSplitFields.length > 0;
      if (!countryUiShown && !hasLineSplit) {
        return {
          color: 'Green',
          message: 'Info Provided',
        } as TariffStatus;
      }
    }

    return {
      color: color,
      message: message,
    } as TariffStatus;
  }, [getSavedSnapshot]);

  const effectiveBulkStatus = bulkStatusOverride || savedBulkStatus;
  const showBulkStatusBanner =
    isStaticView &&
    Boolean(tariffId || forceStatic) &&
    Boolean(effectiveBulkStatus);

  const handleClearBulkStatus = async () => {
    if (!tariffId || effectiveBulkStatus?.color !== 'Yellow') {
      return;
    }

    const snapshot = (getSavedSnapshot() as any) || {};
    const clearedStatus: TariffStatus = {
      color: 'Green',
      message: 'Marked as reviewed manually',
    };

    try {
      setClearingBulkStatus(true);

      await saveTariffResultMutation.mutateAsync({
        id: tariffId,
        data: {
          hts_code: (dbTariffData as any)?.hts_code || tariffInputs.htsCode,
          country_of_origin:
            (dbTariffData as any)?.country_of_origin ||
            tariffInputs.originIso2 ||
            tariffInputs.originCountry,
          product_value: (dbTariffData as any)?.product_value || productValue,
          latest_tariff_result: {
            ...snapshot,
            status: clearedStatus,
          },
        },
      });

      setBulkStatusOverride(clearedStatus);
      enqueueSnackbar('Warning cleared successfully', { variant: 'success' });
      if (refetchDbData) {
        refetchDbData();
      }
    } catch (e: any) {
      enqueueSnackbar(
        `Failed to clear warning: ${e.message || 'Unknown error'}`,
        { variant: 'error' },
      );
    } finally {
      setClearingBulkStatus(false);
    }
  };

  // --- INJECTED: Hydrate State from DB ---
  useEffect(() => {
    const normalizeBoolMap = (val: any): Record<string, boolean> => {
      if (!val) {
        return {};
      }
      if (
        typeof val?.get === 'function' &&
        typeof val?.entries === 'function'
      ) {
        const out: Record<string, boolean> = {};
        for (const [k, v] of val.entries()) {
          out[k] = Boolean(v);
        }
        return out;
      }
      if (Array.isArray(val)) {
        try {
          return Object.fromEntries(val);
        } catch {
          return {};
        }
      }
      if (typeof val === 'object') {
        return val as Record<string, boolean>;
      }
      return {};
    };

    const savedTR = getSavedSnapshot() as any;

    if (!savedTR || !isStaticView) {
      return;
    }

    const dataAny = (dbTariffData as any) || {};

    // 3. Extract sections
    const inputs = savedTR.inputs || {};
    const results = savedTR.results || {};
    const base = savedTR.base || {};
    const math = savedTR.math || {};

    // 4. Hydrate Inputs
    const materialPct = hydrateMaterialPct(
      inputs.materialPct,
      dataAny.material_pct,
    );

    setTariffInputs({
      htsCode: inputs.htsCode || dataAny.hts_code || '',
      originCountry: inputs.originCountry || dataAny.country_of_origin || '',
      originIso2: inputs.originIso2 || '',
      materialPct: materialPct,
      amountValue: inputs.amountValue ?? 10,
      amountUnit: inputs.amountUnit ?? 'kg',
      loadingCountry: inputs.loadingCountry || '',
      transportMethod:
        inputs.transportMethod || dataAny.trans_method || 'Ocean',
    });

    setProductValue(inputs.productValue ?? dataAny.product_value ?? 1000);

    // Saved Work uses effectDate/loadDate, while some bulk shapes may have entryDate/loadingDate
    const savedEntryDate = inputs.effectDate || inputs.entryDate;
    if (savedEntryDate) {
      setEffectDate(new Date(savedEntryDate));
    }

    const savedLoadDate = inputs.loadDate || inputs.loadingDate;
    if (savedLoadDate) {
      setLoadDate(new Date(savedLoadDate));
    }

    // 5. Hydrate Maps & Text
    const enabledMap = normalizeBoolMap(results.enabledExclusions);
    const enabledFtasMap = normalizeBoolMap(results.enabledFtas);
    setEnabledExclusions(enabledMap);
    setEnabledFtas(enabledFtasMap);

    const savedCheckKey = `${digitsOnly(
      inputs.htsCode || dataAny.hts_code || '',
    )}|${originKey(
      inputs.originIso2 || '',
      inputs.originCountry || dataAny.country_of_origin || '',
    )}`;

    // The import bans matched when this check was saved, and the answers
    // given to their gates. Keyed to the saved line, so entering Live Mode
    // keeps the answers.
    setImportBans(Array.isArray(results.importBans) ? results.importBans : []);
    setBanGateAnswers(
      results.banGateAnswers && typeof results.banGateAnswers === 'object'
        ? results.banGateAnswers
        : {},
    );
    importBanCheckRef.current = savedCheckKey;

    // Remission orders claimed on a Canada check, kept for Live Mode the same
    // way.
    setEnabledRemissions(normalizeBoolMap(results.enabledRemissions));
    remissionCheckRef.current = savedCheckKey;

    // ---- Metals / Smelt-Pour fallback chain ---------------------------
    // Bulk analyzer rows store per-metal $ values + their associated
    // country directly on the Tariff row (aluminum_value/smelt_country,
    // steel_value/pour_country, copper_value). When the saved snapshot
    // doesn't already carry materialPctByKey (older rows or single-tool
    // saves), reconstruct it from those columns so the metal sliders
    // hydrate to the same percentage that drove the calculation.
    const pvForPct = Number(inputs.productValue ?? dataAny.product_value ?? 0);
    const aluminumValue = Number(dataAny.aluminum_value || 0);
    const steelValue = Number(dataAny.steel_value || 0);
    const copperValue = Number(dataAny.copper_value || 0);
    const derivedMetalPctMap: Record<string, number> = {};
    if (pvForPct > 0) {
      if (aluminumValue > 0) {
        derivedMetalPctMap.ALUMINUMPERCENT = (aluminumValue / pvForPct) * 100;
      }
      if (steelValue > 0) {
        derivedMetalPctMap.STEELPERCENT = (steelValue / pvForPct) * 100;
      }
      if (copperValue > 0) {
        derivedMetalPctMap.COPPER = (copperValue / pvForPct) * 100;
      }
    }

    const savedPctByKey =
      inputs.materialPctByKey && Object.keys(inputs.materialPctByKey).length > 0
        ? inputs.materialPctByKey
        : null;
    const hydratedPctByKey =
      savedPctByKey ||
      (Object.keys(derivedMetalPctMap).length > 0 ? derivedMetalPctMap : {});
    setMaterialPctByKey(hydratedPctByKey);

    // For the single loadingCountry slot, prefer (in order): saved snapshot,
    // pour country when steel is the dominant metal, smelt country
    // otherwise, then the legacy combined column. Mirrors how the
    // bulk stream reader picks countryOfSmeltPour.
    const steelDominant =
      steelValue > aluminumValue && steelValue > copperValue;
    const fallbackCountry =
      (steelDominant
        ? dataAny.pour_country || dataAny.smelt_country
        : dataAny.smelt_country || dataAny.pour_country) ||
      dataAny.country_of_smelt_pour ||
      '';
    setLoadingCountry(inputs.loadingCountry || fallbackCountry || '');
    setExplanationText(savedTR?.explanation?.text || '');
    setAdcvdResults(savedTR?.adcvdResults || results?.adcvdResults || '');
    // Quantity map for the cost panel, keyed by canonical unit.
    //
    // inputs.amountInputs is NOT a safe source on its own: the bulk pipelines
    // reuse that same map to carry metal-content SHARE percentages
    // (STEELPERCENT, ALUMINUMPERCENT, COPPER, USCONTENT), so hydrating it
    // wholesale dropped "84.96" into the kg box on any Tariff Analyzer row
    // with a metal split. Split-share keys are upper-case field names and
    // unit keys are lower-case canonical units, so the case tells them apart.
    //
    // The reported pair (amountValue + amountUnit) wins: for a Tariff
    // Analyzer row that is the upload's Quantity / Unit of Measure, which is
    // what actually priced the specific portion of the duty.
    const hydratedAmounts: Record<string, number> = {};
    Object.keys(inputs.amountInputs || {}).forEach((key) => {
      const value = Number((inputs.amountInputs || {})[key]);
      if (key === key.toLowerCase() && Number.isFinite(value) && value > 0) {
        hydratedAmounts[key] = value;
      }
    });
    const reportedQty = Number(inputs.amountValue);
    const reportedUnit = normalizeUnit(String(inputs.amountUnit || ''));
    if (reportedUnit && Number.isFinite(reportedQty) && reportedQty > 0) {
      hydratedAmounts[reportedUnit] = reportedQty;
    }
    setAmountInputs(
      Object.keys(hydratedAmounts).length > 0
        ? hydratedAmounts
        : { kg: Number(inputs.amountValue) || 10 },
    );
    setHideIeepa(inputs.hideIeepa || false);

    // --- NEW: Hydrate Insights ---
    if (savedTR.insights) {
      setHtsHistory(savedTR.insights.history || []);
      setRiskFlags(savedTR.insights.riskFlags || []);
    }

    // 6. SANITIZE LINES (The "Phantom" Fix)
    // We filter the lines *before* setting them into tariffResults.
    const rawLines = (results.lines || []) as Line[];
    const optionalPool = (results.optionalExclusions || []) as Candidate[];

    const sanitizedLines = rawLines.map((line) => ({
      ...line,
      rows: line.rows.filter((row) => {
        const digits = row._codeDigits || row.code.replace(/\D/g, '');

        // Is this row an optional exclusion?
        const isOptional = optionalPool.some(
          (opt) => opt.codeDigits === digits,
        );

        // If optional and NOT enabled, drop it.
        if (isOptional && !enabledMap[digits]) {
          return false;
        }
        return true;
      }),
    }));

    // 7. Set Tariff Results (Single Source of Truth)
    if (results.summaryRows || base.baseDutyRate) {
      setTariffResults({
        isLoading: false,
        data: {
          baseDutyRate: base.baseDutyRate,
          hasIeepaTariffs: results.hasIeepaTariffs || false,
          baseDutyRateRaw: base.baseDutyRate,
          results: results.summaryRows || [],
          lines: sanitizedLines,
          lineSplitFields: results.lineSplitFields || [],
          optionalExclusions:
            results.optionalExclusions || savedTR.optionalExclusions || [],
          ftaOptions: results.ftaOptions || [],
          additionalDutyRate: math.additionalDutyRateDisplay || '0%',
          requiredAmountUnits:
            results.requiredAmountUnits || savedTR.requiredAmountUnits || [],
          baseAmount: math.baseAmountDollars || 0,
          lineSplitLabel: base.lineSplitLabel || results.lineSplitLabel || '',
          productDesc: base.productDesc || dataAny.description || '',
          ...math,
          // Duty, fees and the total are read from the engine's math block,
          // and a saved view never calls the engine: the snapshot's own math
          // is that block, as it was when the check was saved.
          engineMath: math,
        },
      });

      // Set UI Flags
      setLineSplitShow(Boolean(results.hasSplit));
      setLoadingCountryShow(savedTR?.countryUi?.show || false);
      setCountryFieldKey(savedTR?.countryUi?.key);
      setCountryFieldCodes(savedTR?.countryUi?.codes || []);
      setAmmountShow((results.requiredAmountUnits?.length || 0) > 0);
    }
  }, [getSavedSnapshot, dbTariffData, isStaticView]);

  useEffect(() => {
    setBulkStatusOverride(null);
  }, [savedBulkStatus?.color, savedBulkStatus?.message, tariffId]);

  useEffect(() => {
    if (forceStatic) {
      setIsEditMode(false);
    }
  }, [forceStatic]);

  // --- STATUS HELPER CONSTANTS ---
  const COMMON_EXEMPTIONS = new Set([
    '99030103',
    '99030105',
    '99030112',
    '99030122',
    '99030129',
    '99030131',
    '99030180',
    '99030182',
    '99030189',
    '99030190',
    '99030275',
    '99030276',
    '99030277',
    '99030278',
    '99030281',
    '99030285',
    '99030286',
    '99030290',
    '99030291',
    '99037405',
    '99037407',
    '99037409',
    '99037411',
    '99037604',
    '99038869',
    '99038870',
    '99039109',
    '99039110',
    '99039115',
    '99039116',
    '99039280',
    '99039402',
    '99039404',
    '99039406',
    '99039601',
    '99039602',
  ]);

  const determineStatus = (
    inputs: any,
    summaryRows: ResultRow[],
    optionalExclusions: Candidate[],
    htsDocument: any,
    enabledFtasMap: Record<string, boolean> = {},
  ): TariffStatus => {
    const d10 = (s: string) => String(s).replace(/\D/g, '');

    // --- RED CHECKS ---
    if (!inputs.originCountry || inputs.originCountry.length !== 2) {
      return { color: 'Red', message: 'Invalid Country of Origin' };
    }

    const htsClean = d10(inputs.htsCode);
    // Check strict 10-digit length OR if the base document was not found
    if (htsClean.length !== 10 || !htsDocument) {
      return { color: 'Red', message: 'Invalid HTS Code' };
    }

    const loadMs = new Date(inputs.loadingDate).getTime();
    const entryMs = new Date(inputs.entryDate).getTime();
    if (loadMs > entryMs) {
      return { color: 'Red', message: 'Loading Date after Entry Date' };
    }

    // Rate Unit Check — detect every form the duty math would actually price
    // ("0.8¢ each", "$0.45/pr", "32¢/kg", etc.). Delegates to the full
    // parser declared later in the component so the warning catches the
    // same components the calc would actually try to price; the previous
    // narrow regex missed bare-space units like "each".
    const hasUnitRate = summaryRows.some(
      (r) => parseAmountRateComponents(r.rate).length > 0,
    );

    const weightInputProvided =
      (inputs.materialWeight || 0) > 0 ||
      (inputs.amountValue || 0) > 0 ||
      Object.values(inputs.amountInputs || {}).some((v: any) => v > 0);

    if (hasUnitRate && !weightInputProvided) {
      return { color: 'Red', message: 'Specific Rate Missing Unit Quantity' };
    }

    // --- YELLOW CHECKS ---
    const availableExemptions: Candidate[] = [];
    const availableFtas: Candidate[] = [];

    for (const c of optionalExclusions) {
      if (COMMON_EXEMPTIONS.has(d10(c.codeDigits))) {
        availableExemptions.push(c);
      } else {
        availableFtas.push(c);
      }
    }

    const hasAcceptedFta = Object.values(enabledFtasMap).some(Boolean);

    if (
      availableFtas.length > 0 &&
      !hasAcceptedFta &&
      (inputs.useFTA === undefined || inputs.useFTA === null)
    ) {
      return { color: 'Yellow', message: 'FTA Eligible' };
    }

    if (availableExemptions.length > 0) {
      return { color: 'Yellow', message: 'Exemption Available' };
    }

    // Smelt/material warnings only apply when those UI inputs are actually
    // shown for this HTS (e.g. metals). Skip otherwise — materialPct defaults
    // to 40 even for HTS codes that have no material/smelt concept.
    const smeltOrMaterialShown = Boolean(loadingCountryShow || lineSplitShow);
    const isMaterialDefault = inputs.materialPct === 40;
    if (
      smeltOrMaterialShown &&
      inputs.countryOfSmeltPour &&
      isMaterialDefault
    ) {
      return { color: 'Yellow', message: 'Smelt Provided, Material Default' };
    }

    if (smeltOrMaterialShown && isMaterialDefault) {
      return { color: 'Yellow', message: 'Material Percentage Default' };
    }

    return { color: 'Green', message: 'Info Provided' };
  };

  // Builds the same saved-ticket snapshot shape ({inputs, base, results,
  // math, ...}) that `latest_tariff_result` persists, so the PDF export
  // (screeningTicketTariffPDF, which reads this exact shape) always matches
  // what a save would record — without duplicating the computation.
  const buildTariffSnapshot = () => {
    // 1. Get current data state (Source of Truth)
    const trData = tariffResults?.data || {};
    const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));
    // A barred check saves the ban alone: with no entry there is no duty
    // or fee to total.
    const banRow = activeImportBan
      ? importBanResultRow(activeImportBan)
      : null;

    // 2. Grab the lines directly.
    // If we are in Live Mode, recomputeFromState updated this.
    // If we are in View Mode, useEffect sanitized this.
    const linesForSave = banRow ? [] : clone(trData.lines || []);
    const currentSummaryRows = banRow ? [banRow] : clone(trData.results || []);

    // 3. Totals — the figures on screen: the engine's for a US line, the
    //    foreign calculator's for MX/CA, which owes no US fees.
    const baseRateDisplay = trData.baseDutyRate || '';
    const figures = engineFigures(banRow ? undefined : trData.engineMath);
    const additional = foreignCostOverrides
      ? Number(foreignCostOverrides.additionalCost) || 0
      : figures.additional;
    const baseAmount = foreignCostOverrides ? 0 : figures.baseAmount;
    const dutyCost = foreignCostOverrides
      ? Number(foreignCostOverrides.dutyCost) || 0
      : figures.dutyCost;
    const mpfSnap = foreignCostOverrides ? 0 : figures.mpf;
    const hmfSnap = foreignCostOverrides ? 0 : figures.hmf;
    const totalCost = foreignCostOverrides
      ? Number(foreignCostOverrides.totalCost) || 0
      : figures.totalCost;

    const additionalRateRaw = productValue
      ? (additional / productValue) * 100
      : 0;
    const additionalRateDisplay = `${additionalRateRaw.toFixed(2)}%`;

    // 4. Build Input Snapshot
    const inputsSnapshot: Record<string, any> = {
      htsCode: tariffInputs.htsCode,
      originCountry: tariffInputs.originCountry,
      originIso2: tariffInputs.originIso2,
      productValue: productValue,
      effectDate: new Date(effectDate).toISOString(), // Ensure standard naming
      entryDate: new Date(effectDate).toISOString(), // Add entryDate specifically for helper
      loadingDate: new Date(loadDate).toISOString(),
      transportMethod: String(tariffInputs.transportMethod || 'Ocean'),
      materialPct: tariffInputs.materialPct,
      countryOfSmeltPour: loadingCountry,
      amountValue: tariffInputs.amountValue,
      amountInputs: amountInputs,
      // Ensure useFTA is tracked if you have it in state, otherwise undefined triggers the Yellow check
      useFTA: undefined,
      hideIeepa: hideIeepa,
    };

    const inputsForStatus = {
      ...inputsSnapshot,
      originCountry: tariffInputs.originIso2 || tariffInputs.originCountry,
    };

    // --- NEW: RE-EVALUATE STATUS ON SAVE ---
    const status: TariffStatus = banRow
      ? { color: 'Red', message: 'Import Barred' }
      : determineStatus(
          inputsForStatus,
          currentSummaryRows,
          trData.optionalExclusions || [],
          htsDocument, // Pass the HTS Document from state
          enabledFtas,
        );
    // ---------------------------------------

    const appliedExclusionsList = Object.entries(enabledExclusions)
      .filter(([_, isEnabled]) => isEnabled)
      .map(([code]) => code);

    return {
      inputs: inputsSnapshot,
      status: status,
      base: {
        baseDutyRate: baseRateDisplay,
        productDesc: trData.productDesc || '',
        lineSplitLabel: trData.lineSplitLabel || '',
      },
      results: {
        hasSplit: linesForSave.length > 1,
        summaryRows: currentSummaryRows,
        lines: linesForSave,

        optionalExclusions: clone(trData.optionalExclusions || []),
        enabledExclusions: { ...enabledExclusions },
        appliedExclusions: appliedExclusionsList,
        ftaOptions: clone(trData.ftaOptions || []),
        enabledFtas: { ...enabledFtas },
        enabledRemissions: { ...enabledRemissions },
        lineSplitFields: clone(trData.lineSplitFields || []),
        importBans: clone(importBans),
        banGateAnswers: { ...banGateAnswers },
        activeImportBan: activeImportBan ? clone(activeImportBan) : null,
      },
      countryUi: {
        show: Boolean(loadingCountryShow),
        key: countryFieldKey || '',
        codes: Array.isArray(countryFieldCodes) ? [...countryFieldCodes] : [],
      },
      math: {
        additionalDutyRate: additionalRateRaw,
        additionalDutyRateDisplay: additionalRateDisplay,
        additionalDutyDollars: additional,
        baseAmountDollars: baseAmount,
        dutyCost: dutyCost,
        mpf: mpfSnap,
        hmf: hmfSnap,
        totalCost: totalCost,
        baseDutyRate: baseRateDisplay,
        hmfPmf: mpfSnap + hmfSnap,
      },
      explanation: {
        text: String(explanationText || ''),
        generatedAt: new Date().toISOString(),
      },
      insights: {
        history: htsHistory,
        riskFlags: riskFlags,
      },
      savedAt: new Date().toISOString(),
    };
  };

  const handleSaveChanges = async () => {
    if (!tariffId) {
      return;
    }
    setSaving(true);

    try {
      const snapshot = buildTariffSnapshot();

      const payload = {
        // 1. Update Root Columns (Fixes the Bulk List View)
        hts_code: snapshot.inputs.htsCode,
        country_of_origin:
          snapshot.inputs.originIso2 || snapshot.inputs.originCountry,
        product_value: productValue,

        // 2. Update the Calculation Blob (Keeps Live Mode correct)
        latest_tariff_result: snapshot,
      };

      await saveTariffResultMutation.mutateAsync({
        id: tariffId,
        data: payload, // CHANGED: Send 'payload' instead of 'snapshot'
      });

      enqueueSnackbar('Changes saved successfully', { variant: 'success' });
      setIsEditMode(false);
      if (refetchDbData) {
        refetchDbData();
      }
    } catch (e: any) {
      console.error(e);
      enqueueSnackbar(
        `Failed to save changes: ${e.message || 'Unknown error'}`,
        {
          variant: 'error',
        },
      );
    } finally {
      setSaving(false);
    }
  };

  /* -------------------- Help content -------------------- */
  const helpContent = (
    <p className='module-header-tooltip-text'>
      Check for tariffs that may apply to your products and receive the total
      calculation that you will need to account for in a transaction.
    </p>
  );

  // ---- helpers ----
  const safeNum = (n: any) => (Number.isFinite(Number(n)) ? Number(n) : 0);

  const normalizeLinesForSave = (
    uiLines: Line[] | undefined,
    fallbackRows: ResultRow[],
    value: number,
  ): Array<Line & { order: number }> => {
    if (!Array.isArray(uiLines) || uiLines.length === 0) {
      return [
        {
          id: 'base',
          label: 'Line 1',
          value: value,
          rows: JSON.parse(JSON.stringify(fallbackRows)),
          order: 1, // ✅ now allowed
        },
      ];
    }

    return uiLines.map((l, idx) => ({
      ...l,
      value: safeNum(l.value),
      rows: JSON.parse(JSON.stringify(l.rows)),
      order: typeof l.order === 'number' ? l.order : idx + 1,
    }));
  };

  const repairSplitLineValues = (lines: Line[], value: number): Line[] => {
    if (!Array.isArray(lines) || lines.length < 2) {
      return lines;
    }

    // Prefer the USCONTENT key (capped) when present so USMCA derivative
    // steel snapshots hydrate to the same split the live calc produced.
    const pctEntries = Object.entries(materialPctByKey || {}).filter(([, n]) =>
      Number.isFinite(Number(n)),
    );
    const usContentEntry = pctEntries.find(([k]) => k === USMCA_USCONTENT_KEY);
    const [sourceKey, sourcePct] = usContentEntry ||
      pctEntries[0] || ['', Number(tariffInputs.materialPct || 0)];
    const pct = cappedSplitPct(String(sourceKey), sourcePct);

    if (!(pct > 0 && pct < 100)) {
      return lines;
    }

    const total = lines.reduce((sum, line) => sum + safeNum(line.value), 0);
    const line2Current = safeNum(lines[1]?.value);
    const expectedTotal = safeNum(value);

    const needsRepair =
      expectedTotal > 0 &&
      (line2Current <= 0 || Math.abs(total - expectedTotal) > 0.01);

    if (!needsRepair) {
      return lines;
    }

    const line2Value = Math.max(0, (expectedTotal * pct) / 100);
    const line1Value = Math.max(0, expectedTotal - line2Value);

    return lines.map((line, idx) => ({
      ...line,
      value:
        idx === 0 ? line1Value : idx === 1 ? line2Value : safeNum(line.value),
    }));
  };

  /* -------------------- Ticket creation -------------------- */
  const createTicket = async (name: string) => {
    if (!userhasTariffs) {
      return;
    }
    logAppAction('tariff:save');
    setSaving(true);

    // ---- helpers ----
    const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));

    try {
      // What the user is currently seeing
      const trData = tariffResults?.data || {};
      // A barred check saves the ban alone: with no entry there is no duty
      // or fee to total.
      const banRow = activeImportBan
        ? importBanResultRow(activeImportBan)
        : null;
      const summaryRows: ResultRow[] = banRow
        ? [banRow]
        : clone((trData.results ?? []) as ResultRow[]);
      const uiLines: Line[] =
        !banRow && Array.isArray(trData.lines) ? (trData.lines as Line[]) : [];
      const baseRateDisplay = trData?.baseDutyRate ?? '';

      // Persist the lines exactly as shown (with stable order),
      // or synthesize a single base line when there are none.
      const normalizedLines = normalizeLinesForSave(
        uiLines,
        summaryRows,
        productValue,
      );
      const linesForSave: Line[] = banRow
        ? []
        : repairSplitLineValues(normalizedLines, productValue);

      const optionalExclusionsForSave =
        Array.isArray(trData?.optionalExclusions) &&
        trData.optionalExclusions.length > 0
          ? trData.optionalExclusions
          : tariffCandidates;

      // Duty math must be the exact figures on screen. US results come from
      // the tariff engine; Canada/Mexico results use the foreign-rate
      // calculator, which includes foreign-only surtaxes and has no US fees.
      // Saving the engine math for a foreign result stores an empty object and
      // makes a correctly priced check reopen with $0 values.
      const engineMath = banRow ? {} : tariffResults?.data?.engineMath || {};
      const savedForeignMath =
        selectedTariffCountry !== 'US' && foreignCostOverrides
          ? foreignCostOverrides
          : null;
      const additional = savedForeignMath
        ? Number(savedForeignMath.additionalCost) || 0
        : Number(engineMath.additionalDutyDollars) || 0;
      const baseAmount = savedForeignMath
        ? Number(savedForeignMath.baseCost) || 0
        : Number(engineMath.baseAmountDollars) || 0;
      const dutyCost = savedForeignMath
        ? Number(savedForeignMath.dutyCost) || 0
        : Number(engineMath.dutyCost) || 0;
      const transportMethodSnap = String(
        tariffInputs.transportMethod || 'Ocean',
      );

      const foreignTotalCost = Number(savedForeignMath?.totalCost);
      const { mpf: engineMpf, hmf: engineHmf, totalCost: engineTotalCost } =
        engineFigures(engineMath);
      const mpfSnap = savedForeignMath ? 0 : engineMpf;
      const hmfSnap = savedForeignMath ? 0 : engineHmf;
      const totalCost = savedForeignMath
        ? Number.isFinite(foreignTotalCost)
          ? foreignTotalCost
          : Number(productValue || 0) + dutyCost
        : engineTotalCost;

      const additionalRateRaw = productValue
        ? (additional / productValue) * 100
        : 0;
      const additionalRateDisplay = `${additionalRateRaw
        .toFixed(2)
        .replace(/\.00$/, '')
        .replace(/(\.\d)0$/, '$1')}%`;

      const needsAmount = (trData?.requiredAmountUnits?.length ?? 0) > 0;
      const needsLoadingCountry = Boolean(loadingCountryShow);

      const inputs: Record<string, any> = {
        htsCode: tariffInputs.htsCode,
        originCountry: tariffInputs.originCountry,
        // What the engine was sent (buildEngineInputs), so a scheduled
        // recheck and Live Mode price the same origin.
        originIso2: tariffInputs.originIso2 || undefined,
        productValue: productValue,
        effectDate: new Date(effectDate).toISOString(),
        loadDate: new Date(loadDate).toISOString(),
        dateStrategy: dateStrategy,
        transportMethod: transportMethodSnap, // NEW
        ...(Array.isArray(trData?.lineSplitFields) &&
          trData.lineSplitFields.length > 0 && {
            materialPctByKey: { ...materialPctByKey },
          }),
        ...(needsAmount && {
          amountValue: tariffInputs.amountValue,
          amountUnit: tariffInputs.amountUnit,
          amountInputs: amountInputs,
        }),
        hideIeepa: hideIeepa,
        ...(needsLoadingCountry && { loadingCountry: loadingCountry }),
      };

      Object.keys(inputs).forEach((k) =>
        inputs[k] === undefined ? delete inputs[k] : null,
      );

      // Snapshot payload saved into the ticket
      const tariffSnapshot = {
        // Surfaces the ban on the saved ticket's status banner.
        ...(banRow && { status: { color: 'Red', message: 'Import Barred' } }),
        inputs: {
          ...inputs,
          // keep legacy single slider for back-compat (optional)
          materialPct: tariffInputs.materialPct,
        },
        base: {
          baseDutyRate: baseRateDisplay,
          productDesc: trData?.productDesc ?? '',
          lineSplitLabel:
            trData?.lineSplitLabel ??
            (Array.isArray(trData?.lineSplitFields) &&
            trData.lineSplitFields.length > 0
              ? 'Material Percentage'
              : ''),
        },
        results: {
          hasSplit: Array.isArray(uiLines) && uiLines.length > 1,
          hasIeepaTariffs: trData?.hasIeepaTariffs || false,
          summaryRows: summaryRows, // Duty Summary rows (deduped list)
          lines: linesForSave, // 👈 unlimited lines, IDs preserved, ordered
          optionalExclusions: clone(optionalExclusionsForSave ?? []),
          enabledExclusions: { ...enabledExclusions },
          ftaOptions: clone(trData?.ftaOptions ?? []),
          enabledFtas: { ...enabledFtas },
          enabledRemissions: { ...enabledRemissions },
          lineSplitFields: clone(trData?.lineSplitFields ?? []),
          requiredAmountUnits: clone(trData?.requiredAmountUnits ?? []),
          adcvdResults: adcvdResults || '',
          importBans: clone(importBans),
          banGateAnswers: { ...banGateAnswers },
          activeImportBan: activeImportBan ? clone(activeImportBan) : null,
        },
        countryUi: {
          show: Boolean(loadingCountryShow),
          key: countryFieldKey || '',
          codes: Array.isArray(countryFieldCodes) ? [...countryFieldCodes] : [],
        },
        math: {
          additionalDutyRate: additionalRateRaw,
          additionalDutyRateDisplay: additionalRateDisplay,
          additionalDutyDollars: additional,
          baseAmountDollars: baseAmount,
          dutyCost: dutyCost,
          mpf: mpfSnap,
          hmf: hmfSnap,
          transportMethod: transportMethodSnap,
          totalCost: totalCost,
        },

        explanation: {
          text: String(explanationText || ''),
          generatedAt: new Date().toISOString(),
        },
        insights: {
          history: htsHistory,
          riskFlags: riskFlags,
        },
        adcvdResults: adcvdResults || '',
        savedAt: new Date().toISOString(),
      };

      // ----- existing ticket payload scaffold (unchanged shape) -----
      const comFilterQuery = {
        lenses: [],
        countries: [],
        agencies: [],
        lists: [],
        hsCodeLists: [],
        sections: [],
        chapters: [],
        codes: [],
        entRes: false,
        filterDate: [],
      };
      const entFilterQuery = {
        lenses: [],
        countries: [],
        agencies: [],
        lists: [],
        entRes: false,
        filterDate: [],
      };
      const ownerQuery = {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      };

      const query: any = {
        ticketName: name,
        lastUpdate: formatted_dt,
        commodity: {
          harmonizedScheduleCode: tariffInputs.htsCode,
          commodityName: '',
          countryOfOrigin: tariffInputs.originCountry,
          destinationCountry: '',
        },
        docType: 'Tariff',
        // Keep the legacy Tariff docType for existing ticket renderers while
        // persisting the market that produced this calculation for Saved Work.
        tariffJurisdiction: selectedTariffCountry,
        commodityFilter: comFilterQuery,
        entityFilter: entFilterQuery,
        frequency: frequency || 'Once',
        sharedWith: shareEmail,
        activeFilter: '',
        selectedOpts: [],
        status: 'Complete',
        favorite: false,
        groupName: cur_user.selected_team.name,
        groupId: cur_user.selected_team.id,
        template: [],
        colMapping: [],
        activity: {
          user: {
            kc_id: cur_user.basics?.id,
            email: cur_user.basics?.email,
            firstName: cur_user.basics?.firstName,
            lastName: cur_user.basics?.lastName,
          },
          action: 'saved work',
          timeStamp: Date.now(),
        },
        owner: ownerQuery,

        // 👇 store the full, static snapshot here
        tariffResults: tariffSnapshot,
      };

      const resp = await CasesAPI.createCase({ newCase: query });
      if (resp?.success) {
        shareEmail.forEach((email) => {
          handleShareTicketNotification(name, cur_user, email, resp.result._id);
        });
        setSaving(false);
        handleCloseModal();
        enqueueSnackbar('Successfully created new case', {
          variant: 'success',
        });

        if (pendingLocation) {
          allowNavigationRef.current = true;
          navigateToPendingLocation();
          window.setTimeout(() => {
            allowNavigationRef.current = false;
          }, 0);
        }
      } else if (resp) {
        enqueueSnackbar(
          'Error saving new case. Uploaded file(s) may be too large.',
          {
            variant: 'error',
          },
        );
        setSaving(false);
      }
    } catch (err) {
      console.error('[createTicket] failed:', err);
      enqueueSnackbar('Error saving new case.', { variant: 'error' });
      setSaving(false);
    }
  };

  /* -------------------- Helpers -------------------- */
  const resetTransactionState = () => {
    setEnabledExclusions({});
    setEnabledFtas({});
    setMaterialPctByKey({});
    setTariffCandidates([]);
    setLoadingCountryShow(false);
    setLoadingCountry('');
    setCountryFieldKey(undefined);
    setCountryFieldCodes([]);
  };

  const formatHTS = (input: string): string => {
    if (input.length === 0) {
      return '';
    }
    const digitsOnlyLocal = input.replace(/\D/g, '');

    if (digitsOnlyLocal.length !== 10) {
      enqueueSnackbar('Please enter a valid 10 digit HTS Code.', {
        variant: 'error',
      });
      return 'error';
    }
    const parts = [
      digitsOnlyLocal.slice(0, 4),
      digitsOnlyLocal.slice(4, 6),
      digitsOnlyLocal.slice(6, 8),
      digitsOnlyLocal.slice(8, 10),
    ];
    return parts.join('.');
  };

  // Chapter 98/99 codes can't be run (see utils/htsChapters). The HTS input
  // already flags them and disables Run Check, but a lookup can also start
  // from a code passed in via location state or from re-running a saved check.
  const rejectUnsupportedHtsChapter = (hts: string): boolean => {
    const chapter = getUnsupportedHtsChapter(hts);
    if (!chapter) {
      return false;
    }
    enqueueSnackbar(unsupportedHtsChapterMessage(chapter), {
      variant: 'error',
    });
    return true;
  };

  // put near your other helpers
  const norm = (s: string) =>
    (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u2019’]/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const getIso2 = (c: any): string => {
    if (!c) {
      return '';
    }
    if (typeof c === 'string' && /^[A-Z]{2}$/i.test(c)) {
      return c.toUpperCase();
    }
    return (
      c.usCustomsCountryCode ??
      c.us_customs_country_code ??
      c.alpha2 ??
      c.alpha2Code ??
      c.iso2 ??
      c.country?.alpha2 ??
      c.country?.alpha2Code ??
      c.country?.iso2 ??
      ''
    );
  };

  const candidateHasCountryCond = (c: Candidate) =>
    Array.isArray(c.countryKeys) && c.countryKeys.length > 0;

  const findCountryFieldKeyOn = (cands: Candidate[]): string | undefined => {
    for (const c of cands) {
      if (Array.isArray(c.countryKeys) && c.countryKeys.length) {
        return c.countryKeys[0]; // first one wins (STEEL_COUNTRY_OF_POUR etc.)
      }
    }
    return undefined;
  };

  // helper: does this row still have a monetary effect?
  /**
   * Does this row actually charge anything?
   *
   * The engine stamps `_hasEffect` on every row it returns — it is the same
   * question the duty math already answered, and answering it here meant
   * keeping a copy of the rate parser for a rendering decision.
   *
   * Fail-open: only a row explicitly marked false is skipped. An unmarked row
   * (a locally built one, or an older saved snapshot) is treated as charging,
   * because the cost of over-showing a country input is far lower than the cost
   * of hiding one the user needs.
   */
  const rowHasPositiveEffect = (r: ResultRow): boolean =>
    (r as any)?._hasEffect !== false;

  const buildCountryUiState = (
    applied: Candidate[],
    resolvedLine1: ResultRow[],
    resolvedLine2: ResultRow[],
  ) => {
    const present = new Set<string>();
    for (const r of [...resolvedLine1, ...resolvedLine2]) {
      if (!rowHasPositiveEffect(r)) {
        continue;
      } // <-- ignore neutralized rows
      const d = digitsOnly(r._codeDigits || r.code || '');
      if (d) {
        present.add(d);
      }
    }

    const visibleApplied = applied.filter((c) => present.has(c.codeDigits));
    const countryApplied = visibleApplied.filter(candidateHasCountryCond);

    const codes = Array.from(new Set(countryApplied.map((c) => c.codePretty)));
    const key = findCountryFieldKeyOn(countryApplied);

    return { show: codes.length > 0, codes: codes, key: key };
  };

  const getCountryName = (c: any): string => {
    if (!c) {
      return '';
    }
    if (typeof c === 'string') {
      return c;
    }

    // common shapes we’ve seen
    return (
      c.countryName ??
      c.name ??
      c.displayName ??
      c.label ??
      c.country?.name ??
      c.country?.countryName ??
      '' // fall through
    );
  };

  const ftaApplies = (entry: { countries: string[] }, origin: string) => {
    if (!entry || !entry.countries) {
      return false;
    }
    if (entry.countries.includes('Global')) {
      return true;
    }
    const o = norm(origin);
    return entry.countries.some((c) => norm(c) === o);
  };

  const extractSpecialCodes = (specialText: string): string[] => {
    if (!specialText) {
      return [];
    }
    const m = specialText.match(
      /Special\s+Rate\s+of\s+Duty:[^()]*\(([^)]*)\)/i,
    );
    const raw = m ? m[1] : '';
    const codes = raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => /^[A-Z]{1,2}[\+\*]?$/i.test(s));
    const compacted = codes.map((c) => c.replace(/\s+/g, ''));
    const valid = compacted.filter((c) =>
      Object.prototype.hasOwnProperty.call(FTA, c),
    );
    return [...new Set(valid)];
  };

  // Rebuild once more using the post-exclusion visible digits.
  // This unlocks codes like 9903.85.08 that were blocked by a code you just removed.
  /**
   * A toggle changed — recompute. The engine rebuilds candidates from the
   * claimed codes, so the toggle maps are the whole input.
   */
  const cascadeRebuild = (
    overrideFtas?: Record<string, boolean>,
    overrideExclusions?: Record<string, boolean>,
  ) => {
    recomputeFromState(
      undefined,
      overrideFtas ?? enabledFtas,
      overrideExclusions ?? enabledExclusions,
    );
  };

  const isColumn2Origin = (c: any): boolean => {
    // Prefer ISO-2 if you have it
    const iso = (getIso2(c) || '').toUpperCase();
    if (iso && COLUMN2_ISO2.has(iso)) {
      return true;
    }

    // Fallback on names (normalized)
    const n = norm(getCountryName(c));
    return [
      'belarus',
      'cuba',
      'north korea',
      "democratic people's republic of korea",
      'dprk',
      'russia',
      'russian federation',
    ].includes(n);
  };

  /* ---------- LIVE math helpers ---------- */


  // ---------- Units & parsing ----------

  type AmountComponent = { usdPerUnit: number; unit: string };

  // Normalize unit text to a canonical token (lowercase, no dots/superscripts)
  const normalizeUnit = (u: string): string => {
    if (!u) {
      return '';
    }
    let s = String(u).trim().toLowerCase();
    s = s.replace(/\.$/, ''); // "No." -> "No"
    s = s
      .replace(/²/g, '2')
      .replace(/³/g, '3')
      .replace(/\^2/g, '2')
      .replace(/\^3/g, '3');
    s = s.replace(/\s+/g, '');

    // canonicalize common synonyms from GN 3(g)
    // "No." (number), "ea"/"each"/"eaches" all collapse to pieces — HTSUS
    // rates write the same concept as "each" while ACE exports use "NO"/"EA".
    if (s === 'no' || s === 'ea' || s === 'each' || s === 'eaches') {
      s = 'pcs';
    }
    if (s === 'pair' || s === 'pairs' || s === 'pr' || s === 'prs.') {
      s = 'prs';
    }
    if (s === 'doz' || s === 'dz' || s === 'dozen' || s === 'dozens') {
      s = 'doz';
    }
    if (s === 'kilowatts') {
      s = 'kw';
    }
    if (s === 'kilowatthours' || s === 'kilowatt-hours') {
      s = 'kwh';
    }
    if (s === 'milliliter') {
      s = 'ml';
    }
    if (s === 'liter') {
      s = 'l';
    }
    if (s === 'squaremeter' || s === 'sqm') {
      s = 'm2';
    }
    if (s === 'squarecentimeter' || s === 'sqcm') {
      s = 'cm2';
    }
    if (s === 'cubicmeter' || s === 'cbm') {
      s = 'm3';
    }
    if (s === 'cubiccentimeter') {
      s = 'cm3';
    }
    if (
      s === 'linegross' ||
      s === 'grossline' ||
      s === 'grosslines' ||
      s === 'linesgross'
    ) {
      s = 'line/gross';
    }
    return s;
  };

  const parseAmountRateComponents = (rate: unknown): AmountComponent[] => {
    if (rate === null) {
      return [];
    }
    let s = String(rate).trim();
    if (!s || /free/i.test(s)) {
      return [];
    } // ⬅️ removed the '/%/' early exit

    // normalize
    s = s
      .replace(/\u00A0/g, ' ') // NBSP → space
      .replace(/⁄/g, '/');

    const parseNum = (x: string) => {
      const n = parseFloat(x.replace(/,/g, ''));
      return Number.isFinite(n) ? n : NaN;
    };

    // split multi-part expressions on '+'
    const parts = s
      .split('+')
      .map((p) => p.trim())
      .filter(Boolean);
    const out: AmountComponent[] = [];

    for (const p of parts) {
      // cents per unit (allows '/' inside the unit, e.g., 'line/ gross')
      let m =
        p.match(
          /^\s*([\d.,]+)\s*(?:¢|c(?:ent)?s?)\s*(?:\/|per)\s*([a-zA-Z0-9./^ \-]+)\s*$/i,
        ) ||
        p.match(
          /^\s*([\d.,]+)\s*(?:¢|c(?:ent)?s?)\s*per\s*([a-zA-Z0-9./^ \-]+)\s*$/i,
        );
      if (m) {
        const val = parseNum(m[1]);
        const unit = normalizeUnit(m[2]);
        if (!Number.isNaN(val) && unit) {
          out.push({ usdPerUnit: val / 100, unit: unit });
        }
        continue;
      }

      // cents — space-separated unit ("0.8¢ each"). HTSUS uses bare-space
      // separators for count units; without this branch the specific
      // portion silently drops and base duty undercounts. Unit token must
      // start with a letter so a trailing "5.4%" piece can't false-match.
      m = p.match(
        /^\s*([\d.,]+)\s*(?:¢|c(?:ent)?s?)\s+([a-zA-Z][a-zA-Z0-9./^ \-]*?)\s*$/i,
      );
      if (m) {
        const val = parseNum(m[1]);
        const unit = normalizeUnit(m[2]);
        if (!Number.isNaN(val) && unit) {
          out.push({ usdPerUnit: val / 100, unit: unit });
        }
        continue;
      }
      // dollars per unit
      m = p.match(
        /^\s*(?:\$|usd\s*)?([\d.,]+)\s*(?:usd)?\s*(?:\/|per)\s*([a-zA-Z0-9./^ \-]+)\s*$/i,
      );
      if (m) {
        const val = parseNum(m[1]);
        const unit = normalizeUnit(m[2]);
        if (!Number.isNaN(val) && unit) {
          out.push({ usdPerUnit: val, unit: unit });
        }
        continue;
      }

      // dollars — space-separated unit ("$0.45 each").
      m = p.match(
        /^\s*(?:\$|usd\s*)?([\d.,]+)\s*(?:usd)?\s+([a-zA-Z][a-zA-Z0-9./^ \-]*?)\s*$/i,
      );
      if (m) {
        const val = parseNum(m[1]);
        const unit = normalizeUnit(m[2]);
        if (!Number.isNaN(val) && unit) {
          out.push({ usdPerUnit: val, unit: unit });
        }
      }

      // if the part is just a percent (e.g., '4.6%'), ignore here;
      // your base/line percent handling covers it elsewhere.
    }

    return out;
  };

  const USMCA_USCONTENT_KEY = 'USCONTENT';
  const USMCA_USCONTENT_CAP_PCT = 40;

  // Return the effective per-line percent for a split key, applying the
  // USMCA derivative steel cap to USCONTENT. Caller still stores the raw
  // user value in materialPctByKey so the slider reflects what they typed.
  const cappedSplitPct = (key: string, raw: any): number => {
    const n = Number(raw) || 0;
    if (key === USMCA_USCONTENT_KEY) {
      return Math.min(USMCA_USCONTENT_CAP_PCT, Math.max(0, n));
    }
    return n;
  };


  /**
   * Form state derived from an engine answer. US only — the Canada/Mexico view
   * is driven by foreignCostOverrides.
   *
   * Called from BOTH the first calculation and every toggle: toggling an
   * exclusion changes which candidates apply, which can change whether a
   * country input is needed and whether the line splits.
   */
  const applyEngineFormState = (remote: any) => {
    const engineResults = remote?.results || {};
    setTariffCandidates(engineResults.candidates || []);
    // Every ban covering the line, lifted or not; activeImportBan picks the one
    // the filer's answers leave on.
    setImportBans(
      Array.isArray(engineResults.importBans) ? engineResults.importBans : [],
    );

    // buildCountryUiState is presentation over candidates, not duty math.
    const countryUi = buildCountryUiState(
      engineResults.candidates || [],
      // This answer's rows. tariffResults still holds the previous answer
      // until the next render, so the field was labelled from the last check.
      engineResults.summaryRows || [],
      [],
    );
    const engineCountryCodes: string[] = engineResults.countryKeyCodes || [];
    const finalShow =
      countryUi.show ||
      engineCountryCodes.length > 0 ||
      Boolean(engineResults.needsLoadingCountry);
    const finalCodes =
      countryUi.codes && countryUi.codes.length
        ? countryUi.codes
        : engineCountryCodes;

    setLoadingCountryShow(finalShow);
    setCountryFieldKey(countryUi.key);
    setCountryFieldCodes(finalCodes);

    if (finalShow && !loadingCountry) {
      setLoadingCountry(
        tariffInputs.originIso2 || tariffInputs.originCountry || '',
      );
    }

    const splitShares: any[] = engineResults.splitShares || [];
    if (splitShares.length > 0) {
      setMaterialPctByKey((prev: any) => {
        const next = { ...(prev ?? {}) };
        let added = false;
        for (const sh of splitShares) {
          if (sh?.key && next[sh.key] === undefined) {
            next[sh.key] = Number(sh.pct) || 0;
            added = true;
          }
        }
        // materialPctByKey re-posts on change, so a fresh object on every
        // answer would re-post forever.
        return added ? next : prev;
      });
    }

    setLineSplitShow(splitShares.length > 0);
    setAmmountShow((engineResults.requiredAmountUnits?.length || 0) > 0);
  };

  // Guards against an out-of-order server recompute; see runEngine.
  const engineSeqRef = React.useRef(0);

  /**
   * Re-run the US calculation on the server.
   *
   * US only. The Canada/Mexico view is a different subsystem — its rates come
   * from datahub and it carries the floor/ceiling model the US engine has no
   * equivalent for (see the reconciliation doc, §14.1) — so the endpoint does
   * not serve it and must never be asked to.
   */
  const recomputeViaEngine = (
    ftas: Record<string, boolean>,
    excls: Record<string, boolean>,
    logOnly: boolean,
  ): Promise<any | null> =>
    runEngine({
      inputs: buildEngineInputs(
        tariffInputs,
        productValue || 0,
        amountInputs,
        materialPctByKey,
        ftas,
        excls,
        loadingCountry,
        {
          entryDate: effectDate,
          loadingDate: loadDate,
          excludeIeepa: hideIeepa,
          importBanAnswers: currentImportBanAnswers(),
          // The material controls are 0-100, so 0.5 means half a percent.
          materialPctIsPercent: true,
          // Every `false` here is the user's own: a switch turned off, a 232
          // question answered, or a saved ticket's toggles.
          sendDeclined: true,
        },
      ),
      current: tariffResults?.data || {},
      productValue: productValue || 0,
      seqRef: engineSeqRef,
      label: `${tariffInputs.htsCode} / ${tariffInputs.originCountry}`,
      logOnly: logOnly,
      setData: (data: any) => setTariffResults({ isLoading: false, data: data }),
    });

  const recomputeFromState = async (
    overrideCandidates?: Candidate[],
    overrideFtas?: Record<string, boolean>,
    overrideEnabledExclusions?: Record<string, boolean>,
  ) => {
    setTariffResults((prev: any) => ({ ...(prev || {}), isLoading: true }));

    const ftas: Record<string, boolean> = overrideFtas ?? enabledFtas;
    const excls: Record<string, boolean> =
      overrideEnabledExclusions ?? enabledExclusions;

    // The Canada/Mexico view is computed by foreignCostOverrides, not here, and
    // the endpoint has no answer for it. An effect can still fire this while a
    // foreign country is selected, so clear the loading flag and stop rather
    // than leaving the panel spinning.
    if (selectedTariffCountry !== 'US') {
      setTariffResults((prev: any) => ({ ...(prev || {}), isLoading: false }));
      return;
    }

    // --- Duty: recomputed by the shared engine ---------------------------
    // A toggle is a re-post — the exclusion map as `claimedCh99Codes`, the FTA
    // map as `spiCode`. `overrideCandidates` is intentionally unused: the
    // engine rebuilds the candidate set from the claimed codes.
    const served = await recomputeViaEngine(ftas, excls, false);
    if (served === SUPERSEDED) {
      return;
    }
    if (!served) {
      enqueueSnackbar('Could not recalculate duty. Please try again.', {
        variant: 'error',
      });
      setTariffResults((prev: any) => ({ ...(prev || {}), isLoading: false }));
      return;
    }
    applyEngineFormState(served);
  };

  // FTA toggle
  const toggleFTA = (ftaKey: string, checked: boolean) => {
    if (isStaticView) {
      return;
    }

    setEnabledFtas((prev) => {
      const next = { ...prev, [ftaKey]: checked };
      // cascadeRebuild is the US pipeline; running it for MX/CA would
      // briefly rewrite tariffResults with US-shaped output before the
      // foreign-sync useEffect rebuilds it via buildForeignTariffData,
      // causing a visible flash. Skip it for foreign mode — the sync
      // useEffect already lists enabledFtas in its dependencies.
      if (selectedTariffCountry === 'US') {
        cascadeRebuild(next, undefined); // ftas overridden, exclusions unchanged
      }
      return next;
    });
  };

  // Optional exclusion toggle
  const toggleExclusion = (codeDigits: string, enabled: boolean) => {
    if (isStaticView) {
      return;
    }

    setEnabledExclusions((prev) => {
      const next = { ...prev, [codeDigits]: enabled };
      cascadeRebuild(undefined, next); // ftas unchanged, exclusions overridden
      return next;
    });
  };

  // normalize helpers
  const originKey = (originIso2: string, originName: string) =>
    (originIso2?.toUpperCase() || '').trim() ||
    (originName || '').trim().toLowerCase();

  // Gate answers carry across re-runs of the same line and origin but are
  // dropped when the check moves to another; see importBanCheckRef.
  const importBanAnswersForCheck = (): Record<string, BanGateAnswer> => {
    const checkKey = `${digitsOnly(tariffInputs.htsCode)}|${originKey(
      tariffInputs.originIso2,
      tariffInputs.originCountry,
    )}`;
    if (importBanCheckRef.current === checkKey) {
      return banGateAnswers;
    }
    importBanCheckRef.current = checkKey;
    if (Object.keys(banGateAnswers).length > 0) {
      setBanGateAnswers({});
    }
    return {};
  };

  // A re-run sends the answers only while they belong to the line on screen,
  // and leaves moving them to handleSubmit, the run that starts a new line. A
  // re-post fired before the debounced handleSubmit would otherwise send the
  // last line's answers — and moving importBanCheckRef from here would let
  // that handleSubmit, whose closure still holds them, send them as current.
  const currentImportBanAnswers = (): Record<string, BanGateAnswer> => {
    const checkKey = `${digitsOnly(tariffInputs.htsCode)}|${originKey(
      tariffInputs.originIso2,
      tariffInputs.originCountry,
    )}`;
    return importBanCheckRef.current === checkKey ? banGateAnswers : {};
  };

  // track last fetched pair to avoid duplicate calls
  const lastFetchRef = React.useRef<{ hts: string; origin: string } | null>(
    null,
  );

  useEffect(() => {
    // ✅ FIX: Don't wipe results if we are just viewing a saved record
    if (isStaticView) {
      return;
    }

    // Count every digit rather than the first 10: formatHTS in handleSubmit
    // refuses anything that isn't exactly 10, so an 11-digit entry cut down
    // to 10 here would set the loading state below and then be refused,
    // leaving the spinner up with nothing to clear it.
    const hts = digitsOnly(tariffInputs.htsCode);
    const origin = originKey(
      tariffInputs.originIso2,
      tariffInputs.originCountry,
    );

    // A code the tool will never run (chapter 98/99, or more than 10 digits)
    // must not leave the previous product's results up: Save and Download
    // would pair them with the new code. An incomplete code keeps them while
    // the user is still typing. The HTS input explains the refusal.
    if (getUnsupportedHtsChapter(hts) || hts.length > 10) {
      // A run in flight for the previous code would put its results back.
      usRequestSeq.current += 1;
      foreignRequestSeq.current += 1;
      setLoading(false);
      hydratingRef.current = false;
      resetTransactionState();
      setTariffResults({ isLoading: false, data: {} });
      setForeignTariffResult(EMPTY_FOREIGN_TARIFF_STATE);
      setAdcvdResults('');
      setHtsDocument({});
      setImportBans([]);
      lastFetchRef.current = null;
      return;
    }

    // handleSubmit refuses to run without a tariff license, which would strand
    // the loading state set below, so don't start the run at all. The license
    // banner already tells the user why.
    const ready = userhasTariffs && hts.length === 10 && origin.length > 0;
    if (!ready) {
      return;
    }

    // The run scheduled below replaces any in flight, which would otherwise
    // show the previous code's results until it starts.
    usRequestSeq.current += 1;
    foreignRequestSeq.current += 1;

    // 🚨 New: wipe prior toggles/options before we build a fresh transaction
    resetTransactionState();
    setTariffResults({ isLoading: true, data: {} });
    setAdcvdResults('');
    setHtsDocument({});
    lastFetchRef.current = null;

    const id = setTimeout(async () => {
      const hAgain = digitsOnly(tariffInputs.htsCode);
      const oAgain = originKey(
        tariffInputs.originIso2,
        tariffInputs.originCountry,
      );
      if (hAgain.length !== 10 || !oAgain) {
        return;
      }

      lastFetchRef.current = { hts: hAgain, origin: oAgain };
      await handleSubmit();
    }, 400);

    return () => clearTimeout(id);
  }, [
    tariffInputs.htsCode,
    tariffInputs.originCountry,
    tariffInputs.originIso2,
    // Add these dependencies to ensure the check works properly
    tariffId,
    isEditMode,
    // Starts true and only settles once the license loads.
    userhasTariffs,
  ]);

  useEffect(() => {
    // need a base rate to be present (i.e., after first handleSubmit)
    if (!tariffResults?.data?.baseDutyRateRaw) {
      return;
    }

    if (isStaticView) {
      return;
    }

    // The loading country gates rule applicability, so a change re-posts. The
    // engine rebuilds the candidate set from the claimed codes and
    // applyEngineFormState publishes it, so there is nothing to build here.
    recomputeFromState(undefined, enabledFtas);
  }, [loadingCountry]); // 👈 critical

  const countryMatches = (
    countryA: string | null | undefined,
    countryB: string | null | undefined,
  ): boolean => {
    const normA = normalizeCountry(countryA);
    const normB = normalizeCountry(countryB);

    if (!normA || !normB) {
      return false;
    }

    // "Global" matches everything
    if (normA === 'global' || normB === 'global') {
      return true;
    }

    return normA === normB;
  };

  /* -------------------- Foreign (MX/CA) Submit -------------------- */
  // MX/CA flow is fully backend-driven: the controller returns a
  // pre-normalized match object so we just stash it in state and render
  // a duty-rate panel. No client-side line splits, exclusions, or
  // stacking yet — that arrives with the flags work.
  // `scopeOverride` lets an answer handler re-run the lookup with the answer
  // it just recorded, rather than waiting a render for `canadaScope` state to
  // settle and reading a stale closure.
  const handleForeignSubmit = async (
    countryCode: 'MX' | 'CA',
    scopeOverride?: CanadaScopeAnswers,
  ) => {
    const scope = scopeOverride ?? canadaScope;
    if (isStaticView || !userhasTariffs) {
      return;
    }
    if (rejectUnsupportedHtsChapter(tariffInputs.htsCode)) {
      return;
    }
    const val = formatHTS(tariffInputs.htsCode);
    if (val === 'error') {
      return;
    }

    // Claim this request. Any response whose token is no longer the current
    // one has been superseded and must not touch state.
    const requestId = ++foreignRequestSeq.current;
    const isStale = () => requestId !== foreignRequestSeq.current;

    setForeignTariffResult({
      isLoading: true,
      match: null,
      hitCount: 0,
    });
    // Clear any flags from a prior search so the insights panel doesn't
    // show stale cuotas while the new lookup is in flight.
    setRiskFlags([]);
    setLoading(true);

    try {
      const resolvedOriginIso2 = (
        tariffInputs.originIso2 ||
        tariffInputs.originCountry ||
        ''
      )
        .toString()
        .trim()
        .toUpperCase();
      // SIMA findings carry the origin as a country name, so resolve the ISO-2
      // to its full name and send both; the datahub matches on the name.
      const resolvedOriginName = screeningCountries.find(
        (c) => c.iso2 === resolvedOriginIso2,
      )?.name;
      // Canada PGA requirements come from the commodity index, not the
      // tariff one, so they are fetched alongside rather than after — the
      // same shape the US tab uses, pinned to CA Import so a Canadian lookup
      // never picks up US filing requirements.
      const pgaPromise =
        countryCode === 'CA'
          ? DatahubAPI.commoditySearch({
              indices: ['bite_app_commodities_*'],
              primary: val,
              type: 'import',
              tradeType: 'CA Import',
              lens: ['PGA Codes'],
              agg: true,
            }).catch(() => null)
          : Promise.resolve(null);

      const res = await DatahubAPI.retrieveForeignHTSCode({
        code: val,
        sourceCountry: FOREIGN_SOURCE_BY_COUNTRY[countryCode],
        effectiveDate: format(effectDate, 'yyyy-MM-dd'),
        originCountryIso2: resolvedOriginIso2 || undefined,
        originCountryName: resolvedOriginName || undefined,
        // Canada scope answers, omitted while unanswered so the server can
        // report the measure as conditional rather than assume either way.
        meltedAndPouredInChina: scope.meltedAndPouredInChina,
        smeltedAndCastInChina: scope.smeltedAndCastInChina,
        quotaSide: scope.quotaSide,
      });

      // A newer answer has already been submitted; this response describes
      // superseded inputs.
      if (isStale()) return;

      if (!res?.success) {
        enqueueSnackbar(
          'Unexpected response while retrieving tariffs. Please try again.',
          { variant: 'error' },
        );
        setForeignTariffResult(EMPTY_FOREIGN_TARIFF_STATE);
        setHtsHistory([]);
        return;
      }

      const payload = res?.result || {};
      const rawMatch = payload?.match || null;
      const additionalDuties: ForeignAdditionalDuty[] = Array.isArray(
        payload?.additionalDuties,
      )
        ? payload.additionalDuties
        : [];
      const tigieModifications: MexicoTigieModification[] = Array.isArray(
        payload?.tigieModifications,
      )
        ? payload.tigieModifications
        : [];
      const foreignFtaOptions: ForeignFtaOption[] = Array.isArray(
        payload?.ftaOptions,
      )
        ? payload.ftaOptions
        : [];
      const cuotasSafeguards: MexicoCuotaSafeguard[] = Array.isArray(
        payload?.cuotasSafeguards,
      )
        ? payload.cuotasSafeguards
        : [];
      const simaMeasures: CanadaSimaMeasure[] = Array.isArray(
        payload?.simaMeasures,
      )
        ? payload.simaMeasures
        : [];
      const trqQuotas: CanadaQuota[] = Array.isArray(payload?.trqQuotas)
        ? payload.trqQuotas
        : [];

      // PGA requirements. `HS Operator` is applied here because the commodity
      // search does not — see pgaCoversCode.
      const pgaRes = await pgaPromise;
      if (isStale()) return;
      const enteredDigits = val.replace(/\D/g, '');
      const pgaRequirements: CanadaPgaRequirement[] = (
        (pgaRes?.success &&
          pgaRes?.result?.result?.body?.hits?.hits) ||
        []
      )
        .filter((h: any) => {
          const src = h?._source || {};
          if (src['BITE Lens'] !== 'PGA Codes') return false;
          return pgaCoversCode(src['HS Code'], src['HS Operator'], enteredDigits);
        })
        .map((h: any) => {
          const src = h._source || {};
          return {
            id: h._id || src['BITE Record ID'] || '',
            agency: src['List Name'] || null,
            program: src.Program || null,
            hsCode: src['HS Code'] || null,
            hsOperator: src['HS Operator'] || null,
            intendedUseCode: src['Intended Use Code'] || null,
            productCategory: src['Canadian Product Category'] || null,
            regulationUrl: src['Regulation URL'] || null,
          };
        });

      // HTS Code History, built from the same schedule records the US path
      // uses. Set before the domestic-origin short-circuit below, because the
      // schedule's own revision history is a property of the code and does not
      // depend on where the goods came from.
      setHtsHistory(
        buildHtsHistory(
          (Array.isArray(payload?.scheduleHistory)
            ? payload.scheduleHistory
            : []
          ).map((r: any, i: number) => ({
            id: r?.id || `schedule-${i}`,
            rate: r?.rate ?? null,
            effFrom: r?.effectiveFrom ? new Date(r.effectiveFrom) : null,
            effTo: r?.effectiveTo ? new Date(r.effectiveTo) : null,
          })),
          effectDate instanceof Date && !Number.isNaN(effectDate.getTime())
            ? effectDate
            : new Date(),
        ),
      );
      const hitCount = Number(payload?.hitCount || 0);

      // Trade remedies (CA surtax / safeguard, MX cuotas / safeguards, TIGIE
      // modifications) and FTAs are matched independently of the base Customs
      // Tariff rate, so they can apply even when the base-rate lookup misses
      // (e.g. a stat-suffix line that isn't carried in the nomenclature
      // index). Surface them rather than discarding an applicable remedy
      // along with the missing base record.
      const hasRemedyData =
        additionalDuties.length > 0 ||
        trqQuotas.length > 0 ||
        pgaRequirements.length > 0 ||
        tigieModifications.length > 0 ||
        foreignFtaOptions.length > 0 ||
        cuotasSafeguards.length > 0 ||
        simaMeasures.length > 0;

      // Domestic goods: when the country of origin is the same country as the
      // active tab (e.g. Canada-origin goods on the Canada tab), no import
      // tariffs or trade remedies apply — surtaxes, safeguards, TIGIE
      // modifications, cuotas, SIMA measures, and FTA claims all target
      // FOREIGN origins. Show only the base Customs Tariff (MFN) rate.
      // `resolvedOriginIso2` is already uppercased; compare against both the
      // ISO-2 tab code and the full source-country name in case only a name
      // was captured.
      const originIsDomestic =
        Boolean(resolvedOriginIso2) &&
        (resolvedOriginIso2 === countryCode ||
          resolvedOriginIso2 ===
            FOREIGN_SOURCE_BY_COUNTRY[countryCode].toUpperCase());
      // Once origin is domestic, remedies are suppressed everywhere: the
      // toast messaging, the match shell, and (via the stripped arrays below)
      // the display rows / cost overrides / risk flags that read off `match`.
      const effectiveHasRemedyData = hasRemedyData && !originIsDomestic;

      if (!rawMatch && !effectiveHasRemedyData) {
        enqueueSnackbar(
          `No ${FOREIGN_SOURCE_BY_COUNTRY[countryCode]} tariff record found for HTS ${val}.`,
          { variant: 'warning' },
        );
      } else if (!rawMatch) {
        enqueueSnackbar(
          `No base Customs Tariff rate found for HTS ${val}; showing applicable remedies only.`,
          { variant: 'info' },
        );
      }

      // Precedence could not reduce the applicable orders to one. That is a
      // data defect rather than a stacking case, and it changes what the user
      // should trust in the total, so it gets a toast as well as the row
      // notice in the Duty Summary.
      if (!originIsDomestic && additionalDuties.some((ad) => ad.precedenceConflict)) {
        const conflicting = additionalDuties
          .filter((ad) => ad.precedenceConflict)
          .map((ad) => ad.regulationNumber || ad.programName)
          .filter(Boolean)
          .join(' and ');
        enqueueSnackbar(
          `More than one Canada surtax order resolved to HTS ${val} (${conflicting}). These orders are not meant to overlap — only the highest is charged. Please verify which applies.`,
          { variant: 'warning' },
        );
      }

      // Splice the additional-duty list + TIGIE modifications onto the match
      // object so downstream memos see one cohesive ForeignTariffMatch shape.
      // When the base record is missing but remedies apply, build a minimal
      // shell (base row renders as "—") so the remedies still show.
      const match: ForeignTariffMatch | null =
        rawMatch || effectiveHasRemedyData
          ? {
              ...((rawMatch as ForeignTariffMatch) ||
                ({} as ForeignTariffMatch)),
              sourceCountry: FOREIGN_SOURCE_BY_COUNTRY[countryCode],
              htsCode: (rawMatch as ForeignTariffMatch)?.htsCode || val,
              additionalDuties: originIsDomestic ? [] : additionalDuties,
              tigieModifications: originIsDomestic ? [] : tigieModifications,
              ftaOptions: originIsDomestic ? [] : foreignFtaOptions,
              cuotasSafeguards: originIsDomestic ? [] : cuotasSafeguards,
              simaMeasures: originIsDomestic ? [] : simaMeasures,
              trqQuotas: originIsDomestic ? [] : trqQuotas,
              // A PGA requirement attaches to the GOODS, not the origin, so
              // domestic-origin goods keep it where the trade remedies above
              // are stripped.
              pgaRequirements: pgaRequirements,
            }
          : null;

      setForeignTariffResult({
        isLoading: false,
        match: match,
        hitCount: hitCount,
      });
    } catch (e: any) {
      if (isStale()) return;
      console.error('[handleForeignSubmit] failed:', e);
      enqueueSnackbar(
        `Failed to retrieve tariff: ${e?.message || 'Unknown error'}`,
        { variant: 'error' },
      );
      setForeignTariffResult(EMPTY_FOREIGN_TARIFF_STATE);
    } finally {
      // A superseded request must not clear the spinner: a newer lookup is
      // still in flight behind it.
      if (!isStale()) setLoading(false);
    }
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async () => {
    logAppAction('tariff:run');
    if (selectedTariffCountry !== 'US') {
      // A new search is a new shipment, so scope answers from the previous one
      // must not carry over. Passed explicitly as well as set, since the
      // lookup below runs before the state update lands.
      setCanadaScope({});
      setTrqPositions({});
      setTrqVariantChoice({});
      setTrqVariantChoice({});
      // Remission claims are dropped only when the line or origin changes;
      // see remissionCheckRef.
      const checkKey = `${digitsOnly(tariffInputs.htsCode)}|${originKey(
        tariffInputs.originIso2,
        tariffInputs.originCountry,
      )}`;
      if (remissionCheckRef.current !== checkKey) {
        remissionCheckRef.current = checkKey;
        setEnabledRemissions({});
      }
      return handleForeignSubmit(selectedTariffCountry, {});
    }
    if (isStaticView || !userhasTariffs) {
      return;
    }
    if (rejectUnsupportedHtsChapter(tariffInputs.htsCode)) {
      return;
    }
    const val = formatHTS(tariffInputs.htsCode);
    if (val === 'error') {
      return;
    }

    const requestId = ++usRequestSeq.current;
    const isStale = () => requestId !== usRequestSeq.current;

    resetTransactionState();
    setHideIeepa(false);
    setImportBans([]);

    hydratingRef.current = true;
    setLoading(true);
    setTariffResults({ isLoading: true, data: {} });
    setAdcvdResults('');
    setHtsDocument({});

    try {
      // 1) Fetch the HTS record
      const res = await DatahubAPI.retrieveHTSCode({
        code: val,
        country: tariffInputs.originCountry,
      });
      if (isStale()) return;

      const hits = res?.result?.result?.body?.hits?.hits;
      if (!Array.isArray(hits)) {
        enqueueSnackbar(
          'Unexpected response while retrieving tariffs. Please try again.',
          {
            variant: 'error',
          },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }

      if (hits.length === 0) {
        enqueueSnackbar(
          `No tariff record found for HTS ${val}${
            tariffInputs.originCountry
              ? ` (origin: ${tariffInputs.originCountry})`
              : ''
          }.`,
          { variant: 'warning' },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }

      // HTS Code History — shared with the foreign lookup so the two paths
      // cannot drift. See buildHtsHistory.
      if (Array.isArray(hits)) {
        setHtsHistory(
          buildHtsHistory(
            hits.map((h: any, realIndex: number) => {
              const src = h._source;
              return {
                id: h._id || `hit-${realIndex}`,
                rate: src['General Duty Rate'],
                effFrom: src['Effective From']
                  ? new Date(src['Effective From'])
                  : null,
                effTo: src['Effective To'] ? new Date(src['Effective To']) : null,
              };
            }),
            new Date(effectDate),
          ),
        );
      }

      // --- DATE-BASED DOCUMENT SELECTION LOGIC ---
      const entryDateStr = (() => {
        if (!effectDate || Number.isNaN(effectDate.getTime())) {
          return '0000-00-00';
        }

        // Use the date-fns library (which you already have imported!)
        // to grab the local calendar date safely as a string.
        return format(effectDate, 'yyyy-MM-dd');
      })();

      const matchedHit = hits.find((h: any) => {
        const src = h._source || {};

        // 1. Effective From (Default: Start of time)
        let fromStr = '0000-00-00';
        if (src['Effective From']) {
          fromStr = String(src['Effective From']).slice(0, 10);
        }

        // 2. Effective To (Default: End of time)
        let toStr = '9999-12-31';
        if (src['Effective To']) {
          toStr = String(src['Effective To']).slice(0, 10);
        }

        // Check if Entry Date falls within the document's validity window [Inclusive]
        return entryDateStr >= fromStr && entryDateStr <= toStr;
      });

      if (!matchedHit) {
        enqueueSnackbar(
          `No valid tariff record found for HTS ${val} on ${entryDateStr}.`,
          {
            variant: 'warning',
          },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }

      const htsDoc = matchedHit._source;

      // --- NEW: Prune "Archived" words from the code before use ---
      if (htsDoc && htsDoc['HS Code']) {
        htsDoc['HS Code'] = String(htsDoc['HS Code']).split(' - ')[0].trim();
      }
      // -----------------------------------------------------------

      if (!htsDoc) {
        enqueueSnackbar(
          'Tariff record was missing expected fields. Please try again.',
          {
            variant: 'error',
          },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }
      setHtsDocument(htsDoc);

      // AD/CVD + Forced Labor are matched by their exact `List Name`. PGA
      // records are named "PGA Codes - <Agency>" (FDA, EPA, USDA, …), so they
      // never equal a `List Name` of 'PGA Codes' and can only be matched by
      // `BITE Lens` === 'PGA Codes'. The datahub commodity handler ANDs the
      // lens + listName filters together, so PGA needs its own query; its hits
      // are merged below (the forEach buckets them into categorizedHits.pga).
      const baseFlagQuery = {
        indices: ['bite_app_commodities_*'],
        primary: val,
        type: 'import',
        agg: true,
      };
      const [adcvdRes, pgaRes] = await Promise.all([
        DatahubAPI.commoditySearch({
          ...baseFlagQuery,
          listName: [
            'U.S. Anti Dumping Countervailing Duty Orders (by Order)',
            'Forced and Child Labor HS Codes',
          ],
        }),
        DatahubAPI.commoditySearch({ ...baseFlagQuery, lens: ['PGA Codes'] }),
      ]);
      if (isStale()) return;

      if (adcvdRes?.success) {
        const listNameHits = adcvdRes?.result?.result?.body?.hits?.hits;

        if (!Array.isArray(listNameHits)) {
          enqueueSnackbar(
            'Unexpected response while retrieving tariffs. Please try again.',
          );
        } else {
          const pgaHits = Array.isArray(
            pgaRes?.result?.result?.body?.hits?.hits,
          )
            ? pgaRes.result.result.body.hits.hits
            : [];
          const adcvdHits = [...listNameHits, ...pgaHits];

          // --- NEW: Risk Flag Processing ---
          const newFlags: RiskFlag[] = [];
          const origin = tariffInputs.originCountry;

          // 1. Create buckets to organize hits
          const categorizedHits = {
            adCvd: [] as any[],
            forcedLabor: [] as any[],
            pga: [] as any[],
          };

          // 2. Organize all hits in a SINGLE pass
          adcvdHits.forEach((h: any) => {
            const listName = h._source?.['List Name'];
            const biteLens = h._source?.['BITE Lens'];

            if (
              listName ===
              'U.S. Anti Dumping Countervailing Duty Orders (by Order)'
            ) {
              categorizedHits.adCvd.push(h);
            } else if (listName === 'Forced and Child Labor HS Codes') {
              categorizedHits.forcedLabor.push(h);
            } else if (biteLens === 'PGA Codes') {
              categorizedHits.pga.push(h);
            }
          });

          // 3. Evaluate the buckets and build your flags

          // AD/CVD Check (Requires the extra country match validation)
          const hasAdCvdRisk = categorizedHits.adCvd.some(
            (h: any) =>
              h._source?.['List Name'] ===
                'U.S. Anti Dumping Countervailing Duty Orders (by Order)' &&
              countryMatches(
                origin,
                h._source?.['BITE-Identified Risk Country'],
              ),
          );

          if (hasAdCvdRisk) {
            newFlags.push({
              id: 'adcvd',
              title: 'Potential AD/CVD',
              description:
                'This transaction may be subject to AD / CVD orders. For more detailed information visit the BITE Import Screening Tool and search your transaction details utilizing the AD CVD Commodities filter.',
              severity: 'high',
            });
          }

          // Forced Labor Check
          if (categorizedHits.forcedLabor.length > 0) {
            newFlags.push({
              id: 'forced-labor',
              title: 'Forced Labor',
              description:
                'This product or origin may be flagged under UFLPA or WRO enforcement.',
              severity: 'medium',
            });
          }

          // PGA Check
          if (categorizedHits.pga.length > 0) {
            newFlags.push({
              id: 'pga',
              title: 'PGA Requirement',
              description:
                'Partner Government Agency filing may be required for this entry.',
              severity: 'info',
            });
          }

          // 4. Commit the final state
          setRiskFlags(newFlags);
        }
      }
      const originForColumn2 =
        tariffInputs.originIso2 || tariffInputs.originCountry;

      const generalRate = htsDoc['General Duty Rate'];
      const column2Rate = htsDoc['Column 2 Duty Rate'];

      // Use Column 2 when origin is Belarus, Cuba, North Korea, or Russia (BY, CU, KP, RU)
      const useColumn2 =
        isColumn2Origin(originForColumn2) &&
        String(column2Rate || '').trim() !== '';

      const baseDuty = useColumn2 ? column2Rate : generalRate;
      const desc = htsDoc['Product Name'];

      const baseRow: ResultRow = {
        type: ['BY', 'CU', 'KP', 'RU'].includes(tariffInputs.originIso2)
          ? 'Base Duty (Column 2)'
          : 'Base Duty',
        code: htsDoc['HS Code'], // Now using the cleaned code
        rate: baseDuty,
      };

      const dataObj: any = {
        baseDutyRate: baseDuty,
        baseDutyRateRaw: baseDuty,
        additionalDutyRate: '0%',
        productDesc: desc,
        results: [baseRow],
        lines: [],
        lineSplitLabel: '',
        optionalExclusions: [],
      };

      // 2) FTA options (seed toggles OFF)
      const specialField =
        htsDoc['Special Duty Rate'] || htsDoc['Special Rate of Duty'] || '';
      const specialCodes = extractSpecialCodes(String(specialField));

      const ftaOptions: FtaOption[] = [];
      const initialEnabledFtas: Record<string, boolean> = {};

      const originLocal = (tariffInputs.originCountry || '')
        .trim()
        .toLowerCase();
      const baseRateIsFree = isFreeBase(baseDuty);

      // --- 2a. Country-eligibility FTA injection (MPF-only) -------------------------
      // Surface the origin country's FTA even when the specific HTS code does
      // not carry it in its own special-rate data. Claiming it removes the MPF
      // for goods that qualify by country of origin but does NOT change the
      // base duty (the code itself isn't preferential). Data-backed FTAs are
      // handled in 2b below and keep their existing base-Free behavior.
      const MANUAL_FTA_BY_COUNTRY: Record<string, string> = {
        canada: 'S',
        mexico: 'S',
        'costa rica': 'P',
        'dominican republic': 'P',
        'el salvador': 'P',
        guatemala: 'P',
        honduras: 'P',
        nicaragua: 'P',
        chile: 'CL',
        israel: 'IL',
        isreal: 'IL',
        jordan: 'JO',
        japan: 'JP',
        morocco: 'MA',
        singapore: 'SG',
        oman: 'OM',
        peru: 'PE',
        'south korea': 'KR',
        'republic of korea': 'KR',
        'korea, republic of': 'KR',
        colombia: 'CO',
        panama: 'PA',
        nepal: 'NP',
      };

      const findManualFtaCode = (countryLower: string): string | null => {
        for (const key of Object.keys(MANUAL_FTA_BY_COUNTRY)) {
          if (countryLower.includes(key)) {
            return MANUAL_FTA_BY_COUNTRY[key];
          }
        }
        return null;
      };

      const manualFtaCode = findManualFtaCode(originLocal);

      if (manualFtaCode && MPF_EXEMPT_FTA_CODES.has(manualFtaCode)) {
        const entry = FTA[manualFtaCode];
        if (entry) {
          const alreadyInSpecial = specialCodes.includes(manualFtaCode);
          // Only inject the MPF-only variant when the code does NOT already
          // carry this FTA in its data — otherwise 2b surfaces it as a real,
          // base-Free preferential claim instead.
          if (!alreadyInSpecial) {
            ftaOptions.push({
              code: manualFtaCode,
              name: entry.name,
              mpfOnly: true,
            });
            // Same rule as 2b below: on only when the saved transaction claimed
            // it. This variant is MPF-only, so it never zeroes the base duty.
            initialEnabledFtas[manualFtaCode] =
              claimedSpiCodes.includes(manualFtaCode);
          }
        }
      }

      // --- 2b. Process existing HTS special rates ---
      if (specialCodes.length) {
        const origin = tariffInputs.originCountry;
        for (const code of specialCodes) {
          const entry = FTA[code];
          if (manualFtaCode && code === manualFtaCode && baseRateIsFree) {
            continue;
          }

          if (entry && ftaApplies(entry, origin)) {
            ftaOptions.push({ code: code, name: entry.name });
            // Default OFF, except when the saved transaction actually claimed
            // this SPI. Reaching here already means the tool OFFERS the code
            // (it is in the HTS's special rates) and that ftaApplies() accepted
            // the origin — so this can never force an ineligible preference on;
            // it only restores one the filer really claimed.
            initialEnabledFtas[code] = claimedSpiCodes.includes(code);
          }
        }
      }

      const uniqueFtaOptions = Array.from(
        new Map(ftaOptions.map((item) => [item.code, item])).values(),
      );

      // A claimed SPI the tool cannot offer is NOT applied — it is reported.
      // Two ways to land here: the HTS carries no such special rate, or the
      // origin isn't party to that agreement (ftaApplies rejected it). Either
      // way, silently ignoring the claim is what made a bulk row and its Live
      // Mode recalculation disagree with no visible reason.
      const offeredFtaCodes = new Set(
        uniqueFtaOptions.map((item: any) => item.code),
      );
      setUnofferedSpiCodes(
        claimedSpiCodes.filter((code) => !offeredFtaCodes.has(code)),
      );

      setEnabledFtas(initialEnabledFtas);
      dataObj.baseDutyRateRaw = baseDuty;
      dataObj.baseDutyRate = baseDuty;
      dataObj.ftaOptions = uniqueFtaOptions;

      // 3) Duty — computed by the shared engine ------------------------------
      // The candidate build, dependency expansion, multi-line assignment,
      // IEEPA/Pharma/Russia normalization, summary dedupe, split seeding,
      // display lines, the exclusion pool and the duty math all lived here.
      // They are the engine, and it now runs in one place.
      //
      // Reached only on the US path — handleSubmit returns to
      // handleForeignSubmit above. There is no local fallback: the pipeline it
      // would fall back TO is what was removed.
      const remote = await runEngine({
        inputs: buildEngineInputs(
          tariffInputs,
          productValue || 0,
          amountInputs,
          materialPctByKey,
          undefined,
          undefined,
          loadingCountry,
          {
            entryDate: effectDate,
            loadingDate: loadDate,
            // handleSubmit has just switched the toggle off; the state update
            // has not landed, so hideIeepa still holds the old value.
            excludeIeepa: false,
            importBanAnswers: importBanAnswersForCheck(),
            // The material controls are 0-100, so 0.5 means half a percent.
            materialPctIsPercent: true,
          },
        ),
        current: dataObj,
        productValue: productValue || 0,
        seqRef: engineSeqRef,
        label: `${tariffInputs.htsCode} / ${tariffInputs.originCountry}`,
        logOnly: false,
        setData: (data: any) => Object.assign(dataObj, data),
      });

      if (remote === SUPERSEDED) {
        // A later calculation owns the render.
        return;
      }
      if (!remote) {
        enqueueSnackbar(
          'Could not calculate duty for this line. Please try again.',
          { variant: 'error' },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }

      applyEngineFormState(remote);
      setTariffResults({ isLoading: false, data: dataObj });
      setLoading(false);
      hydratingRef.current = false;
    } catch (e) {
      if (isStale()) return;
      enqueueSnackbar(
        `Error retrieving tariffs. Please try again. ERROR: ${e}`,
        {
          variant: 'error',
        },
      );
      setTariffResults({ isLoading: false, data: {} });
      setLoading(false);
      hydratingRef.current = false;
    }
  };

  // ---- Live totals shown in the right-hand card ----
  // The engine's own figures. These used to be re-derived here from the lines
  // and the base rate — a second implementation of the duty math for a number
  // the engine already reported, and one the two could disagree on.
  const {
    additional: additionalDutyDollars,
    baseAmount: baseAmountDollars,
    liveAdditionalRate,
  } = React.useMemo(() => {
    const em = tariffResults.data?.engineMath || {};
    const additional = Number(em.additionalDutyDollars) || 0;
    const baseAmount = Number(em.baseAmountDollars) || 0;
    const pct = productValue ? (additional / productValue) * 100 : 0;
    return {
      additional: additional,
      baseAmount: baseAmount,
      liveAdditionalRate: `${pct
        .toFixed(2)
        .replace(/\.00$/, '')
        .replace(/(\.\d)0$/, '$1')}%`,
    };
  }, [tariffResults.data?.engineMath, productValue]);

  // Base % dollars — the ad-valorem portion, backed out of the engine's total
  // rather than recomputed per line.
  const basePercentDutyDollars = React.useMemo(() => {
    const em = tariffResults.data?.engineMath || {};
    const total = Number(em.dutyCost) || 0;
    const additional = Number(em.additionalDutyDollars) || 0;
    const baseAmount = Number(em.baseAmountDollars) || 0;
    return Math.max(0, total - additional - baseAmount);
  }, [tariffResults.data?.engineMath]);

  const dutyCost = React.useMemo(
    () => additionalDutyDollars + baseAmountDollars + basePercentDutyDollars,
    [additionalDutyDollars, baseAmountDollars, basePercentDutyDollars],
  );

  // MPF, HMF and the total are the engine's: it applies the fee's fiscal-year
  // bounds, the Chapter 98 and origin exemptions, and the FTA exemptions for
  // the SPIs the toggles posted.
  const { mpf, hmf, totalCost } = React.useMemo(
    () => engineFigures(tariffResults.data?.engineMath),
    [tariffResults.data?.engineMath],
  );

  /* -------------------- Foreign (MX/CA) → US-shape sync -------------------- */
  // When a MX/CA lookup completes, project the parsed rate into the same
  // tariffResults.data shape the US render reads. ammountShow + the
  // requiredAmountUnits on the data drive TariffRagSearch's quantity input.
  useEffect(() => {
    // Saved Work already hydrated tariffResults from its immutable snapshot.
    // There is no live foreign lookup in this mode, so treating the empty
    // foreign result as a no-match would erase the saved Canada/Mexico result.
    if (isStaticView) return;
    if (selectedTariffCountry === 'US') return;

    const match = foreignTariffResult.match;

    // Loading: leave whatever's in tariffResults (the parent already
    // toggled isLoading on foreignTariffResult). Render gates by the
    // top-level `loading` flag.
    if (foreignTariffResult.isLoading) return;

    if (!match) {
      // Foreign search resolved without a hit — wipe any stale data from
      // a prior search so we don't show last query's numbers under the
      // new (failed) lookup.
      setTariffResults({ isLoading: false, data: {} });
      setAmmountShow(false);
      setLineSplitShow(false);
      setLoadingCountryShow(false);
      setRiskFlags([]);
      return;
    }

    const data = buildForeignTariffData(
      match,
      enabledFtas,
      trqPositions,
      trqVariantChoice,
      enabledRemissions,
    );
    setTariffResults({ isLoading: false, data: data });
    setAmmountShow(data.requiredAmountUnits.length > 0);
    setLineSplitShow(false);
    setLoadingCountryShow(false);

    // Project Mexico cuotas / safeguards AND Canada SIMA measures into
    // the same RiskFlag list the US flow uses (PGA, Forced Labor, AD/CVD)
    // so they render in the existing insights panel. We pick a title
    // that reflects the legal action where possible
    // ("Cuota Compensatoria — Antidumping", "SIMA — Anti-dumping") and
    // surface the rate / origin / review reason in the description.
    const cuotas: MexicoCuotaSafeguard[] = match.cuotasSafeguards || [];
    const cuotaFlags: RiskFlag[] = cuotas.map((cs, idx) => {
      const isSafeguard =
        cs.listName === 'MX Safeguards' ||
        cs.measureFamily === 'safeguard' ||
        cs.measureFamilyEn === 'safeguard';
      const action = cs.legalActionType
        ? cs.legalActionType.charAt(0).toUpperCase() +
          cs.legalActionType.slice(1)
        : null;
      const title = isSafeguard
        ? `Mexico Safeguard${action ? ` — ${action}` : ''}`
        : `Cuota Compensatoria${action ? ` — ${action}` : ''}`;
      const product =
        cs.productScopeEn ||
        cs.productScope ||
        cs.productName ||
        'this product';
      const origin =
        cs.rawCountry || cs.entityCountry || cs.entityCountryIso2 || 'origin';
      const rate =
        cs.quotaRateRaw ||
        (cs.rateValueExtracted !== null && cs.rateValueExtracted !== undefined
          ? `${cs.rateValueExtracted}${cs.rateCurrencyOrSymbol || '%'}`
          : null);
      const ratePhrase = rate ? ` at ${rate}` : '';
      const review = cs.reviewReason
        ? ` ${cs.reviewReason}`
        : ' Validate product scope, exporter/producer scope, and resolution before applying.';
      return {
        id: cs.id || cs.measureId || `mx-cuota-${idx}`,
        title: title,
        description:
          `${product} originating in ${origin} may be subject to a ` +
          `${cs.measureFamilyEn || 'compensatory duty'}${ratePhrase}.${review}`,
        severity: 'high',
      };
    });

    // SIMA = Canada's Special Import Measures Act (CBSA's anti-dumping /
    // countervailing regime). Each "Measure in Force" record names a
    // product + origin pair currently subject to action. The CBSA list
    // typically contains many distinct measures per origin country, so
    // rather than emitting one flag per product we bucket by action type
    // and emit AT MOST one Anti-dumping + one Countervailing flag per
    // transaction. Records with combined Type (e.g. "Subsidy and
    // Dumping") contribute to both buckets.
    const simaMeasures: CanadaSimaMeasure[] = match.simaMeasures || [];
    const dumpingBucket: CanadaSimaMeasure[] = [];
    const subsidyBucket: CanadaSimaMeasure[] = [];
    for (const sm of simaMeasures) {
      const lower = (sm.type || '').toLowerCase();
      if (lower.includes('dumping')) dumpingBucket.push(sm);
      if (lower.includes('subsidy')) subsidyBucket.push(sm);
    }

    const buildSimaFlag = (
      bucket: CanadaSimaMeasure[],
      kind: 'dumping' | 'subsidy',
    ): RiskFlag | null => {
      if (bucket.length === 0) return null;
      const title =
        kind === 'dumping' ? 'SIMA — Anti-dumping' : 'SIMA — Countervailing';
      const dutyPhrase =
        kind === 'dumping'
          ? 'anti-dumping duties under the Special Import Measures Act'
          : 'countervailing duties under the Special Import Measures Act';
      const origin =
        bucket[0].entityCountry || bucket[0].entityCountryIso2 || 'origin';
      let description: string;
      if (bucket.length === 1) {
        // Single measure — keep the specific product + MIF Code so the
        // user can look up the exact finding.
        const sm = bucket[0];
        const product = sm.productName || 'This product';
        description =
          `${product} originating in ${origin} appears on CBSA's SIMA ` +
          `Measures in Force list and may be subject to ${dutyPhrase}. ` +
          `Verify the active measure (MIF Code: ${sm.mifCode || '—'}) ` +
          `with CBSA before importing.`;
      } else {
        // Multiple measures — go generic so the flag stays useful even
        // when a single origin has many distinct findings.
        description =
          `Goods originating in ${origin} appear on CBSA's SIMA Measures ` +
          `in Force list with ${bucket.length} active ${dutyPhrase}. ` +
          `Verify whether your specific product is named in the active ` +
          `measures with CBSA before importing.`;
      }
      return {
        id: `ca-sima-${kind}`,
        title: title,
        description: description,
        severity: 'high',
      };
    };

    const simaFlags: RiskFlag[] = [];
    const dumpingFlag = buildSimaFlag(dumpingBucket, 'dumping');
    const subsidyFlag = buildSimaFlag(subsidyBucket, 'subsidy');
    if (dumpingFlag) simaFlags.push(dumpingFlag);
    if (subsidyFlag) simaFlags.push(subsidyFlag);

    // PGA requirements, bucketed by agency the same way SIMA measures are
    // bucketed by action type — one record per covered product line would
    // otherwise bury the panel. The agency is what the filer acts on, and the
    // programmes it names are what tells them why.
    const pgaRequirements: CanadaPgaRequirement[] = match.pgaRequirements || [];
    const byAgency = new Map<string, CanadaPgaRequirement[]>();
    for (const req of pgaRequirements) {
      const key = req.agency || 'Partner Government Agency';
      if (!byAgency.has(key)) byAgency.set(key, []);
      byAgency.get(key)!.push(req);
    }
    const pgaFlags: RiskFlag[] = Array.from(byAgency.entries()).map(
      ([agency, reqs]) => {
        const programs = Array.from(
          new Set(reqs.map((r) => r.program).filter(Boolean)),
        ) as string[];
        // "PGA Health Canada" reads better as "Health Canada" once the flag
        // title already says PGA.
        const agencyName = agency.replace(/^PGA\s+/i, '');
        return {
          id: `pga-${agencyName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          title: `PGA Requirement — ${agencyName}`,
          description: `${
            programs.length > 0
              ? `May require filing under ${programs.join(', ')}.`
              : 'Partner Government Agency filing may be required for this entry.'
          } Confirm the programme requirements with CBSA before filing.`,
          severity: 'info' as const,
        };
      },
    );

    setRiskFlags([...cuotaFlags, ...simaFlags, ...pgaFlags]);
    // Including enabledFtas keeps the base rate display ("Free" when a
    // foreign FTA is claimed) in sync with the toggle without a separate
    // memo branch.
  }, [
    foreignTariffResult.match,
    foreignTariffResult.isLoading,
    isStaticView,
    selectedTariffCountry,
    enabledFtas,
    // A quota declaration — or the line-level rate chosen within it — changes
    // which rate is the base rate, so the display has to rebuild.
    trqPositions,
    trqVariantChoice,
    enabledRemissions,
  ]);

  // Foreign cost overrides — uses our calculateTariffDuty so Canada
  // "but not less than" floor / Mexico AMX additive semantics resolve
  // correctly instead of going through the US ad-valorem pipeline that
  // doesn't know about either.
  const foreignCostOverrides = React.useMemo(() => {
    if (selectedTariffCountry === 'US') return null;
    const match = foreignTariffResult.match;
    if (!match) return null;

    // A tariff-rate quota decides what the base rate even is. Until the user
    // declares their position — or where the dataset has no rate for the side
    // they declared — there is no defensible figure to show, so the cost
    // panel is put into its pending state rather than showing $0.00.
    const trq = resolveTrq(match.trqQuotas, trqPositions, trqVariantChoice);
    if (trq.state === 'pending' || trq.state === 'unavailable') {
      return {
        dutyCost: 0,
        baseCost: 0,
        additionalCost: 0,
        additionalRateDisplay: '—',
        totalCost: productValue || 0,
        warnings: [],
        pending: true,
      };
    }

    // Each side's record states the rate for that side. Over access always
    // replaces the base rate; within access does too where the dataset gives
    // a within rate, falling back to the ordinary base rate when it does not.
    const baseParsed =
      trq.state === 'over'
        ? trq.rate
        : trq.state === 'within' && trq.rate
        ? trq.rate
        : match?.parsedRate;

    // An active TIGIE Annex Modification replaces the base rate. Pick the
    // first modification with a successfully parsed rate and route the
    // base calculation through it, so "Modified" in the display + the
    // dollar amount agree on which rate is actually in force.
    const tigieModifications = match.tigieModifications || [];
    const activeMod = tigieModifications.find((m) => m.parsedRate?.parsed);

    // Rate that drives the base / AMX calc. Null when neither a base rate nor
    // a modification parsed — e.g. the base Customs Tariff line wasn't found
    // but a surtax / safeguard still applies. In that case there's no base
    // duty and we layer only the additional remedies below.
    const parsed: ParsedTariffRate | null =
      activeMod && activeMod.parsedRate
        ? activeMod.parsedRate
        : baseParsed && baseParsed.parsed
        ? baseParsed
        : null;

    const additionalDuties = match.additionalDuties || [];

    // Nothing to compute when there's neither a base/modification rate nor
    // any additional remedy.
    if (!parsed && additionalDuties.length === 0) return null;

    // Quantity is read from the same amountInputs map TariffRagSearch writes
    // into, keyed by canonical unit, so each specific component (which may use
    // a different unit) pulls the quantity entered for its OWN unit rather than
    // sharing one quantity across all components.
    const quantityForUnit = (unit: TariffRateUnit) =>
      Number(amountInputs?.[tariffUnitToCanonical(unit)]) || 0;

    const calc = parsed
      ? calculateTariffDuty(
          parsed,
          productValue || 0,
          quantityForUnit,
          match.unitOfQuantity,
        )
      : null;

    // Cost summary mapping:
    //   AMX (primary + additional) → primary into baseCost, additional
    //                                into the "Additional Duty" line.
    //   floor/ceiling              → whichever wins is the entire duty,
    //                                presented under Base.
    //   single component           → presented under Base.
    // Step 1: split calc.appliedAmount into a base / additional pair
    // based on the parsed-rate shape. MX AMX has both; floor/ceiling /
    // free / simple-percent / simple-specific all collapse into base.
    // Skipped when there's no base rate (base-less remedy-only case).
    let baseCost = 0;
    let amxAdditional = 0;
    let amxAdditionalRateText = '';
    if (parsed && calc) {
      if (parsed.additional) {
        baseCost =
          calc.components.find(
            (c) =>
              c.label.startsWith('Ad valorem') ||
              c.label.startsWith('Specific') ||
              c.label === 'Free',
          )?.amount || 0;
        amxAdditional =
          calc.components.find((c) => c.label.startsWith('Additional'))
            ?.amount || 0;
        amxAdditionalRateText = `$${
          parsed.additional.amountPerUnit
        }/${tariffUnitToCanonical(parsed.additional.unit)}`;
      } else {
        baseCost = calc.appliedAmount;
      }
    }

    // A claimed FTA makes the base rate Free for the importer — zero out
    // baseCost (and the AMX additional, since they're both part of the
    // ordinary base regime that the preferential treatment replaces).
    // Surtaxes / safeguards stay because they're separate remedies, not
    // base duty.
    const ftaClaimed = isAnyForeignFtaClaimed(match, enabledFtas);
    if (ftaClaimed) {
      baseCost = 0;
      amxAdditional = 0;
      amxAdditionalRateText = '';
    }

    // Step 2: layer Canada surtax / safeguard contributions on top. Each is
    // ad valorem against productValue or specific × quantity. Precedence
    // resolution already ran server-side, so what arrives here is normally a
    // single measure — or, for entries predating an exclusivity clause, a
    // genuine legal stack that SHOULD be summed.
    //
    // The exception is `precedenceConflict`: the server could not reduce two
    // non-overlapping orders to one. Summing them would invent a charge no
    // order imposes, so the highest single measure is taken instead and the
    // rows carry a notice. (The schema's rule: log it, do not sum.)
    const amountFor = (ad: ForeignAdditionalDuty): number => {
      const adParsed = ad.parsedRate;
      if (!adParsed?.parsed) return 0;
      if (adParsed.primary.kind === 'percent') {
        return (productValue || 0) * (adParsed.primary.percent / 100);
      }
      if (adParsed.primary.kind === 'specific') {
        const unit = tariffUnitToCanonical(adParsed.primary.unit);
        const qty = Number(amountInputs?.[unit]) || 0;
        return adParsed.primary.amountPerUnit * qty;
      }
      return 0;
    };

    const hasPrecedenceConflict = additionalDuties.some(
      (ad) => ad.precedenceConflict,
    );
    const inForce = hasPrecedenceConflict
      ? additionalDuties
          .slice()
          .sort((a, b) => amountFor(b) - amountFor(a))
          .slice(0, 1)
      : additionalDuties;
    // A claimed remission removes the surtax its order covers. Applied after
    // the precedence pick, so remitting the measure in force can't bring a
    // superseded one back into the charge.
    const chargeable = inForce.filter(
      (ad) => !isRemitted(ad, enabledRemissions),
    );

    let canadaAdditional = 0;
    let canadaPctSum = 0;
    let canadaSpecificSum = 0;
    for (const ad of chargeable) {
      const adParsed = ad.parsedRate;
      if (!adParsed?.parsed) continue;
      const amt = amountFor(ad);
      canadaAdditional += amt;
      if (adParsed.primary.kind === 'percent') {
        canadaPctSum += adParsed.primary.percent;
      } else if (adParsed.primary.kind === 'specific') {
        canadaSpecificSum += amt;
      }
    }

    const additionalCost = amxAdditional + canadaAdditional;
    const dutyCost = baseCost + additionalCost;

    // Step 3: build a display string that summarizes whatever additional
    // pieces are in play — MX AMX leg, summed CA percent surtaxes /
    // safeguards, summed CA specific surtaxes / safeguards.
    const pctPart = canadaPctSum > 0 ? `${canadaPctSum}%` : '';
    const specificPart =
      canadaSpecificSum > 0 ? formatUSD(canadaSpecificSum) : '';
    const additionalRateDisplay =
      [amxAdditionalRateText, pctPart, specificPart]
        .filter(Boolean)
        .join(' + ') || '0%';

    return {
      dutyCost: dutyCost,
      baseCost: baseCost,
      additionalCost: additionalCost,
      additionalRateDisplay: additionalRateDisplay,
      // MPF/HMF are US-only entry fees; exclude from MX/CA totals.
      totalCost: (productValue || 0) + dutyCost,
      warnings: calc?.warnings || [],
      pending: false,
    };
  }, [
    selectedTariffCountry,
    foreignTariffResult.match,
    productValue,
    amountInputs,
    enabledFtas,
    trqPositions,
    trqVariantChoice,
    enabledRemissions,
  ]);

  const aiExplain = async () => {
    try {
      setAILoading(true);

      const chapter99Codes = Array.from(
        new Set(
          (tariffResults.data.results as ResultRow[])
            .map((r) => r?.code || '')
            .filter(Boolean)
            .map((code) => formatHtsPretty(String(code)))
            .filter((pretty) => /^\s*99/.test(pretty.replace(/\D/g, ''))),
        ),
      );

      const otherTariffLayers = (tariffResults.data.results as ResultRow[])
        .filter(
          (r) => r.type !== 'Base Duty' && r.type !== 'Base Duty (Column 2)',
        )
        .map((r) => ({ code: r.code, rate: r.rate, type: r.type }));

      const explainReq = {
        hs_code: formatHtsPretty(
          htsDocument['HS Code'] || formatHTS(tariffInputs.htsCode),
        ),
        country: tariffInputs.originCountry,
        transaction_date: format(effectDate, 'yyyy-MM-dd'),
        general_rate:
          tariffResults.data.baseDutyRateRaw ??
          tariffResults.data.baseDutyRate ??
          '',
        column2_rate: `Not Applicable - ${tariffInputs.originCountry} is not subject to Column 2 duties`,
        special_duty_rate: String(
          (
            htsDocument['Special Duty Rate'] ||
            htsDocument['Special Rate of Duty'] ||
            ''
          ).trim() || 'No Special Duty Found',
        ),
        ad_cvd_status:
          'This transaction may not be subject to AD / CVD orders.',
        chapter99_codes: chapter99Codes,
        other_tariff_layers: otherTariffLayers,
      } as const;

      const aiResp = await runAIExplanationOnly(explainReq);
      setExplanationText(aiResp?.explanation ?? '');
      setAILoading(false);
    } catch (e) {
      console.warn('[AI Explanation] Failed to fetch explanation', e);
      setAILoading(false);
    }
  };

  // 3. GENERATE QUESTIONS FROM CANDIDATES
  const canadaQuotas: CanadaQuota[] = React.useMemo(() => {
    if (selectedTariffCountry !== 'CA') return [];
    const quotas = foreignTariffResult.match?.trqQuotas;
    return Array.isArray(quotas) ? quotas : [];
  }, [selectedTariffCountry, foreignTariffResult.match]);

  // Panel-shaped view of the quotas: the rate each side would apply, rendered
  // from the parsed rate so the figure in the card is the same number the
  // duty calculation would use, not a re-reading of the narrative prose.
  const trqPanelQuotas = React.useMemo(() => {
    const sideFor = (side: CanadaTrqSide | null) => {
      if (!side) return null;
      const p = side.parsedRate;
      // A successfully parsed Free rate is a real rate — several within-access
      // sides are duty-free — and must not fall through to the "not in
      // dataset" wording.
      const rateDisplay = !p?.parsed
        ? null
        : p.primary.kind === 'percent'
        ? `${p.primary.percent}%`
        : p.primary.kind === 'specific'
        ? `$${p.primary.amountPerUnit}/${tariffUnitToCanonical(p.primary.unit)}`
        : 'Free';
      const variants = (side.rateVariants || []).map((v) => {
        const vp = v.parsedRate;
        return {
          label: v.label,
          rateText: v.rateText,
          rateDisplay:
            vp?.parsed && vp.primary.kind === 'percent'
              ? `${vp.primary.percent}%`
              : vp?.parsed && vp.primary.kind === 'specific'
              ? `$${vp.primary.amountPerUnit}/${tariffUnitToCanonical(
                  vp.primary.unit,
                )}`
              : vp?.parsed
              ? 'Free'
              : v.rateText,
        };
      });

      return {
        versionType: side.versionType,
        rateNarrative: side.rateNarrative,
        rateVaries: side.rateVaries === true,
        permitRequirement: side.permitRequirement,
        rateDisplay: rateDisplay,
        variants: variants,
      };
    };

    return canadaQuotas.map((q) => ({
      iclItem: q.iclItem,
      productName: q.productName,
      appliesTo: q.appliesTo,
      quotaYearFrom: q.quotaYearFrom,
      quotaYearTo: q.quotaYearTo,
      administrationMethod: q.administrationMethod,
      sharedWith: q.sharedWith,
      within: sideFor(q.within),
      over: sideFor(q.over),
      missingSides: q.missingSides || [],
    }));
  }, [canadaQuotas]);

  // Declaring a quota position needs no refetch: both sides came back with
  // the lookup, so this only decides which rate is applied.
  // Only one side of a quota can apply, so declaring one clears any line
  // chosen on the other. Leaving it set made both cards read as selected and
  // left a stale choice waiting to be re-applied if the user switched back.
  const clearOtherSideVariant = (
    prev: Record<string, number>,
    iclItem: string,
    keptSide: TrqPosition,
  ): Record<string, number> => {
    const next = { ...prev };
    delete next[`${iclItem}|${keptSide === 'within' ? 'over' : 'within'}`];
    return next;
  };

  const handleDeclareTrq = (iclItem: string, position: TrqPosition) => {
    if (isStaticView) return;
    setTrqPositions((prev) => ({ ...prev, [iclItem]: position }));
    setTrqVariantChoice((prev) =>
      clearOtherSideVariant(prev, iclItem, position),
    );
  };

  // Choosing which line of a varying group describes the goods. Declaring the
  // side follows automatically — the choice is only meaningful for that side,
  // and making the user click twice to say one thing is friction.
  const handleChooseTrqVariant = (
    iclItem: string,
    side: TrqPosition,
    index: number,
  ) => {
    if (isStaticView) return;
    setTrqVariantChoice((prev) => ({
      ...clearOtherSideVariant(prev, iclItem, side),
      [`${iclItem}|${side}`]: index,
    }));
    setTrqPositions((prev) => ({ ...prev, [iclItem]: side }));
  };

  // Filters to prefill when the user follows "Go to Screening".
  //
  // The screening query ANDs its BITE Lens and List Name filters, so asking for
  // the SIMA list AND the "PGA Codes" lens together returns nothing — no
  // document is both. Values WITHIN one filter are ORed, so both families are
  // requested as List Names instead: SIMA's, plus the List Name of each PGA
  // agency that actually flagged. Same records, one filter.
  const canadaScreeningLists = React.useMemo(() => {
    if (selectedTariffCountry !== 'CA') return [];
    // Any agency that actually flagged is unioned in as well, so an agency
    // added to the data later still reaches Screening without a code change.
    const flagged = (foreignTariffResult.match?.pgaRequirements || [])
      .map((r) => r.agency)
      .filter((a): a is string => Boolean(a));
    return Array.from(
      new Set([CANADA_SIMA_LIST_NAME, ...CANADA_PGA_LIST_NAMES, ...flagged]),
    );
  }, [selectedTariffCountry, foreignTariffResult.match]);

  // One toggle per remission order on the matched surtaxes, shown with the
  // FTA toggles (see utils/remissions).
  const canadaRemissionOptions = React.useMemo(
    () =>
      selectedTariffCountry === 'CA'
        ? remissionOptions(foreignTariffResult.match?.additionalDuties)
        : [],
    [selectedTariffCountry, foreignTariffResult.match],
  );

  const toggleRemission = (key: string, enabled: boolean) => {
    if (isStaticView) return;
    setEnabledRemissions((prev) => ({ ...prev, [key]: enabled }));
  };

  // Relief and non-application conditions for the Canada measures in play.
  //
  // Evaluated here rather than server-side because each depends on inputs the
  // user changes constantly — product value, loading date — and a lookup
  // round-trip per keystroke would be wasteful. Nothing here changes which
  // records MATCH; it only annotates the ones that already did.
  const canadaReliefItems: CanadaReliefItem[] = React.useMemo(() => {
    if (selectedTariffCountry !== 'CA') return [];
    const duties = foreignTariffResult.match?.additionalDuties || [];

    return duties
      .map((ad, idx) => {
        const conditions: CanadaReliefCondition[] = [];

        // De minimis. The threshold is CUMULATIVE across the shipment while
        // this tool prices one line, so it can only ever be ruled out, never
        // ruled in: a line above the floor settles it, a line below it does
        // not, because the other lines are not visible here.
        const floor = parseDeMinimisAmount(ad.deMinimisThreshold);
        if (ad.deMinimisThreshold) {
          const value = Number(productValue) || 0;
          const ruledOut = floor !== null && value > floor;
          if (!ruledOut) {
            conditions.push({
              label: 'De minimis threshold',
              detail: `${ad.deMinimisThreshold}. This tool prices one line at a time, and the threshold applies to the shipment's cumulative value for duty${
                floor !== null && value > 0
                  ? ` — this line is ${formatUSD(value)}, so confirm the shipment total against the ${formatUSD(floor)} floor.`
                  : '.'
              }`,
            });
          }
        }

        // Goods in transit. `loadDate` is a proxy for "in transit on or before
        // date X" — the closest input the tool has — so this is raised as a
        // question rather than applied.
        if (ad.inTransitDate) {
          const loadedBefore =
            loadDate instanceof Date &&
            !Number.isNaN(loadDate.getTime()) &&
            moment(loadDate).format('YYYY-MM-DD') <= ad.inTransitDate;
          if (loadedBefore) {
            conditions.push({
              label: 'Goods in transit',
              detail:
                `Goods in transit to Canada on or before ${moment(ad.inTransitDate).format('MM/DD/YYYY')} ` +
                `are excluded from this Order. The loading date entered (${moment(loadDate).format('MM/DD/YYYY')}) ` +
                `is on or before that date, so this exclusion may apply — the duty above still assumes it does not.`,
            });
          }
        }

        if (ad.remissionAvailable === true) {
          const citation = [ad.remissionOrderName, ad.remissionOrderNumber]
            .filter(Boolean)
            .join(', ');
          conditions.push({
            label: `Remission${citation ? ` — ${citation}` : ''}${
              isRemitted(ad, enabledRemissions) ? ' (claimed)' : ''
            }`,
            detail:
              ad.remissionConditionSummary ||
              'A remission order exists for this measure; conditions were not published with the record.',
            url: ad.remissionOrderUrl || null,
          });
        }

        if (ad.exclusionSummary) {
          conditions.push({
            label: 'Goods excluded from this Order',
            detail: ad.exclusionSummary,
          });
        }

        return {
          id: ad.id || `${ad.regulationNumber || 'measure'}-${idx}`,
          measure: ad.programName || ad.measureName || 'Additional duty',
          regulation: ad.regulationNumber,
          conditions: conditions,
        };
      })
      .filter((item) => item.conditions.length > 0);
  }, [
    selectedTariffCountry,
    foreignTariffResult.match,
    productValue,
    loadDate,
    enabledRemissions,
  ]);

  // Unit-mismatch and missing-quantity warnings out of the duty calculation.
  // These were previously computed and dropped, so a rate the tool could not
  // fully apply produced a confident-looking number with no caveat anywhere.
  const foreignCalcFlags: RiskFlag[] = React.useMemo(() => {
    const warnings = foreignCostOverrides?.warnings || [];
    return warnings.map((w, i) => ({
      id: `foreign-calc-warning-${i}`,
      title: 'Duty calculation',
      description: w,
      severity: 'medium' as const,
    }));
  }, [foreignCostOverrides]);

  // Canada scope questions. These are derived from what the lookup actually
  // returned, so a question only appears when a measure turns on it — asking
  // about melt-and-pour for a code no such order covers is noise.
  //
  // A question also has to SURVIVE its own answer. Answering "within access"
  // can drop SOR/2025-148 from the result entirely (within access imposes
  // nothing), and answering "no" to melt-and-pour drops SOR/2025-154 — which
  // would take the question away with it and strand the user on an answer
  // they could not change. So a question stays once it has been answered.
  const canadaScopeQuestions: CanadaScopeQuestion[] = React.useMemo(() => {
    if (selectedTariffCountry !== 'CA') return [];
    const duties = foreignTariffResult.match?.additionalDuties || [];
    const questions: CanadaScopeQuestion[] = [];
    const yesNo = [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
    ];
    const asYesNo = (v?: boolean) =>
      v === undefined ? undefined : v ? 'yes' : 'no';

    const quotaInPlay = duties.some(
      (d) => d.offerChoice === true || d.quotaApplicable === true,
    );
    if (quotaInPlay || canadaScope.hasEipaPermit !== undefined) {
      questions.push({
        id: 'hasEipaPermit',
        prompt:
          'Do you hold a valid EIPA import permit (Import Control List item 82) for these goods?',
        helperText:
          'Goods imported without a valid permit are deemed over access regardless of volume (SOR/2025-148 ss. 2(4), 2.1(4)).',
        options: yesNo,
        selected: asYesNo(canadaScope.hasEipaPermit),
      });
      // Only worth asking with a permit in hand — without one the side is
      // already settled by the deeming rule above.
      if (canadaScope.hasEipaPermit === true) {
        questions.push({
          id: 'quotaSide',
          prompt: 'Are these goods within your quarterly quota allocation?',
          helperText:
            'Within-access imports pay no surtax under this Order, only ordinary duty. Over-access imports pay 50%.',
          options: [
            { id: 'within', label: 'Within access' },
            { id: 'over', label: 'Over access' },
          ],
          selected: canadaScope.quotaSide,
        });
      }
    }

    if (
      duties.some((d) => d.originTest === 'melt_pour') ||
      canadaScope.meltedAndPouredInChina !== undefined
    ) {
      questions.push({
        id: 'meltedAndPouredInChina',
        prompt: 'Was this steel first melted and poured in China?',
        helperText:
          'SOR/2025-154 turns on where the steel was made, not on the country of origin.',
        options: yesNo,
        selected: asYesNo(canadaScope.meltedAndPouredInChina),
      });
    }

    if (
      duties.some((d) => d.originTest === 'smelt_cast') ||
      canadaScope.smeltedAndCastInChina !== undefined
    ) {
      questions.push({
        id: 'smeltedAndCastInChina',
        prompt: 'Was this aluminium smelted and cast in China?',
        helperText:
          'SOR/2025-154 turns on where the aluminium was made, not on the country of origin.',
        options: yesNo,
        selected: asYesNo(canadaScope.smeltedAndCastInChina),
      });
    }

    return questions;
  }, [selectedTariffCountry, foreignTariffResult.match, canadaScope]);

  // Each answer re-runs the lookup, because these change which records match
  // rather than how a matched record is priced.
  const handleCanadaScopeAnswer = (questionId: string, optionId: string) => {
    if (isStaticView) return;
    const next: CanadaScopeAnswers = { ...canadaScope };

    if (questionId === 'hasEipaPermit') {
      const holdsPermit = optionId === 'yes';
      const permitAnswerChanged = canadaScope.hasEipaPermit !== holdsPermit;
      next.hasEipaPermit = holdsPermit;

      if (!holdsPermit) {
        // No permit means deemed over access regardless of volume, so the
        // follow-up is moot and any earlier "within" answer must not survive.
        next.quotaSide = 'over';
      } else if (permitAnswerChanged) {
        // Switching No -> Yes. The "over" recorded above was INFERRED from
        // not holding a permit, not declared, and it is void the moment the
        // permit answer changes — leaving it in place would keep charging the
        // over-access rate while the follow-up question sat unanswered.
        delete next.quotaSide;
      }
      // Answering Yes again when Yes was already the answer leaves a
      // genuinely declared quotaSide alone.
    } else if (questionId === 'quotaSide') {
      next.quotaSide = optionId === 'within' ? 'within' : 'over';
    } else if (questionId === 'meltedAndPouredInChina') {
      next.meltedAndPouredInChina = optionId === 'yes';
    } else if (questionId === 'smeltedAndCastInChina') {
      next.smeltedAndCastInChina = optionId === 'yes';
    } else {
      return;
    }

    setCanadaScope(next);
    handleForeignSubmit('CA', next);
  };

  // The matched bans' gate questions, asked on the same scope card the Canada
  // tab uses. A gate with no stored answer can't lift its ban, so it isn't
  // asked. Each starts at its stored answer: the ban applies until the filer
  // answers otherwise.
  const importBanQuestions = React.useMemo(() => {
    const questions: Array<{
      id: string;
      prompt: string;
      helperText: string;
      options: Array<{ id: string; label: string }>;
      selected?: string;
    }> = [];
    importBans.forEach((ban) => {
      ban.gates.forEach((gate) => {
        if (
          gate.keepOnAnswer === null ||
          questions.some((q) => q.id === gate.id)
        ) {
          return;
        }
        questions.push({
          id: gate.id,
          prompt: gate.question,
          helperText: `${ban.measure}. The goods are excluded from importation unless this answer lifts the ban.`,
          options: [
            { id: 'yes', label: 'Yes' },
            { id: 'no', label: 'No' },
          ],
          selected: importBanGateAnswer(gate, banGateAnswers) ?? undefined,
        });
      });
    });
    return questions;
  }, [importBans, banGateAnswers]);

  // No recalculation needed: the duty result under the ban is already built.
  const handleImportBanAnswer = (gateId: string, optionId: string) => {
    if (isStaticView || (optionId !== 'yes' && optionId !== 'no')) {
      return;
    }
    const answer: BanGateAnswer = optionId;
    setBanGateAnswers((prev) => ({ ...prev, [gateId]: answer }));
  };

  const exclusionQuestions = React.useMemo(() => {
    // CHANGED: Source from the calculated results (displayed in table) instead of raw candidates.
    const activeCandidates =
      (tariffResults?.data?.optionalExclusions as Candidate[]) || [];

    const groups: Record<
      string,
      { id: string; label: string; enablesExclusions: string[] }[]
    > = {};

    activeCandidates.forEach((c) => {
      const q = c.bd_232_question;
      const a = c.bd_232_answer;

      if (q && a) {
        if (!groups[q]) {
          groups[q] = [];
        }

        const answerLabel = a.charAt(0).toUpperCase() + a.slice(1); // Capitalize "yes" -> "Yes"
        const answerId = a.toLowerCase();

        // Check if we already have this answer option for this question
        let option = groups[q].find((o) => o.id === answerId);

        if (!option) {
          option = {
            id: answerId,
            label: answerLabel,
            enablesExclusions: [],
          };
          groups[q].push(option);
        }

        // Add this candidate's code to the option's enablement list
        if (!option.enablesExclusions.includes(c.codeDigits)) {
          option.enablesExclusions.push(c.codeDigits);
        }
      }
    });

    // --- NEW: Ensure both Yes and No options always exist ---
    Object.values(groups).forEach((options) => {
      const hasYes = options.some((o) => o.id === 'yes');
      const hasNo = options.some((o) => o.id === 'no');

      if (hasYes && !hasNo) {
        options.push({ id: 'no', label: 'No', enablesExclusions: [] });
      } else if (hasNo && !hasYes) {
        options.push({ id: 'yes', label: 'Yes', enablesExclusions: [] });
      }
    });
    // --------------------------------------------------------

    // Convert map to array for standard dynamic questions
    const generatedQuestions = Object.entries(groups).map(
      ([prompt, options], idx) => ({
        id: `generated-q-${idx}`,
        prompt: prompt,
        helperText:
          'Select the option that describes your product to apply the correct tariff or exclusion.',
        options: options.sort((a, b) => {
          // Simple sort to put "Yes" before "No" usually, or alphabetical
          if (a.id === 'yes') return -1;
          if (b.id === 'yes') return 1;
          return a.label.localeCompare(b.label);
        }),
      }),
    );

    return generatedQuestions;
  }, [tariffResults?.data?.optionalExclusions]);

  // 4. HANDLE ANSWER SELECTION (BULK TOGGLE)
  const handleQuestionAnswer = (
    codesToEnable: string[],
    codesToDisable: string[],
  ) => {
    if (isStaticView) return;

    setEnabledExclusions((prev) => {
      const next = { ...prev };

      // First disable the "other" options
      codesToDisable.forEach((code) => {
        next[code] = false;
      });

      // Then enable the "selected" options
      codesToEnable.forEach((code) => {
        next[code] = true;
      });

      // Trigger the cascade rebuild with the new state
      cascadeRebuild(undefined, next);

      return next;
    });
  };

  useEffect(() => {
    if (!tariffResults?.data?.baseDutyRateRaw) {
      return;
    }
    if (loading || hydratingRef.current) {
      return;
    }
    if (isStaticView) {
      return;
    }

    // Duty and fees both come from the engine, so any input it prices on
    // re-posts — including on a line with no Chapter 99 candidates.
    recomputeFromState();
  }, [
    productValue,
    materialPctByKey,
    amountInputs,
    hideIeepa,
    tariffInputs.transportMethod,
    // Answering a gate can lift or restore a ban, and the engine returns only
    // the ban while one is on.
    banGateAnswers,
  ]);

  useEffect(() => {
    const all = (tariffResults?.data?.ftaAllCodes || []) as string[];
    if (!all.length) {
      return;
    }

    const freshOptions: FtaOption[] = [];
    const nextEnabled: Record<string, boolean> = {};

    for (const code of all) {
      const entry = FTA[code];
      if (entry && ftaApplies(entry, tariffInputs.originCountry)) {
        freshOptions.push({ code: code, name: entry.name });
        nextEnabled[code] = enabledFtas[code] ?? false; // keep toggle if same key existed
      }
    }

    setTariffResults((prev: any) => ({
      ...prev,
      data: { ...prev.data, ftaOptions: freshOptions },
    }));

    // Drop any toggles that are no longer applicable to the new origin
    const cleaned: Record<string, boolean> = {};
    for (const { code } of freshOptions) {
      cleaned[code] = Boolean(nextEnabled[code]);
    }
    setEnabledFtas(cleaned);

    // origin affects candidate country filters too
    if (tariffResults?.data?.baseDutyRateRaw) {
      recomputeFromState();
    }
  }, [tariffInputs.originCountry, tariffInputs.originIso2]);

  useEffect(() => {
    const units: string[] | undefined =
      tariffResults?.data?.requiredAmountUnits;
    if (!Array.isArray(units) || units.length === 0) {
      return;
    }

    setAmountInputs((prev) => {
      const normLoc = (u: string) =>
        u
          .trim()
          .toLowerCase()
          .replace(/\.$/, '')
          .replace(/²/g, '2')
          .replace(/³/g, '3')
          .replace(/\^2/g, '2')
          .replace(/\^3/g, '3')
          .replace(/\s+/g, '');

      let changed = false;
      const next = { ...prev };

      for (const u of units) {
        const key = normLoc(u);
        if (next[key] === null) {
          // <-- note: catches undefined too
          next[key] = 10;
          changed = true;
        }
      }

      return changed ? next : prev; // <-- critical: no change, no rerender
    });
  }, [tariffResults?.data?.requiredAmountUnits]);

  useEffect(() => {
    // Only run after we have an initial result
    if (!tariffResults?.data?.baseDutyRateRaw) {
      return;
    }
    // Don’t run while the main request is still loading / hydrating
    if (loading || hydratingRef.current) {
      return;
    }

    // --- FIX START: PREVENT RECALCULATION IN VIEW MODE ---
    // If we are viewing a saved record (not editing), do not trigger
    // a rebuild just because loadingCountry was hydrated.
    if (isStaticView) {
      return;
    }
    // --- FIX END ---

    // If there’s no chosen country yet, nothing to recompute
    if (!loadingCountry) {
      return;
    }

    // This uses the CURRENT ftas & exclusions and the NEW loadingCountry:
    // Pass 1 + Pass 2 in cascadeRebuild will rebuild candidates with the
    // new country, and recomputeFromState will rebuild the lines.
    cascadeRebuild();
  }, [loadingCountry]);

  // Live recompute when entry/loading dates change. Dates feed into
  // candidate effective-range filters, so we need a full cascadeRebuild
  // (not just recomputeFromState). Skipped until both dates are valid
  // and we have an initial result to rebuild from.
  useEffect(() => {
    if (!tariffResults?.data?.baseDutyRateRaw) {
      return;
    }
    if (loading || hydratingRef.current) {
      return;
    }
    if (isStaticView) {
      return;
    }
    const effValid =
      effectDate instanceof Date && !Number.isNaN(effectDate.getTime());
    const loadValid =
      loadDate instanceof Date && !Number.isNaN(loadDate.getTime());
    if (!effValid || !loadValid) {
      return;
    }

    cascadeRebuild();
  }, [effectDate, loadDate]);

  /* -------------------- Render -------------------- */
  // --- 1. Define the content as a variable to reuse it ---
  const pageContent = (
    <div
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        paddingTop: '4px',
      }}
    >
      {!isWhiteLabel && (
        <div style={{ marginBottom: '15px' }}>
          <RequiredLicense
            userLicense={normalizedLicense}
            minimumLicense='standard'
          />
        </div>
      )}
      {!tariffId && !forceStatic && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              paddingLeft: isWhiteLabel ? '50px' : '0px',
              paddingRight: isWhiteLabel ? '50px' : '0px',
            }}
          >
            <div
              className='entity-screening-segmented'
              role='tablist'
              aria-label='Tariff country'
            >
              {TARIFF_COUNTRY_OPTIONS.filter(
                (opt) =>
                  opt.uiEnabled !== false &&
                  (useInternationalTariffCountries || opt.value === 'US') &&
                  (!isWhiteLabel || opt.whiteLabelAccess),
              ).map((opt) => {
                const isActive = selectedTariffCountry === opt.value;
                const isLocked = opt.value !== 'US' && !hasForeignTariffAccess;
                return (
                  <button
                    key={opt.value}
                    type='button'
                    role='tab'
                    aria-selected={isActive}
                    aria-disabled={isLocked}
                    disabled={isLocked}
                    title={
                      isLocked
                        ? // Names THIS tab, not a fixed country list: a hardcoded
                          // "Canada and Mexico" kept promising Mexico after it was
                          // withdrawn from the UI, so upgrading would not have
                          // unlocked what the tooltip advertised.
                          `Upgrade to a Pro license to access ${opt.label} tariffs`
                        : undefined
                    }
                    className={isActive ? 'active' : ''}
                    onClick={() =>
                      isLocked
                        ? undefined
                        : handleSelectTariffCountry(opt.value)
                    }
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      {opt.label}
                      {isLocked && (
                        <LockOutlinedIcon
                          aria-hidden='true'
                          style={{ fontSize: '13px' }}
                        />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <Prompt when={shouldWarnOnLeave} message={handleBlockedNavigation} />
      {/* 1. SEARCH INPUTS (Only One Instance Now) */}
      {showBulkStatusBanner && effectiveBulkStatus && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '1.25rem',
          }}
        >
          <div style={{ width: '100%' }}>
            <div
              style={{
                border: `1px solid ${
                  BULK_STATUS_COLORS[effectiveBulkStatus.color].border
                }`,
                borderRadius: '10px',
                padding: '16px 18px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                  <path
                    d='M10 1.667L18.333 16.667H1.667L10 1.667Z'
                    stroke={BULK_STATUS_COLORS[effectiveBulkStatus.color].icon}
                    strokeWidth='1.67'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M10 7.5V10.833'
                    stroke={BULK_STATUS_COLORS[effectiveBulkStatus.color].icon}
                    strokeWidth='1.67'
                    strokeLinecap='round'
                  />
                  <circle
                    cx='10'
                    cy='14.167'
                    r='0.833'
                    fill={BULK_STATUS_COLORS[effectiveBulkStatus.color].icon}
                  />
                </svg>
                <div>
                  <div className='screening-ai-label-text'>
                    Status: {effectiveBulkStatus.color}
                  </div>
                  <div className='tariff-desc-text'>
                    {effectiveBulkStatus.message}
                  </div>
                </div>
              </div>

              {effectiveBulkStatus.color === 'Yellow' && tariffId && (
                <button
                  className={
                    clearingBulkStatus
                      ? 'btn-new-automate-disabled'
                      : 'btn-new-automate'
                  }
                  onClick={handleClearBulkStatus}
                  disabled={clearingBulkStatus}
                  style={{
                    minWidth: '290px',
                    borderColor:
                      BULK_STATUS_COLORS[effectiveBulkStatus.color].action,
                    backgroundColor: 'transparent',
                  }}
                >
                  <div>
                    {clearingBulkStatus
                      ? 'Clearing Warning...'
                      : 'Mark as Reviewed / Clear Warning'}
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          marginBottom: '2rem',
          paddingLeft: isWhiteLabel ? '50px' : '0px',
          paddingRight: isWhiteLabel ? '50px' : '0px',
        }}
      >
        <div style={{ width: '100%' }}>
          <TariffRagSearch
            userHasSearch={userhasTariffs}
            handleSubmit={handleSubmit}
            setShowDownload={setShowPDFDownload}
            inputs={tariffInputs}
            setInputs={setTariffInputs}
            effectDate={effectDate}
            setEffectDate={setEffectDate}
            loadDate={loadDate}
            setLoadDate={setLoadDate}
            results={Boolean(tariffResults.data.baseDutyRate)}
            setModalOpen={setModalOpen}
            productValue={productValue}
            setProductValue={setProductValue}
            lineSplitShow={lineSplitShow}
            loadingCountryShow={loadingCountryShow}
            ammountShow={ammountShow}
            tariffResults={tariffResults.data}
            countryFieldKey={countryFieldKey}
            loadingCountry={loadingCountry}
            setLoadingCountry={setLoadingCountry}
            countryFieldCodes={countryFieldCodes}
            amountInputs={amountInputs}
            setAmountInputs={setAmountInputs}
            materialPctByKey={materialPctByKey}
            setMaterialPctByKey={setMaterialPctByKey}
            clearPage={clearPage}
            readOnly={isStaticView}
            isBulkMode={forceStatic || Boolean(tariffId)}
            tariffCountry={selectedTariffCountry}
          />
        </div>
      </div>

      {/* Claimed SPI the tool can't offer for this HTS/origin. Shown rather
          than applied — a preference the origin doesn't qualify for must never
          be forced on, but silently dropping it is what made a bulk row and its
          Live Mode recalculation disagree with no visible reason. */}
      {unofferedSpiCodes.length > 0 && (
        <div
          className='screening-badge chip-yellow lens-chip'
          style={{
            display: 'block',
            width: '100%',
            margin: '0 0 12px 0',
            padding: '10px 14px',
            lineHeight: 1.45,
          }}
        >
          SPI {unofferedSpiCodes.map((c) => `"${c}"`).join(', ')}{' '}
          {unofferedSpiCodes.length === 1 ? 'was' : 'were'} claimed on this
          transaction but {unofferedSpiCodes.length === 1 ? 'is' : 'are'} not
          available for {tariffInputs.htsCode || 'this HTS code'} from{' '}
          {tariffInputs.originCountry || 'this country of origin'}. The claim has
          not been applied — verify the SPI on the entry.
        </div>
      )}

      {/* 2. MODE BUTTONS (Positioned Below Inputs, using "Good" CSS) */}
      {tariffId && !forceStatic && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            width: '100%',
            margin: '0 0 20px 0',
          }}
        >
          {!isEditMode ? (
            <div className='tariff-buttons-group'>
              <button
                className='btn-new-automate'
                onClick={() => setShowPDFDownload()}
              >
                <div>Download</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={async () => {
                  // Snapshot the saved toggles before handleSubmit resets
                  // them, so the user enters Live Mode with the same FTA /
                  // exclusion selections they previously saved.
                  const savedTR = (getSavedSnapshot() as any) || {};
                  const savedFtas = savedTR?.results?.enabledFtas;
                  const savedExclusions = savedTR?.results?.enabledExclusions;

                  setIsEditMode(true);
                  await handleSubmit();

                  const hasFtas =
                    savedFtas &&
                    typeof savedFtas === 'object' &&
                    Object.keys(savedFtas).length > 0;
                  const hasExclusions =
                    savedExclusions &&
                    typeof savedExclusions === 'object' &&
                    Object.keys(savedExclusions).length > 0;

                  if (!hasFtas && !hasExclusions) {
                    return;
                  }

                  const mergedFtas = hasFtas
                    ? {
                        ...enabledFtas,
                        ...(savedFtas as Record<string, boolean>),
                      }
                    : undefined;

                  const mergedExclusions = hasExclusions
                    ? {
                        ...enabledExclusions,
                        ...(savedExclusions as Record<string, boolean>),
                      }
                    : undefined;

                  if (mergedFtas) {
                    setEnabledFtas(mergedFtas);
                  }

                  if (mergedExclusions) {
                    setEnabledExclusions(mergedExclusions);
                  }

                  cascadeRebuild(mergedFtas, mergedExclusions);
                }}
              >
                <div>Enter Live Mode</div>
              </button>
            </div>
          ) : (
            <>
              <button
                className='btn-new-clear'
                onClick={() => setIsEditMode(false)}
                style={{ minWidth: '140px' }}
              >
                <div>Discard Changes</div>
              </button>

              <button
                className={
                  saving ? 'btn-new-automate-disabled' : 'btn-new-automate'
                }
                onClick={handleSaveChanges}
                disabled={saving}
              >
                <div>{saving ? 'Saving...' : 'Save Changes'}</div>
              </button>
            </>
          )}
        </div>
      )}
      {Object.keys(tariffResults.data).length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '1.5rem',
            paddingLeft: isWhiteLabel ? '50px' : '0px',
            paddingRight: isWhiteLabel ? '50px' : '0px',
          }}
        >
          <div style={{ width: '100%' }}>
            <TariffToolInsightsPanel
              htsHistory={htsHistory}
              riskFlags={[...riskFlags, ...foreignCalcFlags]}
              hts_code={tariffInputs.htsCode}
              origin_country={
                tariffInputs.originCountry || tariffInputs.originIso2
              }
              // A barred line has no exclusions left to claim.
              questions={activeImportBan ? [] : exclusionQuestions}
              enabledExclusions={enabledExclusions}
              onAnswer={handleQuestionAnswer}
              // The US tab's scope card asks the import-ban gate questions.
              scopeQuestions={
                selectedTariffCountry === 'US'
                  ? importBanQuestions
                  : canadaScopeQuestions
              }
              onScopeAnswer={
                selectedTariffCountry === 'US'
                  ? handleImportBanAnswer
                  : handleCanadaScopeAnswer
              }
              scopeTitle={
                selectedTariffCountry === 'US'
                  ? 'Import Ban Applicability'
                  : undefined
              }
              scopeBusy={foreignTariffResult.isLoading || isStaticView}
              tariffCountry={selectedTariffCountry}
              trqQuotas={trqPanelQuotas}
              trqPositions={trqPositions}
              onDeclareTrq={handleDeclareTrq}
              onChooseTrqVariant={handleChooseTrqVariant}
              trqVariantChoice={trqVariantChoice}
              onViewTrqDetails={() => setTrqDetailOpen(true)}
              trqBusy={foreignTariffResult.isLoading || isStaticView}
              reliefMeasures={canadaReliefItems.map((r) => ({
                id: r.id,
                measure: r.measure,
                count: r.conditions.length,
              }))}
              onViewRelief={() => setReliefModalOpen(true)}
              showScreeningLink={
                !isWhiteLabel &&
                (selectedTariffCountry === 'US' ||
                  selectedTariffCountry === 'CA')
              }
              screeningTradeType={
                selectedTariffCountry === 'CA' ? 'CA Import' : 'US Import'
              }
              // Canada requests both of its flag families as List Names (see
              // canadaScreeningLists) and passes no lens, because the screening
              // query ANDs the two filters. The US set is lens-based and has no
              // list alongside it, so it is unaffected.
              screeningLenses={
                selectedTariffCountry === 'CA'
                  ? []
                  : [
                      'AD CVD Commodities',
                      'Forced Labor Commodities',
                      'PGA Codes',
                    ]
              }
              screeningLists={
                selectedTariffCountry === 'CA' ? canadaScreeningLists : []
              }
            />
          </div>
        </div>
      )}

      {/* 3. RESULTS SECTION — same layout for US / MX / CA. Foreign mode
          syncs its parsed rate into tariffResults via the useEffect below
          and overrides the cost props so the existing US render works
          identically against MX/CA data. */}
      <div ref={targetRef} data-pdf-root='true'>
        <div
          style={{
            background: 'var(--primary-color)',
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            borderTop: '2px solid var(--bg-secondary)',
            paddingLeft: isWhiteLabel ? '50px' : '0px',
            paddingRight: isWhiteLabel ? '50px' : '0px',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              marginBottom: '20px',
              paddingTop: '2rem',
            }}
          >
            <div>
              {loading ? (
                <LoadingFeedback size='big' />
              ) : Object.keys(tariffResults.data).length > 0 ? (
                <TarifFResultsComp
                  tariffResults={tariffResults.data}
                  importBan={activeImportBan}
                  productValue={productValue}
                  dutyCost={
                    foreignCostOverrides
                      ? foreignCostOverrides.dutyCost
                      : dutyCost
                  }
                  totalCost={
                    foreignCostOverrides
                      ? foreignCostOverrides.totalCost
                      : totalCost
                  }
                  baseCost={
                    foreignCostOverrides
                      ? foreignCostOverrides.baseCost
                      : baseAmountDollars + basePercentDutyDollars
                  }
                  displayAdditionalRate={
                    foreignCostOverrides
                      ? foreignCostOverrides.additionalRateDisplay
                      : liveAdditionalRate
                  }
                  costPendingMessage={
                    foreignCostOverrides?.pending
                      ? tariffResults.data?.trqPendingMessage
                      : undefined
                  }
                  tariffCandidates={
                    tariffResults.data?.optionalExclusions ?? []
                  }
                  enabledExclusions={enabledExclusions}
                  toggleExclusion={toggleExclusion}
                  materialPct={tariffInputs.materialPct}
                  ftaOptions={tariffResults.data?.ftaOptions ?? []}
                  enabledFtas={enabledFtas}
                  toggleFta={toggleFTA}
                  explanationText={explanationText}
                  aiLoading={aiLoading}
                  aiExplain={isStaticView ? undefined : aiExplain}
                  adcvdResults={adcvdResults}
                  mpf={foreignCostOverrides ? 0 : mpf}
                  hmf={foreignCostOverrides ? 0 : hmf}
                  hasIeepaTariffs={tariffResults.data?.hasIeepaTariffs}
                  hideIeepa={hideIeepa}
                  toggleIeepa={setHideIeepa}
                  ftaSectionTitle={
                    selectedTariffCountry === 'MX'
                      ? 'Preferential Treaties for Mexico'
                      : selectedTariffCountry === 'CA'
                      ? 'Most Favored Nations (MFN)'
                      : 'Free Trade Agreements'
                  }
                  remissionOptions={canadaRemissionOptions}
                  enabledRemissions={enabledRemissions}
                  toggleRemission={isStaticView ? undefined : toggleRemission}
                />
              ) : (
                <ResultsPlaceholder />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quota detail. The card carries the declaration and the two rates;
          everything needed to understand WHICH quota the goods draw against —
          the pools, the volumes, the permit mechanics — lives here, where
          there is room for it.

          Built on the shared drawer classes the Entity Screening result
          drawer uses, so record detail opens the same way across the app. */}
      {trqDetailOpen && canadaQuotas.length > 0 && (
        <div
          className='drawer-backdrop'
          onClick={() => setTrqDetailOpen(false)}
        >
          <aside
            className='drawer-panel'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='drawer-header'>
              <h2 className='drawer-title'>Tariff-Rate Quota</h2>
              <button
                type='button'
                className='drawer-close-btn'
                onClick={() => setTrqDetailOpen(false)}
                aria-label='Close'
              >
                <CloseIcon sx={{ fontSize: 28 }} />
              </button>
            </div>

            <div className='drawer-body'>
              {canadaQuotas.map((q) => {
                // Short, scannable facts belong in the key/value box; the
                // long-form legal prose does not — right-aligned monospace is
                // unreadable at paragraph length, so those get their own
                // labelled blocks below.
                const facts: Array<{ label: string; value: string | null }> = [
                  { label: 'Product', value: q.productName },
                  { label: 'ICL item', value: q.iclItem },
                  { label: 'Administration', value: q.administrationMethod },
                  {
                    label: 'Quota year',
                    value:
                      q.quotaYearFrom && q.quotaYearTo
                        ? `${q.quotaYearFrom} – ${q.quotaYearTo}`
                        : null,
                  },
                  {
                    label: 'Supply managed',
                    value:
                      q.supplyManaged === null
                        ? null
                        : q.supplyManaged
                        ? 'Yes'
                        : 'No',
                  },
                  { label: 'Administered by', value: q.agency },
                ].filter((f) => Boolean(f.value));

                const prose: Array<{ label: string; value: string | null }> = [
                  {
                    label: 'Shared quota',
                    value: q.sharedWith
                      ? `${q.sharedWith} — volume counts against a pool shared with other items, not a cap reserved for these goods.`
                      : null,
                  },
                  {
                    label: 'Within-access permit',
                    value: q.within?.permitRequirement || null,
                  },
                  {
                    label: 'Over-access permit',
                    value: q.over?.permitRequirement || null,
                  },
                  { label: 'Preferential rates', value: q.preferentialNote },
                  { label: 'Underfill mechanism', value: q.underfillNote },
                ].filter((b) => Boolean(b.value));

                return (
                  <React.Fragment key={q.iclItem || q.productName || 'quota'}>
                    <div>
                      <div className='drawer-section-title'>Quota</div>
                      <div className='drawer-kv-box'>
                        {facts.map((f) => (
                          <div className='drawer-kv-row' key={f.label}>
                            <span className='drawer-kv-label'>{f.label}</span>
                            <span className='drawer-kv-value'>{f.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {q.appliesTo.length > 0 ? (
                      <div>
                        <div className='drawer-section-title'>
                          Tariff lines in this quota
                        </div>
                        <div className='drawer-kv-box'>
                          <div className='trq-detail-codes'>
                            {q.appliesTo.map((c) => (
                              <span key={c} className='trq-detail-code'>
                                {formatHtsDotted(c)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* Which pool the goods draw against depends on origin,
                        and the volumes differ by orders of magnitude between
                        them, so they are broken out rather than left as
                        prose. */}
                    {q.pools.length > 0 ? (
                      <div>
                        <div className='drawer-section-title'>
                          Quota volumes by agreement
                        </div>
                        <div className='drawer-kv-box'>
                          {q.pools.map((pool, i) => (
                            <div
                              key={`${pool.agreement}-${i}`}
                              className='trq-pool'
                            >
                              <span className='trq-pool-agreement'>
                                {pool.agreement}
                              </span>
                              <span className='trq-pool-desc'>
                                {pool.description}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {prose.map((b) => (
                      <div key={b.label}>
                        <div className='drawer-section-title'>{b.label}</div>
                        <div className='drawer-text-block'>{b.value}</div>
                      </div>
                    ))}

                    {q.gacNoticeUrl || q.regulationUrl ? (
                      <div>
                        <div className='drawer-section-title'>Sources</div>
                        <div className='trq-detail-links'>
                          {q.gacNoticeUrl ? (
                            <a
                              className='tariff-relief-condition-link'
                              href={q.gacNoticeUrl}
                              target='_blank'
                              rel='noreferrer noopener'
                            >
                              GAC notice
                            </a>
                          ) : null}
                          {q.regulationUrl ? (
                            <a
                              className='tariff-relief-condition-link'
                              href={q.regulationUrl}
                              target='_blank'
                              rel='noreferrer noopener'
                            >
                              Import Control List
                            </a>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </div>
          </aside>
        </div>
      )}

      {/* Relief & exemptions detail. Same shared drawer as the quota detail
          and the Entity Screening result drawer, so every "open the record"
          action in the app behaves the same way. A drawer also suits the
          content: these are long-form legal conditions. */}
      {reliefModalOpen && canadaReliefItems.length > 0 && (
        <div
          className='drawer-backdrop'
          onClick={() => setReliefModalOpen(false)}
        >
          <aside
            className='drawer-panel'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='drawer-header'>
              <h2 className='drawer-title'>Relief &amp; Exemptions</h2>
              <button
                type='button'
                className='drawer-close-btn'
                onClick={() => setReliefModalOpen(false)}
                aria-label='Close'
              >
                <CloseIcon sx={{ fontSize: 28 }} />
              </button>
            </div>

            <div className='drawer-body'>
              <div className='drawer-text-block'>
                The duty shown assumes none of the following apply. Each turns
                on something this tool cannot confirm on its own — the
                shipment&apos;s total value rather than this line, the date the
                goods went into transit, or a remission the importer has to
                claim — so the charge stands and the condition is listed here
                to check.
              </div>

              {canadaReliefItems.map((item) => (
                <div key={item.id}>
                  <div className='drawer-section-title'>
                    {item.measure}
                    {item.regulation ? ` · ${item.regulation}` : ''}
                  </div>

                  {item.conditions.map((c, i) => (
                    <div
                      key={`${item.id}-${i}`}
                      className='tariff-relief-condition'
                    >
                      <div className='tariff-relief-condition-label'>
                        {c.label}
                      </div>
                      <div className='drawer-text-block'>{c.detail}</div>
                      {c.url ? (
                        <a
                          className='tariff-relief-condition-link'
                          href={c.url}
                          target='_blank'
                          rel='noreferrer noopener'
                        >
                          Read the order
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}

      <Modal open={isGeneratingPDF} onClose={() => setIsGeneratingPDF(false)}>
        <div
          style={{
            position: 'absolute',
            top: '0',
            left: '-10000px', // Safeguarded off-screen execution
            width: '950px', // Optimized compact layout footprint
            backgroundColor: '#FFFFFF',
            color: '#000000',
            fontFamily: 'Helvetica, Arial, sans-serif',
            padding: '24px', // Compressed border padding
            boxSizing: 'border-box',
          }}
        >
          {/* Target Capture Wrapper */}
          <div
            ref={targetRef}
            data-pdf-root='true'
            style={{ backgroundColor: '#FFFFFF' }}
          >
            {/* TOP HEADER BLOCK: GRID FOR GENERAL METRICS & APPLICABILITY */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1.8fr',
                gap: '20px',
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #E4E7EC',
              }}
            >
              {/* Column 1: Transaction Parameters */}
              <div
                style={{
                  paddingRight: '10px',
                  borderRight: '1px solid #F2F4F7',
                }}
              >
                <h2
                  style={{
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    margin: '0 0 10px 0',
                    color: '#475467',
                    fontWeight: 700,
                  }}
                >
                  Transaction Parameters
                </h2>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    fontSize: '12px',
                    color: '#344054',
                  }}
                >
                  <div>
                    <strong>HTS Code:</strong> {tariffInputs.htsCode || '—'}
                  </div>
                  <div>
                    <strong>Country of Origin:</strong>{' '}
                    {tariffInputs.originCountry || '—'}
                  </div>
                  <div>
                    <strong>Product Value:</strong> $
                    {Number(productValue || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <div>
                    <strong>Transport Method:</strong>{' '}
                    {tariffInputs.transportMethod || '—'}
                  </div>
                  <div>
                    <strong>Effective Date:</strong>{' '}
                    {effectDate ? moment(effectDate).format('MM/DD/YYYY') : '—'}
                  </div>
                  <div>
                    <strong>Loading Date:</strong>{' '}
                    {loadDate ? moment(loadDate).format('MM/DD/YYYY') : '—'}
                  </div>
                </div>
              </div>

              {/* Column 2: Exclusion Applicability Assistant */}
              <div>
                {/* Canada scope answers change which measures apply — a 50%
                    over-access surtax versus a 25% one turns on them — so the
                    export records what was answered. Without this the rate in
                    the PDF has no visible justification. */}
                {canadaScopeQuestions.length > 0 ? (
                  <div style={{ marginBottom: '14px' }}>
                    <h2
                      style={{
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        margin: '0 0 10px 0',
                        color: '#475467',
                        fontWeight: 700,
                      }}
                    >
                      Measure Applicability
                    </h2>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        fontSize: '11px',
                      }}
                    >
                      {canadaScopeQuestions.map((q) => {
                        const answer = q.options.find(
                          (o) => o.id === q.selected,
                        );
                        return (
                          <div
                            key={q.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: '10px',
                              borderBottom: '1px dashed #F2F4F7',
                              paddingBottom: '4px',
                            }}
                          >
                            <span
                              style={{ color: '#344054', fontWeight: 500 }}
                            >
                              {q.prompt.length > 85
                                ? `${q.prompt.substring(0, 85)}...`
                                : q.prompt}
                            </span>
                            <span
                              style={{
                                fontWeight: 700,
                                color: answer ? '#157048' : '#B54708',
                                minWidth: '60px',
                                textAlign: 'right',
                              }}
                            >
                              {answer ? answer.label : 'Unanswered'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <h2
                  style={{
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    margin: '0 0 10px 0',
                    color: '#475467',
                    fontWeight: 700,
                  }}
                >
                  Exclusion Applicability Assistant
                </h2>

                {exclusionQuestions.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      fontSize: '11px',
                    }}
                  >
                    {exclusionQuestions.map((q) => {
                      const isYesSelected = q.options
                        .find((o) => o.id === 'yes')
                        ?.enablesExclusions.some(
                          (code) => enabledExclusions[code],
                        );
                      const activeSelectionText = isYesSelected ? 'Yes' : 'No';

                      return (
                        <div
                          key={q.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: '10px',
                            borderBottom: '1px dashed #F2F4F7',
                            paddingBottom: '4px',
                          }}
                        >
                          <span style={{ color: '#344054', fontWeight: 500 }}>
                            {q.prompt.length > 85
                              ? `${q.prompt.substring(0, 85)}...`
                              : q.prompt}
                          </span>
                          <span
                            style={{
                              fontWeight: 700,
                              color: isYesSelected ? '#157048' : '#344054',
                              minWidth: '35px',
                              textAlign: 'right',
                            }}
                          >
                            {activeSelectionText}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p
                    style={{ fontSize: '12px', color: '#667085', margin: '0' }}
                  >
                    No questionnaire criteria active for this profile.
                  </p>
                )}

                <div
                  style={{
                    marginTop: '8px',
                    fontSize: '11px',
                    color: '#475467',
                    backgroundColor: '#F8F9FA',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    display: 'inline-block',
                  }}
                >
                  <strong>Enabled codes:</strong>{' '}
                  {Object.keys(enabledExclusions)
                    .filter((k) => enabledExclusions[k])
                    .join(', ') || 'None'}
                </div>
              </div>
            </div>

            {/* MID GRAPHIC BLOCK: DATA INSIGHT FLAGGING SQUASHED SIDE-BY-SIDE */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #E4E7EC',
              }}
            >
              {/* Risk Flags Card */}
              <div>
                <h3
                  style={{
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    margin: '0 0 6px 0',
                    color: '#475467',
                    fontWeight: 700,
                  }}
                >
                  Risk Flags
                </h3>
                {riskFlags.length > 0 ? (
                  riskFlags.map((flag) => (
                    <div
                      key={flag.id}
                      style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        borderLeft: '2px solid #D92D20',
                        backgroundColor: '#FEF3F2',
                        marginBottom: '4px',
                      }}
                    >
                      <span style={{ fontWeight: 600, color: '#B42318' }}>
                        {flag.title}:
                      </span>{' '}
                      <span style={{ color: '#667085' }}>
                        {flag.description}
                      </span>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#667085',
                      backgroundColor: '#F8F9FA',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      fontStyle: 'italic',
                    }}
                  >
                    No flags found for AD/CVD, Forced Labor, or PGA.
                  </div>
                )}
              </div>

              {/* HTS Code History Card */}
              <div>
                <h3
                  style={{
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    margin: '0 0 6px 0',
                    color: '#475467',
                    fontWeight: 700,
                  }}
                >
                  HTS Code History
                </h3>
                {htsHistory.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    {htsHistory.slice(0, 2).map(
                      (
                        hist, // Bound to top 2 results to maintain layout ceiling
                      ) => (
                        <div
                          key={hist.id}
                          style={{
                            fontSize: '11px',
                            backgroundColor: '#F8F9FA',
                            padding: '4px 8px',
                            borderRadius: '4px',
                          }}
                        >
                          <strong style={{ color: '#344054' }}>
                            {hist.date}:
                          </strong>{' '}
                          <span style={{ color: '#475467' }}>
                            {hist.summary}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#667085',
                      backgroundColor: '#F8F9FA',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      fontStyle: 'italic',
                    }}
                  >
                    No recent changes detected for this HTS code.
                  </div>
                )}
              </div>
            </div>

            {/* CORE CALCULATOR BREAKDOWNS (Inherits parent font constraints for high density rendering) */}
            <div
              style={{ width: '100%', fontSize: '12px', marginBottom: '10px' }}
            >
              <TarifFResultsComp
                tariffResults={tariffResults.data}
                importBan={activeImportBan}
                productValue={productValue}
                dutyCost={
                  foreignCostOverrides
                    ? foreignCostOverrides.dutyCost
                    : dutyCost
                }
                totalCost={
                  foreignCostOverrides
                    ? foreignCostOverrides.totalCost
                    : totalCost
                }
                baseCost={
                  foreignCostOverrides
                    ? foreignCostOverrides.baseCost
                    : baseAmountDollars + basePercentDutyDollars
                }
                displayAdditionalRate={
                  foreignCostOverrides
                    ? foreignCostOverrides.additionalRateDisplay
                    : liveAdditionalRate
                }
                costPendingMessage={
                  foreignCostOverrides?.pending
                    ? tariffResults.data?.trqPendingMessage
                    : undefined
                }
                tariffCandidates={tariffResults.data?.optionalExclusions ?? []}
                enabledExclusions={enabledExclusions}
                toggleExclusion={toggleExclusion}
                materialPct={tariffInputs.materialPct}
                ftaOptions={tariffResults.data?.ftaOptions ?? []}
                enabledFtas={enabledFtas}
                toggleFta={toggleFTA}
                explanationText={explanationText}
                aiLoading={aiLoading}
                aiExplain={undefined} // Suppress asynchronous click hooks
                adcvdResults={adcvdResults}
                mpf={foreignCostOverrides ? 0 : mpf}
                hmf={foreignCostOverrides ? 0 : hmf}
                hasIeepaTariffs={tariffResults.data?.hasIeepaTariffs}
                hideIeepa={hideIeepa}
                toggleIeepa={setHideIeepa}
                ftaSectionTitle={
                  selectedTariffCountry === 'MX'
                    ? 'Preferential Treaties for Mexico'
                    : selectedTariffCountry === 'CA'
                    ? 'Most Favored Nations (MFN)'
                    : 'Free Trade Agreements'
                }
                remissionOptions={canadaRemissionOptions}
                enabledRemissions={enabledRemissions}
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <CreateTicketWizard
          cur_user={cur_user}
          ticketType='tariff'
          docType='Tariff'
          tariffJurisdiction={selectedTariffCountry}
          setFrequency={setFrequency}
          frequency={frequency}
          dateStrategy={dateStrategy}
          setDateStrategy={setDateStrategy}
          handleSaveTicket={createTicket}
          newTicketName={newTicketName}
          setNewTicketName={setNewTicketName}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={saving}
        />
      </Modal>

      <Dialog
        className='dialog_box'
        open={isLeaveModalOpen}
        onClose={() => setIsLeaveModalOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '620px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Leave Tariff Tool?
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={() => setIsLeaveModalOpen(false)}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              Leaving this page will discard your current work. Use Save Ticket
              to store this transaction in Saved Work before leaving.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-clear'
                onClick={handleLeaveWithoutSaving}
              >
                <div>Leave Without Saving</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleSaveTicketFromLeaveModal}
              >
                <div>Save Ticket</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleLeaveAndOptOut}
              >
                <div>Leave + Don&apos;t Show Again</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  // --- 2. Conditional Return ---

  // If we have a tariffId, we are embedded in Supplier.tsx (which has its own header).
  // So just return the content directly.
  if (tariffId || forceStatic) {
    return pageContent;
  }

  // Otherwise, we are in Standalone Tool mode. Wrap in ModuleHeaderLayout.
  if (!isWhiteLabel) {
    return (
      <ModuleHeaderLayout title='Check Tariffs' helpContent={helpContent}>
        {pageContent}
      </ModuleHeaderLayout>
    );
  }

  // Fallback for white label
  return <div>{pageContent}</div>;
};

export default TariffPage;
